import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Star, GitFork, ArrowLeft, Calendar, User, Tag } from 'lucide-react';
import clsx from 'clsx';
import FileTree from '../components/FileTree';
import FilePreview from '../components/FilePreview';
import { fetchTemplate, forkTemplate, starTemplate, fetchVersions } from '../lib/api';
import type { Template, FileTreeNode } from '../types/wizard';
import { terraformDemoFileTree, terraformDemoFileContents } from '../data/terraform-aws-demo';
import { springBootDemoFileTree, springBootDemoFileContents } from '../data/springBoot-demo';
import { pythonMlDemoFileTree, pythonMlDemoFileContents } from '../data/pythonMl-demo';
import { djangoSaasDemoFileTree, djangoSaasDemoFileContents } from '../data/djangoSaas-demo';
import { goApiDemoFileTree, goApiDemoFileContents } from '../data/goApi-demo';
import { reactNativeDemoFileTree, reactNativeDemoFileContents } from '../data/reactNative-demo';
import { rustActixDemoFileTree, rustActixDemoFileContents } from '../data/rustActix-demo';

const tabs = ['Overview', 'Files', 'Versions'] as const;

const demoFileTree: FileTreeNode[] = [
    {
      name: '.claude',
      path: '.claude',
      type: 'directory',
      children: [
        {
          name: 'rules',
          path: '.claude/rules',
          type: 'directory',
          children: [
            {
              name: 'common',
              path: '.claude/rules/common',
              type: 'directory',
              children: [
                { name: 'coding-style.md', path: '.claude/rules/common/coding-style.md', type: 'file' },
                { name: 'git-workflow.md', path: '.claude/rules/common/git-workflow.md', type: 'file' },
                { name: 'security.md', path: '.claude/rules/common/security.md', type: 'file' },
                { name: 'testing.md', path: '.claude/rules/common/testing.md', type: 'file' },
              ],
            },
            {
              name: 'typescript',
              path: '.claude/rules/typescript',
              type: 'directory',
              children: [
                { name: 'coding-style.md', path: '.claude/rules/typescript/coding-style.md', type: 'file' },
                { name: 'testing.md', path: '.claude/rules/typescript/testing.md', type: 'file' },
              ],
            },
          ],
        },
        { name: 'settings.json', path: '.claude/settings.json', type: 'file' },
      ],
    },
    { name: '.editorconfig', path: '.editorconfig', type: 'file' },
    { name: '.eslintrc.js', path: '.eslintrc.js', type: 'file' },
    { name: '.gitattributes', path: '.gitattributes', type: 'file' },
    {
      name: '.github',
      path: '.github',
      type: 'directory',
      children: [
        { name: 'CODEOWNERS', path: '.github/CODEOWNERS', type: 'file' },
        {
          name: 'ISSUE_TEMPLATE',
          path: '.github/ISSUE_TEMPLATE',
          type: 'directory',
          children: [
            { name: 'ai_task.md', path: '.github/ISSUE_TEMPLATE/ai_task.md', type: 'file' },
            { name: 'bug_report.md', path: '.github/ISSUE_TEMPLATE/bug_report.md', type: 'file' },
            { name: 'feature_request.md', path: '.github/ISSUE_TEMPLATE/feature_request.md', type: 'file' },
          ],
        },
        { name: 'PULL_REQUEST_TEMPLATE.md', path: '.github/PULL_REQUEST_TEMPLATE.md', type: 'file' },
        {
          name: 'workflows',
          path: '.github/workflows',
          type: 'directory',
          children: [
            { name: 'ai-code-audit.yml', path: '.github/workflows/ai-code-audit.yml', type: 'file' },
            { name: 'ci.yml', path: '.github/workflows/ci.yml', type: 'file' },
            { name: 'deploy-production.yml', path: '.github/workflows/deploy-production.yml', type: 'file' },
            { name: 'deploy-staging.yml', path: '.github/workflows/deploy-staging.yml', type: 'file' },
          ],
        },
      ],
    },
    { name: '.gitignore', path: '.gitignore', type: 'file' },
    { name: '.mcp.json', path: '.mcp.json', type: 'file' },
    { name: '.prettierrc', path: '.prettierrc', type: 'file' },
    { name: 'CLAUDE.md', path: 'CLAUDE.md', type: 'file' },
    { name: 'README.md', path: 'README.md', type: 'file' },
    {
      name: 'docs',
      path: 'docs',
      type: 'directory',
      children: [
        { name: 'AI-USAGE-POLICY.md', path: 'docs/AI-USAGE-POLICY.md', type: 'file' },
        { name: 'ARCHITECTURE.md', path: 'docs/ARCHITECTURE.md', type: 'file' },
        { name: 'CONTRIBUTING.md', path: 'docs/CONTRIBUTING.md', type: 'file' },
        { name: 'ONBOARDING.md', path: 'docs/ONBOARDING.md', type: 'file' },
      ],
    },
    {
      name: 'git-hooks',
      path: 'git-hooks',
      type: 'directory',
      children: [
        { name: 'commit-msg', path: 'git-hooks/commit-msg', type: 'file' },
        { name: 'pre-commit', path: 'git-hooks/pre-commit', type: 'file' },
        { name: 'pre-push', path: 'git-hooks/pre-push', type: 'file' },
      ],
    },
    { name: 'install.sh', path: 'install.sh', type: 'file' },
    { name: 'lint-staged.config.js', path: 'lint-staged.config.js', type: 'file' },
    {
      name: 'mock-mcp-servers',
      path: 'mock-mcp-servers',
      type: 'directory',
      children: [
        { name: 'README.md', path: 'mock-mcp-servers/README.md', type: 'file' },
        { name: 'github-server.mjs', path: 'mock-mcp-servers/github-server.mjs', type: 'file' },
        { name: 'jira-server.mjs', path: 'mock-mcp-servers/jira-server.mjs', type: 'file' },
        { name: 'package.json', path: 'mock-mcp-servers/package.json', type: 'file' },
      ],
    },
    { name: 'package.json', path: 'package.json', type: 'file' },
    {
      name: 'playbook',
      path: 'playbook',
      type: 'directory',
      children: [
        { name: 'PLAYBOOK.md', path: 'playbook/PLAYBOOK.md', type: 'file' },
        {
          name: 'claude-overrides',
          path: 'playbook/claude-overrides',
          type: 'directory',
          children: [
            { name: '01-discovery.claude.md', path: 'playbook/claude-overrides/01-discovery.claude.md', type: 'file' },
            { name: '02-architecture.claude.md', path: 'playbook/claude-overrides/02-architecture.claude.md', type: 'file' },
            { name: '03-foundation.claude.md', path: 'playbook/claude-overrides/03-foundation.claude.md', type: 'file' },
            { name: '04-core-implementation.claude.md', path: 'playbook/claude-overrides/04-core-implementation.claude.md', type: 'file' },
            { name: '05-integration-hardening.claude.md', path: 'playbook/claude-overrides/05-integration-hardening.claude.md', type: 'file' },
            { name: '06-testing-quality.claude.md', path: 'playbook/claude-overrides/06-testing-quality.claude.md', type: 'file' },
            { name: '07-launch.claude.md', path: 'playbook/claude-overrides/07-launch.claude.md', type: 'file' },
          ],
        },
        { name: 'codeforge-phase.sh', path: 'playbook/codeforge-phase.sh', type: 'file' },
        { name: 'current-phase.json', path: 'playbook/current-phase.json', type: 'file' },
        {
          name: 'phases',
          path: 'playbook/phases',
          type: 'directory',
          children: [
            { name: '01-discovery.md', path: 'playbook/phases/01-discovery.md', type: 'file' },
            { name: '02-architecture.md', path: 'playbook/phases/02-architecture.md', type: 'file' },
            { name: '03-foundation.md', path: 'playbook/phases/03-foundation.md', type: 'file' },
            { name: '04-core-implementation.md', path: 'playbook/phases/04-core-implementation.md', type: 'file' },
            { name: '05-integration-hardening.md', path: 'playbook/phases/05-integration-hardening.md', type: 'file' },
            { name: '06-testing-quality.md', path: 'playbook/phases/06-testing-quality.md', type: 'file' },
            { name: '07-launch.md', path: 'playbook/phases/07-launch.md', type: 'file' },
          ],
        },
        {
          name: 'prompts',
          path: 'playbook/prompts',
          type: 'directory',
          children: [
            {
              name: '01',
              path: 'playbook/prompts/01',
              type: 'directory',
              children: [
                { name: 'gather-requirements.md', path: 'playbook/prompts/01/gather-requirements.md', type: 'file' },
                { name: 'nfr-checklist.md', path: 'playbook/prompts/01/nfr-checklist.md', type: 'file' },
                { name: 'user-stories.md', path: 'playbook/prompts/01/user-stories.md', type: 'file' },
              ],
            },
            {
              name: '02',
              path: 'playbook/prompts/02',
              type: 'directory',
              children: [
                { name: 'adr-generator.md', path: 'playbook/prompts/02/adr-generator.md', type: 'file' },
                { name: 'api-design.md', path: 'playbook/prompts/02/api-design.md', type: 'file' },
                { name: 'data-model-design.md', path: 'playbook/prompts/02/data-model-design.md', type: 'file' },
              ],
            },
            {
              name: '03',
              path: 'playbook/prompts/03',
              type: 'directory',
              children: [
                { name: 'ci-pipeline.md', path: 'playbook/prompts/03/ci-pipeline.md', type: 'file' },
                { name: 'iac-setup.md', path: 'playbook/prompts/03/iac-setup.md', type: 'file' },
                { name: 'project-scaffold.md', path: 'playbook/prompts/03/project-scaffold.md', type: 'file' },
              ],
            },
            {
              name: '04',
              path: 'playbook/prompts/04',
              type: 'directory',
              children: [
                { name: 'auth-implementation.md', path: 'playbook/prompts/04/auth-implementation.md', type: 'file' },
                { name: 'crud-generator.md', path: 'playbook/prompts/04/crud-generator.md', type: 'file' },
                { name: 'feature-implementation.md', path: 'playbook/prompts/04/feature-implementation.md', type: 'file' },
              ],
            },
            {
              name: '05',
              path: 'playbook/prompts/05',
              type: 'directory',
              children: [
                { name: 'integration-adapter.md', path: 'playbook/prompts/05/integration-adapter.md', type: 'file' },
                { name: 'observability-setup.md', path: 'playbook/prompts/05/observability-setup.md', type: 'file' },
                { name: 'security-hardening.md', path: 'playbook/prompts/05/security-hardening.md', type: 'file' },
              ],
            },
            {
              name: '06',
              path: 'playbook/prompts/06',
              type: 'directory',
              children: [
                { name: 'e2e-test-generator.md', path: 'playbook/prompts/06/e2e-test-generator.md', type: 'file' },
                { name: 'load-test.md', path: 'playbook/prompts/06/load-test.md', type: 'file' },
                { name: 'test-suite-generator.md', path: 'playbook/prompts/06/test-suite-generator.md', type: 'file' },
              ],
            },
            {
              name: '07',
              path: 'playbook/prompts/07',
              type: 'directory',
              children: [
                { name: 'go-live-checklist.md', path: 'playbook/prompts/07/go-live-checklist.md', type: 'file' },
                { name: 'monitoring-setup.md', path: 'playbook/prompts/07/monitoring-setup.md', type: 'file' },
                { name: 'runbook-generator.md', path: 'playbook/prompts/07/runbook-generator.md', type: 'file' },
              ],
            },
          ],
        },
      ],
    },
    {
      name: 'plugin',
      path: 'plugin',
      type: 'directory',
      children: [
        {
          name: 'codeforge-nextjs-enterprise',
          path: 'plugin/codeforge-nextjs-enterprise',
          type: 'directory',
          children: [
            {
              name: '.claude-plugin',
              path: 'plugin/codeforge-nextjs-enterprise/.claude-plugin',
              type: 'directory',
              children: [
                { name: 'plugin.json', path: 'plugin/codeforge-nextjs-enterprise/.claude-plugin/plugin.json', type: 'file' },
              ],
            },
            { name: 'README.md', path: 'plugin/codeforge-nextjs-enterprise/README.md', type: 'file' },
            {
              name: 'agents',
              path: 'plugin/codeforge-nextjs-enterprise/agents',
              type: 'directory',
              children: [
                { name: 'architect.md', path: 'plugin/codeforge-nextjs-enterprise/agents/architect.md', type: 'file' },
                { name: 'build-error-resolver.md', path: 'plugin/codeforge-nextjs-enterprise/agents/build-error-resolver.md', type: 'file' },
                { name: 'code-reviewer.md', path: 'plugin/codeforge-nextjs-enterprise/agents/code-reviewer.md', type: 'file' },
                { name: 'planner.md', path: 'plugin/codeforge-nextjs-enterprise/agents/planner.md', type: 'file' },
                { name: 'tdd-guide.md', path: 'plugin/codeforge-nextjs-enterprise/agents/tdd-guide.md', type: 'file' },
              ],
            },
            {
              name: 'commands',
              path: 'plugin/codeforge-nextjs-enterprise/commands',
              type: 'directory',
              children: [
                { name: 'build-fix.md', path: 'plugin/codeforge-nextjs-enterprise/commands/build-fix.md', type: 'file' },
                { name: 'code-review.md', path: 'plugin/codeforge-nextjs-enterprise/commands/code-review.md', type: 'file' },
                { name: 'phase.md', path: 'plugin/codeforge-nextjs-enterprise/commands/phase.md', type: 'file' },
                { name: 'plan.md', path: 'plugin/codeforge-nextjs-enterprise/commands/plan.md', type: 'file' },
                { name: 'tdd.md', path: 'plugin/codeforge-nextjs-enterprise/commands/tdd.md', type: 'file' },
                { name: 'verify.md', path: 'plugin/codeforge-nextjs-enterprise/commands/verify.md', type: 'file' },
              ],
            },
            {
              name: 'hooks',
              path: 'plugin/codeforge-nextjs-enterprise/hooks',
              type: 'directory',
              children: [
                { name: 'hooks.json', path: 'plugin/codeforge-nextjs-enterprise/hooks/hooks.json', type: 'file' },
              ],
            },
            {
              name: 'skills',
              path: 'plugin/codeforge-nextjs-enterprise/skills',
              type: 'directory',
              children: [
                {
                  name: 'build-fix',
                  path: 'plugin/codeforge-nextjs-enterprise/skills/build-fix',
                  type: 'directory',
                  children: [
                    { name: 'SKILL.md', path: 'plugin/codeforge-nextjs-enterprise/skills/build-fix/SKILL.md', type: 'file' },
                  ],
                },
                {
                  name: 'code-review',
                  path: 'plugin/codeforge-nextjs-enterprise/skills/code-review',
                  type: 'directory',
                  children: [
                    { name: 'SKILL.md', path: 'plugin/codeforge-nextjs-enterprise/skills/code-review/SKILL.md', type: 'file' },
                  ],
                },
                {
                  name: 'phase',
                  path: 'plugin/codeforge-nextjs-enterprise/skills/phase',
                  type: 'directory',
                  children: [
                    { name: 'SKILL.md', path: 'plugin/codeforge-nextjs-enterprise/skills/phase/SKILL.md', type: 'file' },
                  ],
                },
                {
                  name: 'plan',
                  path: 'plugin/codeforge-nextjs-enterprise/skills/plan',
                  type: 'directory',
                  children: [
                    { name: 'SKILL.md', path: 'plugin/codeforge-nextjs-enterprise/skills/plan/SKILL.md', type: 'file' },
                  ],
                },
                {
                  name: 'tdd',
                  path: 'plugin/codeforge-nextjs-enterprise/skills/tdd',
                  type: 'directory',
                  children: [
                    { name: 'SKILL.md', path: 'plugin/codeforge-nextjs-enterprise/skills/tdd/SKILL.md', type: 'file' },
                  ],
                },
                {
                  name: 'verify',
                  path: 'plugin/codeforge-nextjs-enterprise/skills/verify',
                  type: 'directory',
                  children: [
                    { name: 'SKILL.md', path: 'plugin/codeforge-nextjs-enterprise/skills/verify/SKILL.md', type: 'file' },
                  ],
                },
              ],
            },
          ],
        },
        { name: 'marketplace.json.example', path: 'plugin/marketplace.json.example', type: 'file' },
      ],
    },
];

const demoFileContents: Record<string, string> = {
  '.claude/rules/common/coding-style.md': `# Coding Style Conventions

These rules apply to all code in the nextjs-enterprise project. Follow them in every file you create or modify.

## Naming Conventions

- Files and directories: \`kebab-case\` (e.g., \`user-profile.ts\`, \`auth-provider.tsx\`)
- React components: \`PascalCase\` (e.g., \`UserProfile\`, \`AuthProvider\`)
- Functions and variables: \`camelCase\` (e.g., \`getUserById\`, \`isAuthenticated\`)
- Constants and env references: \`UPPER_SNAKE_CASE\` (e.g., \`MAX_RETRY_COUNT\`, \`DATABASE_URL\`)
- TypeScript types and interfaces: \`PascalCase\`, no \`I\` prefix (e.g., \`UserSession\`, not \`IUserSession\`)
- Database models in Prisma schema: \`PascalCase\` singular (e.g., \`model User\`, not \`model Users\`)

## File Structure

- Maximum 300 lines per file — split into smaller modules if exceeded
- One primary responsibility per file; co-locate helpers only if they are private to that module
- Co-locate related files: tests next to source (\`user-service.ts\` + \`user-service.test.ts\`), types with their module
- Group by feature inside \`src/modules/\`, not by technical layer

## Import Ordering

- Order imports in three groups separated by blank lines:
  1. External packages (\`next\`, \`react\`, \`@prisma/client\`, \`socket.io\`)
  2. Internal aliases (\`@/lib/\`, \`@/modules/\`, \`@/components/\`)
  3. Relative imports (\`./\`, \`../\`)
- Do not use wildcard imports (\`import * as\`) unless wrapping a third-party namespace

## Export Rules

- Prefer named exports over default exports in all files except Next.js pages/layouts (which require default)
- Do not create barrel files (\`index.ts\` that re-exports). Import directly from the source module path
- Example: \`import { createUser } from '@/modules/user/user-service'\` not from \`@/modules/user\`

## Error Handling

- Define custom error classes extending \`AppError\` from \`@/lib/errors\` — include \`code\`, \`statusCode\`, and \`context\`
- Never swallow errors silently; always log or rethrow with added context
- Use \`Result<T, E>\` pattern for expected failures in service functions; throw only for unexpected errors
- Wrap external API calls and database queries in try/catch with specific error mapping

## Logging

- Use the structured logger from \`@/lib/logger\` — never use \`console.log\`, \`console.error\`, or \`console.warn\`
- Include correlation IDs, module name, and operation in every log entry
- Log at appropriate levels: \`debug\` for flow tracing, \`info\` for business events, \`warn\` for recoverable issues, \`error\` for failures

## Comments

- Do not write comments that restate the code ("increment counter by 1")
- Document **why** a decision was made, not **what** the code does
- Use JSDoc on exported functions with \`@param\`, \`@returns\`, and \`@throws\` tags
- Mark temporary workarounds with \`// TODO(username): reason — ticket reference\``,

  '.claude/rules/common/git-workflow.md': `# Git Workflow — nextjs-enterprise

## Branching Strategy: GitHub Flow

- \`main\` is **always deployable**. Never commit directly to \`main\`.
- Create feature branches from \`main\`. Merge back via PR only.
- Delete branches after merge. Keep the remote branch list clean.

## Branch Naming

Format: \`type/TICKET-short-desc\`

- \`feat/AUTH-42-login-flow\`
- \`fix/API-99-null-check\`
- \`chore/INFRA-12-docker-optimize\`
- \`docs/ONBOARD-3-setup-guide\`

Valid prefixes: \`feat\`, \`fix\`, \`docs\`, \`refactor\`, \`test\`, \`chore\`, \`ci\`, \`hotfix\`

## Conventional Commits

Every commit message must follow conventional commits:

\`\`\`
type(scope): imperative description   ← max 72 chars

Optional body explaining WHY, not what.  ← wrap at 80 chars
Refs: AUTH-42
\`\`\`

Types: \`feat\`, \`fix\`, \`docs\`, \`refactor\`, \`test\`, \`chore\`, \`ci\`, \`perf\`, \`build\`

Scopes: \`auth\`, \`api\`, \`db\`, \`ws\`, \`cache\`, \`ui\`, \`infra\`, \`deps\`

- Use **imperative mood**: "add login endpoint" not "added login endpoint"
- Subject line max **72 characters**. No trailing period.
- Body should explain **why** the change was made, not restate the diff.

## Commit Signing

All commits **must** be GPG or SSH signed. Ensure your local config:

\`\`\`
git config --global commit.gpgsign true
\`\`\`

Unsigned commits will be rejected by branch protection rules.

## Pull Request Requirements

- **Description required** — summarize what and why, link the issue (\`Closes #123\`)
- **Tests passing** — all CI status checks must be green before merge
- **1+ approval** — at least one team member must approve
- **No force-push to main** — ever
- **PR size** — aim for **<400 lines changed**; split large features into stacked PRs
- Use draft PRs for work-in-progress; convert to ready when CI passes

## AI Disclosure

When Claude writes or substantially reviews code in a PR:

1. Add the \`AI-assisted\` label to the PR
2. Include a co-author trailer in the merge commit:
   \`\`\`
   Co-authored-by: Claude <claude@anthropic.com>
   \`\`\`
3. Note in the PR description which parts were AI-generated or AI-reviewed

## Protected Branch: \`main\`

- Require pull request before merging
- Require at least 1 approving review
- Require status checks to pass (lint, typecheck, test, build)
- No force pushes
- No deletions
- Require signed commits
- Require linear history (squash merge)

## Merge Strategy

- **Squash-merge** feature branches into \`main\` — keeps history linear and clean
- Use the PR title as the squash commit message (must follow conventional commits)
- Ensure the squash commit message includes issue references`,

  '.claude/rules/common/security.md': `# Security Practices — nextjs-enterprise

## Input Validation
- Validate ALL input at system boundaries using \`zod\` schemas defined in \`src/lib/validations/\`.
- Never trust client input — re-validate in API routes and server actions even if validated client-side.
- Coerce and strip unknown fields: use \`z.object({}).strict()\` for API payloads.
- Validate URL params, query strings, headers, and request bodies equally.

## Authentication
- Use NextAuth.js via \`getServerSession(authOptions)\` in every API route and server component that requires auth.
- Never roll custom JWT logic — rely on NextAuth.js session management configured in \`src/lib/auth.ts\`.
- Protect all \`/api/*\` routes except explicitly public ones (health checks, webhooks with signature verification).
- Check \`session.user\` is non-null before proceeding; return 401 immediately if missing.

## Authorization
- Enforce role-based access control (RBAC) using roles stored in the \`User\` model (\`ADMIN\`, \`MEMBER\`, \`VIEWER\`).
- Always verify resource ownership before mutations: load the entity, compare \`userId\` to \`session.user.id\`.
- Use a shared \`authorize(session, resource, action)\` helper in \`src/lib/authorization.ts\` — never inline permission checks.
- Admin routes under \`**/api/admin/*\` must verify \`session.user.role === 'ADMIN'\`.

## Secrets Handling
- Store secrets in environment variables only — never hardcode tokens, keys, or passwords.
- Use \`.env.local\` for local development (git-ignored), AWS Secrets Manager for staging/production.
- Access secrets via \`src/lib/env.ts\` which validates all required vars at startup with zod.
- Never log secrets or include them in error responses.

## SQL Injection Prevention
- Always use Prisma's parameterized query builder — never construct raw SQL strings.
- If \`prisma.$queryRaw\` is absolutely necessary, use \`Prisma.sql\` tagged template literals only.
- Never interpolate user input into any database query.

## XSS Prevention
- Rely on React's automatic output escaping for all rendered content.
- Never use \`dangerouslySetInnerHTML\` without sanitizing via \`dompurify\` first — prefer avoiding it entirely.
- Validate redirect URLs against an allowlist; reject open redirects by checking \`new URL(target).origin\`.
- Set \`Content-Security-Policy\` headers in \`next.config.js\` middleware.

## CSRF Protection
- Next.js Server Actions include built-in CSRF tokens — do not disable this.
- For custom API routes, validate the \`Origin\` and \`Referer\` headers against \`NEXTAUTH_URL\`.
- Use \`SameSite=Lax\` (default) or \`SameSite=Strict\` for session cookies.

## Rate Limiting
- Apply rate limiting to \`/api/auth/*\` endpoints and all public-facing APIs using \`src/lib/rate-limit.ts\` backed by Redis.
- Auth endpoints: 5 requests per minute per IP. Public APIs: 60 requests per minute per IP.
- Return \`429 Too Many Requests\` with \`Retry-After\` header when limits are exceeded.

## Dependency Policy
- Run \`pnpm audit\` in CI on every pull request — fail the build on high/critical vulnerabilities.
- Pin exact dependency versions in \`package.json\` (no \`^\` or \`~\` prefixes).
- Review new dependencies for maintenance status, bundle size, and known issues before adding.
- Update dependencies weekly via Renovate bot with auto-merge for patch-level updates only.

## Sensitive Paths
Files matching these patterns require extra scrutiny during code review:
- \`prisma/migrations/*\` — schema changes affect data integrity
- \`.env*\` — must never be committed (verify \`.gitignore\`)
- \`**/auth/*\` — authentication logic is a critical attack surface
- \`**/api/admin/*\` — privileged operations require RBAC verification`,

  '.claude/rules/common/testing.md': `# Testing Standards — nextjs-enterprise

## Coverage & Execution

- **80% minimum coverage** on all new or modified code — enforced in CI via \`vitest --coverage\`.
- Run \`pnpm test -- --watch\` during development for fast feedback loops.
- CI runs \`pnpm test -- --run --coverage\` — no watch mode in pipelines.

## Test Naming & Structure

- Follow **Arrange-Act-Assert** in every test body. Separate sections with a blank line.
- Naming convention: \`describe("ModuleName", () => { it("should do X when Y", ...) })\`.
- One assertion **concept** per test — multiple \`expect()\` calls are fine if they verify the same behavior.
- Co-locate unit tests next to source: \`src/modules/billing/billing.service.test.ts\`.
- Integration tests live in \`tests/integration/\`, E2E tests in \`tests/e2e/\`.

## Unit Tests

- Must be **fast** (<50ms each) and fully isolated — no network, no database, no filesystem.
- Mock all external dependencies: Prisma client, Redis client, HTTP calls, WebSocket (Socket.io).
- Use \`vi.mock()\` for module-level mocks, \`vi.fn()\` for individual function stubs.
- Reset mocks in \`beforeEach\` with \`vi.clearAllMocks()\` — never rely on mock state from a prior test.

## Integration Tests

- Test **module boundaries** and Prisma queries against a real PostgreSQL test database.
- Use the \`DATABASE_URL\` from \`.env.test\` pointing to a dedicated test database.
- Run migrations before the suite: \`prisma migrate deploy --preview-feature\` in \`globalSetup\`.
- Wrap each test in a transaction and roll back, or truncate tables in \`afterEach\`.

## E2E Tests (Playwright)

- Reserve E2E for **critical user flows only**: sign-in, onboarding, checkout, core CRUD.
- Target a running dev server (\`pnpm dev\`) or a preview deployment — never mock the backend.
- Keep the suite under 20 tests to maintain a <5-minute CI runtime.

## Mocking HTTP

- Use **MSW** (\`msw/node\`) for intercepting outbound HTTP in unit and integration tests.
- Define handlers in \`tests/mocks/handlers.ts\` and start the server in \`tests/setup.ts\`.
- Never use \`vi.mock("fetch")\` — MSW is the standard for HTTP mocking in this project.

## Test Data

- Use **factory functions** (in \`tests/factories/\`) — never static JSON fixtures.
- Generate realistic data with \`@faker-js/faker\`. Seed faker in \`tests/setup.ts\` for reproducibility.
- Example: \`createUserFactory({ role: "ADMIN" })\` returns a full user object with sensible defaults.

## Independence & Cleanup

- Every test **sets up and tears down its own state** — no shared mutable state between tests.
- Do not use \`beforeAll\` to create data that multiple tests read — duplicate setup is acceptable.
- Tests must pass when run individually (\`vitest run -t "test name"\`) and in any order.`,

  '.claude/rules/typescript/coding-style.md': `# TypeScript Coding Style — nextjs-enterprise

## Strict Mode

The project enforces \`strict: true\` in \`tsconfig.json\` along with \`noUncheckedIndexedAccess\` and \`exactOptionalPropertyTypes\`. Never weaken these settings. Fix type errors properly — do not use \`@ts-ignore\` or \`@ts-expect-error\` without a linked issue number.

## Types vs Interfaces

- Prefer \`type\` for object shapes, unions, and intersections.
- Use \`interface\` only when you need declaration merging or \`implements\` in a class.
- Export all shared types from the module's \`types.ts\` barrel file.

## Type Imports

Always use \`import type { X }\` for type-only imports. ESLint enforces this via \`@typescript-eslint/consistent-type-imports\`.

## Generics

Use descriptive names constrained with \`extends\`: \`TUser extends BaseUser\`, \`TResponse extends ApiResult\` — never bare \`T\`, \`U\`, \`V\`.

## Enums

Do **not** use TypeScript \`enum\`. Use \`as const\` objects with inferred types:

\`\`\`ts
export const OrderStatus = { PENDING: 'PENDING', SHIPPED: 'SHIPPED', DELIVERED: 'DELIVERED' } as const;
export type OrderStatus = (typeof OrderStatus)[keyof typeof OrderStatus];
\`\`\`

## Null Handling

- Use nullish coalescing (\`??\`) and optional chaining (\`?.\`) everywhere.
- **Never** use non-null assertion (\`!\`). If you are certain a value exists, narrow with a guard or throw an explicit error.

## \`satisfies\` Operator

Prefer \`satisfies\` over type annotations when you want to validate a value matches a type while preserving its literal/narrowed type:

\`\`\`ts
const config = { retries: 3, timeout: 5000 } satisfies CacheConfig;
\`\`\`

## Runtime Validation with Zod

- Validate all external input (API route bodies, search params, webhook payloads) with Zod schemas.
- Derive static types with \`z.infer<typeof schema>\` — never duplicate the shape manually.
- Place schemas in \`src/modules/<module>/schemas.ts\`.

## Server vs Client Components

- Default to React Server Components. Add \`'use client'\` only when the component uses hooks, browser APIs, or event handlers.
- Never import server-only code (Prisma, env secrets) into client components. Use the \`server-only\` package to enforce this.

## Next.js App Router Patterns

- Every route segment should include \`loading.tsx\`, \`error.tsx\`, and \`not-found.tsx\` where user-facing.
- Shared layouts go in \`layout.tsx\`; do not fetch data in layouts unless it is needed by all children.
- Use \`generateMetadata\` for dynamic SEO — never hardcode \`<title>\` in page components.

## Prisma Types

Always import generated types from \`@prisma/client\`. Never manually define types that mirror database models. Use Prisma's utility types (\`Prisma.UserGetPayload<{ include: { posts: true } }>\`) for complex includes/selects.`,

  '.claude/rules/typescript/testing.md': `# TypeScript Testing Rules — Vitest + Next.js

## Vitest Configuration

- Use \`vitest.config.ts\` at project root; resolve \`@/*\` path aliases to match \`tsconfig.json\` paths.
- Set \`environment: 'jsdom'\` as default; override per-file with \`// @vitest-environment node\` for server-side tests.
- Use \`@vitejs/plugin-react\` in the vitest config to support JSX transforms.
- Coverage provider: \`v8\`. Exclude \`**/*.test.{ts,tsx}\`, \`**/*.d.ts\`, \`**/types/**\`, \`prisma/**\`, \`*.config.*\` from reports.

## Test File Location

- Co-locate tests next to source: \`UserCard.tsx\` → \`UserCard.test.tsx\`, \`createOrder.ts\` → \`createOrder.test.ts\`.
- Shared test utilities live in \`src/test/helpers.ts\`; custom render wrapper in \`src/test/render.tsx\`.

## Type-Safe Mocks

- Always type \`vi.mock\` return values — use \`vi.mocked<typeof import('...')>()\` for full type inference.
- **Never use \`any\` in test code.** Use \`Partial<T>\`, \`Pick<T, K>\`, or factory functions to build typed fixtures.
- Prefer \`vi.fn<Parameters<T>, ReturnType<T>>()\` over untyped \`vi.fn()\`.

## React Component Testing

- Use \`@testing-library/react\` with a custom \`render()\` that wraps providers (SessionProvider, QueryClientProvider).
- Query by role first: \`screen.getByRole('button', { name: /submit/i })\`. Avoid \`getByTestId\` unless no semantic alternative exists.
- **Do NOT use snapshot tests for components** — they are brittle and produce noisy diffs. Assert specific DOM state instead.
- Snapshot tests are acceptable only for serialized data structures (API response shapes, config objects).

## Server Actions & API Routes

- Mock \`next/headers\` (\`cookies()\`, \`headers()\`) via \`vi.mock('next/headers')\` with typed return values.
- Test server actions by calling the exported function directly; assert input validation errors (Zod) and auth guard rejections before happy paths.
- For API route handlers (\`app/api/**/route.ts\`), import the \`GET\`/\`POST\` handler and pass a constructed \`NextRequest\`; assert on the returned \`NextResponse\` status and JSON body.

## Prisma Testing

- Use a singleton \`PrismaClient\` pointed at a test PostgreSQL database (\`DATABASE_URL\` from \`.env.test\`).
- Wrap each test in a transaction that rolls back: use \`prisma.$transaction\` with manual abort or the \`prismock\` library for unit tests that don't need a real DB.
- Seed test data via factory functions (\`createTestUser()\`, \`createTestOrder()\`) — never rely on shared mutable state across tests.

## Coverage & CI

- Enforce minimum thresholds in CI: \`statements: 80\`, \`branches: 75\`, \`functions: 80\`, \`lines: 80\`.
- Run \`pnpm vitest run --coverage\` in the \`test\` GitHub Actions job; fail the pipeline if thresholds are not met.`,

  '.claude/settings.json': `{
  "allowedTools": [
    "bash(git diff:*)",
    "bash(git log:*)",
    "bash(git status:*)",
    "bash(pnpm lint:*)",
    "bash(pnpm test:*)",
    "bash(pnpm type-check:*)",
    "bash(pnpm prisma format:*)",
    "bash(pnpm prisma validate:*)",
    "bash(cat:*)",
    "bash(ls:*)",
    "bash(find:*)",
    "bash(grep:*)",
    "bash(wc:*)",
    "bash(head:*)",
    "bash(tail:*)",
    "file_read",
    "file_write",
    "file_edit"
  ],
  "plugins": [
    "plugin/codeforge-nextjs-enterprise"
  ],
  "model": {
    "default": "sonnet",
    "complex": "opus",
    "complexTaskPatterns": [
      "architecture",
      "refactor across multiple modules",
      "database schema design",
      "security review",
      "performance optimization",
      "websocket implementation",
      "authentication flow"
    ]
  },
  "contextFiles": [
    "CLAUDE.md",
    ".claude/rules/*.md"
  ]
}`,

  '.editorconfig': `root = true

[*]
indent_style = space
indent_size = 2
charset = utf-8
trim_trailing_whitespace = true
insert_final_newline = true
end_of_line = lf

[*.md]
trim_trailing_whitespace = false

[Makefile]
indent_style = tab

[*.yml]
indent_size = 2

[*.yaml]
indent_size = 2

[*.py]
indent_size = 4`,

  '.eslintrc.js': `/** @type {import('eslint').Linter.Config} */
module.exports = {
  root: true,
  parser: '@typescript-eslint/parser',
  parserOptions: {
    ecmaVersion: 'latest',
    sourceType: 'module',
    project: './tsconfig.json',
  },
  plugins: ['@typescript-eslint', 'import'],
  extends: [
    'next/core-web-vitals',
    'next/typescript',
    'prettier',
  ],
  rules: {
    'no-console': 'warn',
    'no-var': 'error',
    'prefer-const': 'error',
    '@typescript-eslint/no-explicit-any': 'error',
    '@typescript-eslint/no-unused-vars': [
      'error',
      { argsIgnorePattern: '^_', varsIgnorePattern: '^_' },
    ],
    'import/order': [
      'error',
      {
        groups: [
          'builtin',
          'external',
          'internal',
          ['parent', 'sibling', 'index'],
          'type',
        ],
        'newlines-between': 'always',
        alphabetize: { order: 'asc', caseInsensitive: true },
      },
    ],
    'import/no-duplicates': 'error',
  },
  ignorePatterns: ['node_modules', '.next', 'coverage', 'prisma/generated'],
};`,

  '.gitattributes': `# Auto detect text files and ensure LF line endings
* text=auto eol=lf

# ============================================================================
# Source code
# ============================================================================
*.ts        text diff=typescript
*.tsx       text diff=typescript
*.js        text diff=javascript
*.jsx       text diff=javascript
*.mjs       text diff=javascript
*.cjs       text diff=javascript

# ============================================================================
# Styles
# ============================================================================
*.css       text diff=css
*.scss      text diff=css
*.module.css text diff=css

# ============================================================================
# Markup & Data
# ============================================================================
*.html      text diff=html
*.md        text diff=markdown
*.mdx       text diff=markdown
*.json      text diff=json
*.yaml      text
*.yml       text
*.toml      text
*.xml       text

# ============================================================================
# Prisma
# ============================================================================
*.prisma    text diff=prisma

# ============================================================================
# Configuration
# ============================================================================
*.env*      text
*.config.*  text
.editorconfig text
.gitattributes text
.gitignore  text
.prettierrc text
.eslintrc*  text
Dockerfile  text
docker-compose* text

# ============================================================================
# Shell scripts
# ============================================================================
*.sh        text eol=lf
*.bash      text eol=lf

# ============================================================================
# Generated files — hidden from GitHub diffs & language stats
# ============================================================================
pnpm-lock.yaml    linguist-generated -diff
package-lock.json linguist-generated -diff

*.js.map    linguist-generated -diff
*.css.map   linguist-generated -diff
*.d.ts.map  linguist-generated -diff

# Next.js build output
.next/**    linguist-generated

# Prisma generated client
node_modules/.prisma/** linguist-generated

# ============================================================================
# Binary files — no line ending conversion, no diff
# ============================================================================
*.png       binary
*.jpg       binary
*.jpeg      binary
*.gif       binary
*.ico       binary
*.webp      binary
*.avif      binary
*.svg       text

*.woff      binary
*.woff2     binary
*.ttf       binary
*.eot       binary
*.otf       binary

*.pdf       binary
*.zip       binary
*.tar.gz    binary`,

  '.github/CODEOWNERS': `# CODEOWNERS — nextjs-enterprise
#
# This file defines code ownership for automated review assignments.
# GitHub will automatically request reviews from the listed teams
# when a pull request modifies files matching the given patterns.
#
# Order matters — later rules take precedence over earlier ones.
# See: https://docs.github.com/en/repositories/managing-your-repositorys-settings-and-features/customizing-your-repository/about-code-owners

# Default owners for everything in the repo
*                                   @org/engineering

# Authentication & authorization module
src/modules/auth/**                 @org/security-team

# Payments module
src/modules/payments/**             @org/payments-team

# Database schema, migrations, and seed files
prisma/**                           @org/backend-team

# CI/CD workflows, GitHub config, and Actions
.github/**                          @org/devops

# Claude Code configuration and AI agent governance
.claude/**                          @org/ai-governance

# Documentation and architectural decision records
docs/**                             @org/tech-leads

# Infrastructure-as-code, Dockerfiles, and deployment configs
infrastructure/**                   @org/devops`,

  '.github/ISSUE_TEMPLATE/ai_task.md': `---
name: "AI Task"
description: "Task designed for autonomous execution by Claude Code"
title: "[AI] "
labels: ["ai-task", "claude"]
assignees: []
---

## Task Description

<!-- Clear, specific description of what needs to be done. Write as if instructing an autonomous agent. -->

## Context

<!-- List relevant files, modules, and any background needed to complete this task. -->

- **Relevant files:** <!-- e.g., src/modules/auth/services/session.service.ts, prisma/schema.prisma -->
- **Module:** <!-- e.g., auth, billing, core -->
- **Branch base:** \`main\`
- **Related docs:** <!-- e.g., docs/architecture/auth-flow.md -->

## Constraints

<!-- Explicitly state what should NOT be modified or changed. -->

- [ ] Do NOT modify existing database migrations
- [ ] Do NOT change public API contracts without updating OpenAPI spec
- [ ] Do NOT alter files outside the specified module unless necessary
- <!-- Add project-specific constraints here -->

## Expected Output

<!-- Concrete deliverables. Be specific about file paths and formats. -->

- [ ] <!-- e.g., New service at src/modules/auth/services/mfa.service.ts -->
- [ ] <!-- e.g., Unit tests at src/modules/auth/__tests__/mfa.service.test.ts -->
- [ ] <!-- e.g., Updated Prisma schema with MfaToken model -->
- [ ] PR with conventional commit messages (\`feat:\`, \`fix:\`, \`refactor:\`, etc.)

## Verification Steps

<!-- How to confirm the task is complete. Must be runnable commands. -->

\`\`\`bash
pnpm typecheck          # No TypeScript errors
pnpm lint               # No ESLint/Prettier violations
pnpm test -- --run      # All vitest tests pass
pnpm prisma validate    # Schema is valid (if modified)
\`\`\`

## Related Issues

<!-- Link any dependent or related issues. -->

- Depends on: #
- Blocks: #
- Related: #`,

  '.github/ISSUE_TEMPLATE/bug_report.md': `---
name: Bug Report
about: Report a bug in the nextjs-enterprise application
title: "[BUG] "
labels: bug, triage
assignees: ''
---

## Description

A clear and concise description of the bug.

## Steps to Reproduce

1. Go to '...'
2. Click on '...'
3. Scroll down to '...'
4. See error

## Expected Behavior

A clear description of what you expected to happen.

## Actual Behavior

A clear description of what actually happened. Include any error messages or logs.

## Environment

- **OS:** [e.g., macOS 14.5, Ubuntu 22.04]
- **Browser:** [e.g., Chrome 126, Safari 17.5]
- **Node.js version:** [e.g., 20.15.0]
- **pnpm version:** [e.g., 9.4.0]
- **Next.js version:** [e.g., 14.2.x]
- **Database:** [e.g., PostgreSQL 16]
- **Redis version:** [e.g., 7.2]
- **Deployment:** [e.g., local, AWS ECS staging, AWS ECS production]

## Screenshots

If applicable, add screenshots or screen recordings to help explain the problem.

## Logs

<details>
<summary>Relevant log output</summary>

\`\`\`
Paste any relevant logs from the browser console, server output, or Docker container logs here.
\`\`\`

</details>

## Additional Context

Add any other context about the problem here, such as related issues, recent deployments, or Prisma migration changes.`,

  '.github/ISSUE_TEMPLATE/feature_request.md': `---
name: Feature Request
description: Propose a new feature for nextjs-enterprise
title: "[FEATURE] "
labels: ["enhancement", "triage"]
assignees: []
---

## User Story

As a **[type of user]**,
I want **[desired functionality]**,
so that **[expected benefit or outcome]**.

## Acceptance Criteria

- [ ] Criteria 1: Define the expected behavior clearly
- [ ] Criteria 2: Specify edge cases and error handling
- [ ] Criteria 3: Include relevant API route or page behavior (\`/app/...\`)
- [ ] Criteria 4: State any Prisma schema changes required
- [ ] Criteria 5: Confirm WebSocket (Socket.io) or Redis caching implications if applicable

## Technical Notes

- **Module affected:** (e.g., \`src/modules/auth\`, \`src/modules/api\`)
- **Database migrations:** Yes / No
- **New dependencies:** List any new pnpm packages required
- **Auth impact (NextAuth.js):** Yes / No — describe scope
- **Environment variables:** List any new env vars needed

## Design / Mockups

Attach screenshots, Figma links, or wireframes here.

## Priority

- [ ] 🔴 Critical — blocks launch
- [ ] 🟠 High — needed for current phase
- [ ] 🟡 Medium — improves experience significantly
- [ ] 🟢 Low — nice to have`,

  '.github/PULL_REQUEST_TEMPLATE.md': `## Summary

<!-- Describe what changed and why. Link to the relevant issue(s). -->

Closes #

## Type of Change

- [ ] ✨ Feature (new functionality)
- [ ] 🐛 Bug fix (non-breaking fix for an issue)
- [ ] ♻️ Refactor (code change that neither fixes a bug nor adds a feature)
- [ ] 📝 Documentation update
- [ ] ✅ Test (adding or updating tests)
- [ ] 🔧 CI/CD or infrastructure change
- [ ] 💥 Breaking change (fix or feature that would cause existing functionality to change)

## Testing

<!-- Describe what was tested and how to verify. Include relevant commands. -->

\`\`\`bash
pnpm test          # unit + integration via vitest
pnpm lint          # ESLint + Prettier check
pnpm db:check      # Prisma schema validation
\`\`\`

- [ ] New/updated unit tests cover the changes
- [ ] Tested locally against PostgreSQL and Redis
- [ ] WebSocket flows verified (if applicable)

## Screenshots

<!-- If this PR includes UI changes, add before/after screenshots. Otherwise, remove this section. -->

## AI Disclosure

<!-- Transparency: was AI used to author any part of this PR? -->

- [ ] Claude Code (or other AI tool) was used in this PR

<!-- If yes, add the co-author trailer to your commit message:
Co-authored-by: Claude <noreply@anthropic.com>
-->

## Checklist

- [ ] I have performed a self-review of my own diff
- [ ] \`pnpm test\` passes with no failures
- [ ] \`pnpm lint\` reports zero errors and warnings
- [ ] Documentation has been updated (if applicable)
- [ ] No secrets, credentials, or \`.env\` values are committed
- [ ] Commit messages follow [Conventional Commits](https://www.conventionalcommits.org/) format
- [ ] Commits are signed (\`git log --show-signature\`)
- [ ] Prisma migrations are included (if schema changed)`,

  '.github/workflows/ai-code-audit.yml': `name: AI Code Audit

on:
  schedule:
    - cron: '0 9 * * 1'  # Every Monday at 9:00 UTC
  workflow_dispatch:
    inputs:
      scope:
        description: 'Audit scope (full, security, architecture, quality)'
        required: false
        default: 'full'
        type: choice
        options:
          - full
          - security
          - architecture
          - quality

permissions:
  contents: read
  issues: write

jobs:
  ai-audit:
    name: Claude Code Audit
    runs-on: ubuntu-latest
    timeout-minutes: 30
    steps:
      - name: Checkout repository
        uses: actions/checkout@v4
        with:
          fetch-depth: 0

      - name: Setup pnpm
        uses: pnpm/action-setup@v4
        with:
          version: 9

      - name: Gather codebase metrics
        id: metrics
        run: |
          echo "ts_files=$(find src -name '*.ts' -o -name '*.tsx' | wc -l)" >> "$GITHUB_OUTPUT"
          echo "test_files=$(find src -name '*.test.ts' -o -name '*.test.tsx' | wc -l)" >> "$GITHUB_OUTPUT"
          echo "prisma_models=$(grep -c '^model ' prisma/schema.prisma 2>/dev/null || echo 0)" >> "$GITHUB_OUTPUT"
          echo "recent_commits=$(git log --oneline --since='7 days ago' | wc -l)" >> "$GITHUB_OUTPUT"

      - name: Run AI audit via Claude
        id: audit
        env:
          ANTHROPIC_API_KEY: \${{ secrets.ANTHROPIC_API_KEY }}
          AUDIT_SCOPE: \${{ inputs.scope || 'full' }}
        run: |
          # Placeholder: Replace with actual Claude API call or claude-code CLI
          # Example using Claude Code CLI (when available in CI):
          #   claude-code audit --scope "$AUDIT_SCOPE" --output audit-report.md
          #
          # For now, generate structured prompt for manual or API-based invocation
          cat > audit-prompt.txt << 'PROMPT'
          You are auditing the nextjs-enterprise project (Next.js 14, TypeScript strict, Prisma + PostgreSQL, modular-monolith).

          Analyze the codebase for:
          1. SECURITY: SQL injection, XSS, auth bypass, exposed secrets, insecure NextAuth.js config, Socket.io auth gaps
          2. QUALITY: TypeScript strict violations, dead code, complex functions (>30 LOC), missing error boundaries
          3. ARCHITECTURE: Module boundary violations in src/modules/*, circular deps, Prisma client misuse outside data layer
          4. TEST COVERAGE: Untested API routes, missing integration tests, Socket.io handler test gaps
          5. DOCUMENTATION: Stale README sections, undocumented API routes, missing JSDoc on public exports

          Output a structured Markdown report with severity levels (critical/high/medium/low) per finding.
          PROMPT

          echo "audit_date=$(date -u +%Y-%m-%d)" >> "$GITHUB_OUTPUT"
          echo "Audit prompt prepared. Source files: \${{ steps.metrics.outputs.ts_files }}, Tests: \${{ steps.metrics.outputs.test_files }}"

          # TODO: Invoke Claude API here and capture response to audit-report.md
          # curl -s https://api.anthropic.com/v1/messages ...
          echo "## AI Code Audit — \${{ steps.metrics.outputs.ts_files }} source files analyzed" > audit-report.md
          echo "Audit scope: \${AUDIT_SCOPE} | Date: $(date -u +%Y-%m-%d)" >> audit-report.md
          echo "" >> audit-report.md
          echo "> ⚠️ Placeholder report — connect ANTHROPIC_API_KEY to enable full AI audit" >> audit-report.md

      - name: Create GitHub issue with findings
        uses: peter-evans/create-issue-from-file@v5
        with:
          title: "🤖 AI Code Audit — \${{ steps.audit.outputs.audit_date }}"
          content-filepath: audit-report.md
          labels: |
            ai-audit
            automated
          assignees: \${{ github.repository_owner }}`,

  '.github/workflows/ci.yml': `name: CI

on:
  push:
    branches: [main]
  pull_request:
    branches: [main]

concurrency:
  group: ci-\${{ github.ref }}
  cancel-in-progress: true

env:
  NODE_VERSION: "20"
  PNPM_VERSION: "9"

jobs:
  lint:
    name: Lint
    runs-on: ubuntu-latest
    steps:
      - uses: actions/checkout@v4
      - uses: pnpm/action-setup@v4
        with:
          version: \${{ env.PNPM_VERSION }}
      - uses: actions/setup-node@v4
        with:
          node-version: \${{ env.NODE_VERSION }}
          cache: pnpm
      - run: pnpm install --frozen-lockfile
      - run: pnpm lint

  typecheck:
    name: Type Check
    runs-on: ubuntu-latest
    steps:
      - uses: actions/checkout@v4
      - uses: pnpm/action-setup@v4
        with:
          version: \${{ env.PNPM_VERSION }}
      - uses: actions/setup-node@v4
        with:
          node-version: \${{ env.NODE_VERSION }}
          cache: pnpm
      - run: pnpm install --frozen-lockfile
      - run: pnpm tsc --noEmit

  test:
    name: Test
    runs-on: ubuntu-latest
    services:
      postgres:
        image: postgres:16-alpine
        env:
          POSTGRES_USER: testuser
          POSTGRES_PASSWORD: testpass
          POSTGRES_DB: nextjs_enterprise_test
        ports:
          - 5432:5432
        options: >-
          --health-cmd="pg_isready -U testuser"
          --health-interval=10s
          --health-timeout=5s
          --health-retries=5
    env:
      DATABASE_URL: postgresql://testuser:testpass@localhost:5432/nextjs_enterprise_test
      NEXTAUTH_SECRET: ci-test-secret-do-not-use-in-production
      REDIS_URL: ""
    steps:
      - uses: actions/checkout@v4
      - uses: pnpm/action-setup@v4
        with:
          version: \${{ env.PNPM_VERSION }}
      - uses: actions/setup-node@v4
        with:
          node-version: \${{ env.NODE_VERSION }}
          cache: pnpm
      - run: pnpm install --frozen-lockfile
      - run: pnpm prisma db push --skip-generate
      - run: pnpm prisma generate
      - run: pnpm test --run --coverage
      - uses: actions/upload-artifact@v4
        if: always()
        with:
          name: coverage-report
          path: coverage/
          retention-days: 14

  build:
    name: Build
    needs: [lint, typecheck, test]
    runs-on: ubuntu-latest
    env:
      DATABASE_URL: postgresql://placeholder:placeholder@localhost:5432/placeholder
      NEXTAUTH_SECRET: build-secret-placeholder
    steps:
      - uses: actions/checkout@v4
      - uses: pnpm/action-setup@v4
        with:
          version: \${{ env.PNPM_VERSION }}
      - uses: actions/setup-node@v4
        with:
          node-version: \${{ env.NODE_VERSION }}
          cache: pnpm
      - run: pnpm install --frozen-lockfile
      - run: pnpm prisma generate
      - run: pnpm build`,

  '.github/workflows/deploy-production.yml': `name: Deploy Production

on:
  workflow_dispatch:
    inputs:
      image_tag:
        description: "Docker image tag to deploy (e.g., sha-abc1234)"
        required: true
        type: string
      skip_canary:
        description: "Skip canary phase and deploy directly"
        required: false
        type: boolean
        default: false

env:
  AWS_REGION: us-east-1
  ECR_REPOSITORY: nextjs-enterprise
  ECS_CLUSTER: nextjs-enterprise-prod
  ECS_SERVICE: nextjs-enterprise-prod-svc
  TASK_DEFINITION: .aws/task-definition-prod.json

permissions:
  id-token: write
  contents: read

jobs:
  deploy-production:
    name: Production Deploy
    runs-on: ubuntu-latest
    environment: production
    steps:
      - name: Checkout
        uses: actions/checkout@v4

      - name: Configure AWS credentials
        uses: aws-actions/configure-aws-credentials@v4
        with:
          role-to-assume: \${{ secrets.AWS_PROD_DEPLOY_ROLE_ARN }}
          aws-region: \${{ env.AWS_REGION }}

      - name: Login to Amazon ECR
        id: ecr-login
        uses: aws-actions/amazon-ecr-login@v2

      - name: Verify image exists in ECR
        run: |
          aws ecr describe-images \\
            --repository-name \${{ env.ECR_REPOSITORY }} \\
            --image-ids imageTag=\${{ inputs.image_tag }} \\
            --region \${{ env.AWS_REGION }}

      - name: Render ECS task definition
        id: render-task
        uses: aws-actions/amazon-ecs-render-task-definition@v1
        with:
          task-definition: \${{ env.TASK_DEFINITION }}
          container-name: nextjs-enterprise
          image: \${{ steps.ecr-login.outputs.registry }}/\${{ env.ECR_REPOSITORY }}:\${{ inputs.image_tag }}

      - name: Canary deploy (10% traffic)
        if: \${{ !inputs.skip_canary }}
        run: |
          aws ecs update-service \\
            --cluster \${{ env.ECS_CLUSTER }} \\
            --service \${{ env.ECS_SERVICE }} \\
            --task-definition $(cat \${{ steps.render-task.outputs.task-definition }} | jq -r '.taskDefinitionArn // empty') \\
            --deployment-configuration "deploymentCircuitBreaker={enable=true,rollback=true},maximumPercent=110,minimumHealthyPercent=100" \\
            --desired-count 1 \\
            --force-new-deployment
          echo "Canary task launched — routing 10% traffic via target group weight"
          aws elbv2 modify-rule \\
            --rule-arn \${{ secrets.ALB_PROD_RULE_ARN }} \\
            --actions '[{"Type":"forward","ForwardConfig":{"TargetGroups":[{"TargetGroupArn":"\${{ secrets.TG_STABLE_ARN }}","Weight":90},{"TargetGroupArn":"\${{ secrets.TG_CANARY_ARN }}","Weight":10}]}}]'

      - name: Health check & monitoring window
        if: \${{ !inputs.skip_canary }}
        run: |
          echo "Waiting 5 minutes for canary health signals..."
          for i in $(seq 1 10); do
            STATUS=$(curl -sf "\${{ secrets.PROD_HEALTH_URL }}/api/health" | jq -r '.status' || echo "unhealthy")
            echo "Check $i/10: $STATUS"
            if [ "$STATUS" != "ok" ] && [ "$i" -ge 6 ]; then
              echo "::error::Canary health check failed after 3 minutes — triggering rollback"
              exit 1
            fi
            sleep 30
          done
          echo "Canary healthy — proceeding to full rollout"

      - name: Full rollout (100% traffic)
        run: |
          aws elbv2 modify-rule \\
            --rule-arn \${{ secrets.ALB_PROD_RULE_ARN }} \\
            --actions '[{"Type":"forward","ForwardConfig":{"TargetGroups":[{"TargetGroupArn":"\${{ secrets.TG_STABLE_ARN }}","Weight":100},{"TargetGroupArn":"\${{ secrets.TG_CANARY_ARN }}","Weight":0}]}}]'
          aws ecs deploy \\
            --cluster \${{ env.ECS_CLUSTER }} \\
            --service \${{ env.ECS_SERVICE }} \\
            --task-definition \${{ steps.render-task.outputs.task-definition }} \\
            --codedeploy-appspec .aws/appspec-prod.yaml || true
          aws ecs wait services-stable --cluster \${{ env.ECS_CLUSTER }} --services \${{ env.ECS_SERVICE }}
          echo "Production deploy complete: \${{ inputs.image_tag }}"

      - name: Notify Slack
        if: always()
        uses: slackapi/slack-github-action@v1.27.0
        with:
          payload: |
            {
              "text": "\${{ job.status == 'success' && '✅' || '🚨' }} Production deploy *\${{ job.status }}*",
              "blocks": [
                {
                  "type": "section",
                  "text": {
                    "type": "mrkdwn",
                    "text": "\${{ job.status == 'success' && '✅' || '🚨' }} *nextjs-enterprise* production deploy *\${{ job.status }}*\\n• Image: \`\${{ inputs.image_tag }}\`\\n• Actor: \`\${{ github.actor }}\`\\n• <\${{ github.server_url }}/\${{ github.repository }}/actions/runs/\${{ github.run_id }}|View run>"
                  }
                }
              ]
            }
        env:
          SLACK_WEBHOOK_URL: \${{ secrets.SLACK_DEPLOY_WEBHOOK_URL }}
          SLACK_WEBHOOK_TYPE: INCOMING_WEBHOOK`,

  '.github/workflows/deploy-staging.yml': `name: Deploy to Staging

on:
  push:
    branches: [main]
  workflow_dispatch:

concurrency:
  group: deploy-staging
  cancel-in-progress: false

permissions:
  id-token: write
  contents: read

env:
  AWS_REGION: us-east-1
  ECR_REPOSITORY: nextjs-enterprise
  ECS_CLUSTER: nextjs-enterprise-staging
  ECS_SERVICE: nextjs-enterprise-web
  STAGING_URL: https://staging.nextjs-enterprise.example.com

jobs:
  deploy:
    name: Build & Deploy to Staging
    runs-on: ubuntu-latest
    environment: staging
    steps:
      - name: Checkout
        uses: actions/checkout@v4

      - name: Configure AWS credentials (OIDC)
        uses: aws-actions/configure-aws-credentials@v4
        with:
          role-to-assume: \${{ secrets.AWS_DEPLOY_ROLE_ARN }}
          aws-region: \${{ env.AWS_REGION }}

      - name: Login to Amazon ECR
        id: ecr-login
        uses: aws-actions/amazon-ecr-login@v2

      - name: Build and push Docker image
        env:
          ECR_REGISTRY: \${{ steps.ecr-login.outputs.registry }}
          IMAGE_TAG: \${{ github.sha }}
        run: |
          docker build \\
            --build-arg DATABASE_URL="\${{ secrets.STAGING_DATABASE_URL }}" \\
            --build-arg NEXT_PUBLIC_APP_ENV=staging \\
            -t $ECR_REGISTRY/$ECR_REPOSITORY:$IMAGE_TAG \\
            -t $ECR_REGISTRY/$ECR_REPOSITORY:staging-latest \\
            -f Dockerfile .
          docker push $ECR_REGISTRY/$ECR_REPOSITORY --all-tags

      - name: Update ECS service
        run: |
          aws ecs update-service \\
            --cluster $ECS_CLUSTER \\
            --service $ECS_SERVICE \\
            --force-new-deployment \\
            --query 'service.taskDefinition'
          aws ecs wait services-stable \\
            --cluster $ECS_CLUSTER \\
            --services $ECS_SERVICE

      - name: Run smoke tests
        run: |
          pnpm install --filter=smoke-tests
          pnpm --filter=smoke-tests test:smoke --base-url=$STAGING_URL

      - name: Notify Slack on success
        if: success()
        uses: slackapi/slack-github-action@v1.27.0
        with:
          payload: |
            {"text":"✅ Staging deploy succeeded for \`\${{ github.sha }}\` by \${{ github.actor }}\\n\${{ env.STAGING_URL }}"}
        env:
          SLACK_WEBHOOK_URL: \${{ secrets.SLACK_WEBHOOK_URL }}

      - name: Notify Slack on failure
        if: failure()
        uses: slackapi/slack-github-action@v1.27.0
        with:
          payload: |
            {"text":"❌ Staging deploy FAILED for \`\${{ github.sha }}\` by \${{ github.actor }}\\nRun: \${{ github.server_url }}/\${{ github.repository }}/actions/runs/\${{ github.run_id }}"}
        env:
          SLACK_WEBHOOK_URL: \${{ secrets.SLACK_WEBHOOK_URL }}`,

  '.gitignore': `# ==============================
# Dependencies
# ==============================
node_modules/
.pnpm-store/

# ==============================
# Build output
# ==============================
.next/
out/
dist/
build/
*.tsbuildinfo

# ==============================
# Environment variables
# ==============================
.env
.env.local
.env.development.local
.env.test.local
.env.production.local
# Committed env template is fine
!.env.example

# ==============================
# IDE / Editor
# ==============================
.vscode/*
!.vscode/settings.json
!.vscode/extensions.json
.idea/
*.swp
*.swo
*~

# ==============================
# OS files
# ==============================
.DS_Store
Thumbs.db
Desktop.ini

# ==============================
# Testing & Coverage
# ==============================
coverage/
.vitest-results/
test-results/
playwright-report/

# ==============================
# Database
# ==============================
*.sqlite
*.sqlite3
*.db

# NOTE: Do NOT ignore prisma/generated — the Prisma client
# is generated at build time via \`prisma generate\` and should
# not be committed, but the schema and migrations must be.
prisma/generated/

# ==============================
# Docker
# ==============================
docker-compose.override.yml

# ==============================
# Monitoring / Logs
# ==============================
logs/
*.log
npm-debug.log*
pnpm-debug.log*

# ==============================
# Misc
# ==============================
.turbo/
.cache/
.vercel/
*.pem
.sentryclirc

# Storybook (if added later)
storybook-static/`,

  '.mcp.json': `{
  "mcpServers": {
    "jira": {
      "command": "node",
      "args": ["mock-mcp-servers/jira-server.mjs"],
      "env": {
        "JIRA_PROJECT_KEY": "NXE",
        "JIRA_BASE_URL": "https://nextjs-enterprise.atlassian.net"
      }
    },
    "github": {
      "command": "node",
      "args": ["mock-mcp-servers/github-server.mjs"],
      "env": {
        "GITHUB_REPO": "nextjs-enterprise",
        "GITHUB_OWNER": "your-org"
      }
    },
    "postgres": {
      "command": "npx",
      "args": [
        "-y",
        "@modelcontextprotocol/server-postgres",
        "postgresql://postgres:postgres@localhost:5432/nextjs_enterprise_dev"
      ]
    },
    "filesystem": {
      "command": "npx",
      "args": [
        "-y",
        "@modelcontextprotocol/server-filesystem",
        "/home/user/projects/nextjs-enterprise"
      ]
    }
  }
}`,

  '.prettierrc': `{
  "$schema": "https://json.schemastore.org/prettierrc",
  "semi": true,
  "singleQuote": true,
  "trailingComma": "all",
  "printWidth": 100,
  "tabWidth": 2,
  "arrowParens": "always",
  "endOfLine": "lf",
  "bracketSpacing": true,
  "jsxSingleQuote": false,
  "plugins": ["prettier-plugin-tailwindcss"]
}`,

  'CLAUDE.md': `# CLAUDE.md — nextjs-enterprise

## Project Overview

**nextjs-enterprise** is a greenfield modular-monolith built with Next.js 14 (App Router), TypeScript (strict), Prisma ORM (PostgreSQL), and Redis caching. It runs on AWS ECS via Docker, uses NextAuth.js for authentication, Socket.io for WebSocket support, and is monitored with Prometheus + Grafana.

## Quick Reference

| Command              | Description                  |
|----------------------|------------------------------|
| \`pnpm dev\`           | Start dev server (port 3000) |
| \`pnpm build\`         | Production build             |
| \`pnpm test\`          | Run vitest suite             |
| \`pnpm test:coverage\` | Run tests with coverage      |
| \`pnpm lint\`          | ESLint + Prettier check      |
| \`pnpm lint:fix\`      | Auto-fix lint issues         |
| \`pnpm db:migrate\`    | Run Prisma migrations        |
| \`pnpm db:generate\`   | Regenerate Prisma client     |
| \`pnpm db:studio\`     | Open Prisma Studio           |

## Key Paths

- \`src/\` — Application source (app/, modules/, lib/, components/)
- \`prisma/\` — Schema, migrations, seed files
- \`.claude/rules/\` — Detailed coding rules, conventions, and constraints
- \`playbook/\` — Greenfield playbook phases and prompts
- \`.github/workflows/\` — CI/CD pipeline definitions (GitHub Actions)
- \`docker/\` — Dockerfile and compose files for local/ECS deployment

## Rules

Detailed rules are auto-loaded from \`.claude/rules/\`. These cover TypeScript conventions, module boundaries, commit standards (conventional commits, signed), testing requirements, and architecture guardrails.

## Playbook

Current methodology phase is tracked in \`playbook/current-phase.json\`. The project follows a 7-phase greenfield playbook: discovery → architecture → foundation → core-implementation → integration-hardening → testing-quality → launch.

## Plugin

Plugin: \`plugin/codeforge-nextjs-enterprise/\``,

  'README.md': `# nextjs-enterprise

Enterprise-grade Next.js 14 application with modular-monolith architecture, real-time WebSocket support, and AI-assisted development workflows.

[![CI](https://github.com/your-org/nextjs-enterprise/actions/workflows/ci.yml/badge.svg)](https://github.com/your-org/nextjs-enterprise/actions/workflows/ci.yml)
[![Coverage](https://img.shields.io/codecov/c/github/your-org/nextjs-enterprise)](https://codecov.io/gh/your-org/nextjs-enterprise)
[![TypeScript](https://img.shields.io/badge/TypeScript-5.x-blue)](https://www.typescriptlang.org/)
[![Next.js](https://img.shields.io/badge/Next.js-14-black)](https://nextjs.org/)
[![License: MIT](https://img.shields.io/badge/License-MIT-yellow.svg)](LICENSE)

## Tech Stack

| Layer | Technology |
|-------|-----------|
| Framework | Next.js 14 (App Router) |
| Language | TypeScript (strict mode) |
| Database | PostgreSQL via Prisma ORM |
| Caching | Redis |
| Auth | NextAuth.js |
| Real-time | Socket.io |
| Testing | Vitest |
| Package Manager | pnpm |
| CI/CD | GitHub Actions → AWS ECS |
| Monitoring | Prometheus + Grafana |

## Quick Start

**Prerequisites:** Node.js 20+, pnpm 9+, Docker (for PostgreSQL & Redis)

\`\`\`bash
# Clone and install
git clone https://github.com/your-org/nextjs-enterprise.git
cd nextjs-enterprise
pnpm install

# Start infrastructure (PostgreSQL + Redis)
docker compose up -d

# Set up environment and database
cp .env.example .env.local
pnpm db:migrate
pnpm db:seed

# Start development server
pnpm dev
\`\`\`

Open [http://localhost:3000](http://localhost:3000) in your browser.

## Project Structure

\`\`\`
├── src/
│   ├── app/                  # Next.js App Router pages & layouts
│   ├── modules/              # Domain modules (modular-monolith boundaries)
│   │   ├── auth/             # Authentication module
│   │   ├── users/            # User management module
│   │   └── notifications/    # Real-time notifications (Socket.io)
│   ├── lib/                  # Shared utilities, Prisma client, Redis client
│   └── components/           # Shared UI components
├── prisma/                   # Schema, migrations, seed scripts
├── tests/                    # Integration & e2e tests
├── .claude/                  # Claude Code agents, commands, skills
├── .github/workflows/        # CI/CD pipelines
├── docs/                     # Architecture, ADRs, contributing guide
├── docker-compose.yml        # Local development infrastructure
└── Dockerfile                # Production container (AWS ECS)
\`\`\`

## Available Scripts

| Command | Description |
|---------|-------------|
| \`pnpm dev\` | Start dev server with hot reload |
| \`pnpm build\` | Production build |
| \`pnpm start\` | Start production server |
| \`pnpm test\` | Run Vitest test suite |
| \`pnpm test:coverage\` | Run tests with coverage report |
| \`pnpm lint\` | ESLint + Prettier check |
| \`pnpm lint:fix\` | Auto-fix lint issues |
| \`pnpm db:migrate\` | Run Prisma migrations |
| \`pnpm db:seed\` | Seed database with sample data |
| \`pnpm db:studio\` | Open Prisma Studio GUI |
| \`pnpm db:generate\` | Regenerate Prisma client |

## Architecture

This project follows a **modular-monolith** pattern — domain logic is organized into self-contained modules under \`src/modules/\`, each with its own services, repositories, and API routes, while sharing a single deployment unit.

For detailed architecture decisions, module boundaries, and data flow diagrams, see [docs/ARCHITECTURE.md](docs/ARCHITECTURE.md).

## AI-Assisted Development

This project includes a full [Claude Code](https://docs.anthropic.com/en/docs/claude-code) configuration for AI-assisted development:

- **CLAUDE.md** — Project context and conventions for Claude Code sessions
- **plugin/codeforge-nextjs-enterprise/agents/** — Specialized agents for code review, testing, and module scaffolding
- **plugin/codeforge-nextjs-enterprise/commands/** — Slash commands for common workflows (\`/plan\`, \`/tdd\`, etc.)
- **playbook/** — 7-phase greenfield playbook with copy-pasteable prompts for each phase

Run \`/help\` in a Claude Code session to see all available commands.

## Contributing

We use **conventional commits**, **GitHub Flow** branching, and **signed commits**. All PRs require passing CI and at least one approval.

See [docs/CONTRIBUTING.md](docs/CONTRIBUTING.md) for the full guide.

## License

[MIT](LICENSE)`,

  'docs/AI-USAGE-POLICY.md': `# AI Usage Policy — nextjs-enterprise

## Purpose

This document defines guidelines for using **Claude Code** and other AI assistants on the nextjs-enterprise project. The goal is to maximize productivity while maintaining security, quality, and accountability standards expected of a senior-heavy engineering team.

## Approved Uses

- **Code generation** — scaffolding components, API routes, Prisma models, utility functions.
- **Code review assistance** — identifying bugs, suggesting improvements, checking for anti-patterns.
- **Refactoring** — modernizing patterns, extracting modules, improving type safety.
- **Test authoring** — generating Vitest unit/integration tests and test fixtures.
- **Documentation** — README updates, JSDoc comments, ADR drafts, runbook entries.
- **DevOps** — Dockerfile improvements, GitHub Actions workflows, Terraform snippets.

## Restrictions

| Rule | Detail |
|------|--------|
| **No secrets in prompts** | Never paste API keys, tokens, \`.env\` values, or AWS credentials into any AI tool. |
| **No customer/user data** | PII, database dumps, and production logs must never be shared with AI assistants. |
| **Security-critical code requires human review** | Authentication flows (NextAuth.js config), authorization middleware, cryptographic operations, and Prisma raw queries must be written or thoroughly reviewed by a human. |
| **No blind copy-paste** | Engineers must understand every line of AI-generated code before committing. |
| **No AI-generated dependency additions** | Adding new packages to \`package.json\` must be validated against the approved dependency list. |

## Disclosure Requirements

1. **Pull Requests** — Add the \`ai-assisted\` label to any PR containing AI-generated or AI-modified code.
2. **Commit trailers** — Include a co-author trailer in conventional commits:
   \`\`\`
   feat(auth): add session refresh logic

   Co-authored-by: Claude <claude@anthropic.com>
   \`\`\`
3. **ADRs** — Note when AI was used to draft or evaluate architectural decisions.

## Review Requirements

All AI-generated code follows the **same PR review process** as human-written code — no exceptions. Reviewers should pay particular attention to:

- Hardcoded values or hallucinated API surfaces
- Incorrect Prisma schema relations or missing migrations
- Socket.io event handler edge cases
- Redis cache invalidation logic
- TypeScript \`any\` casts or type assertion abuse

## Quality Standards

AI-generated code must pass all existing quality gates: \`pnpm lint\`, \`pnpm typecheck\`, \`pnpm test\`, and Prettier formatting. Coverage thresholds (≥ 80%) apply equally. Code that does not meet the bar defined in \`CLAUDE.md\` and \`.claude/rules/\` must be reworked — authorship source is irrelevant.

## Plugin & MCP Usage

| MCP Server | Status | Purpose |
|------------|--------|---------|
| \`@anthropic/mcp-filesystem\` | ✅ Approved | Local file read/write within repo |
| \`@anthropic/mcp-github\` | ✅ Approved | PR creation, issue management |
| \`@anthropic/mcp-postgres\` | ⚠️ Dev only | Query local dev database — **never production** |
| \`@anthropic/mcp-redis\` | ⚠️ Dev only | Inspect local Redis cache state |
| Any unlisted MCP server | ❌ Blocked | Requires team lead approval before use |

Plugin and MCP configurations live in \`.claude/mcp-servers.json\`. Changes require PR review from a senior engineer.

## Incident Response

If AI-generated code introduces a vulnerability or production issue:

1. **Revert** — Immediately revert the offending commit via \`git revert\`.
2. **Triage** — Open a \`severity/critical\` GitHub issue with the \`ai-incident\` label.
3. **Root cause** — Document what the AI got wrong and why review missed it.
4. **Update guardrails** — Add a rule to \`.claude/rules/\` to prevent recurrence.
5. **Notify** — Inform the team lead and update the project's incident log in \`docs/incidents/\`.

---

*Policy owner: Tech Lead · Last reviewed: 2024-12 · Review cadence: quarterly*`,

  'docs/ARCHITECTURE.md': `# Architecture — nextjs-enterprise

## System Overview

\`\`\`
┌─────────────────────────────────────────────────────────┐
│                    Client (Browser)                      │
│         React Server Components + Client Islands         │
├──────────────┬──────────────────────┬───────────────────┤
│  Next.js App Router (Edge/Node)     │  Socket.io Server │
│  ├─ Middleware (auth, rate-limit)    │  (ws://live)      │
│  ├─ API Routes (/api/v1/*)          │                   │
│  └─ Server Actions                  │                   │
├──────────────┴──────────────────────┴───────────────────┤
│                   Module Boundary Layer                   │
│  ┌──────────┐ ┌──────────┐ ┌──────────┐ ┌────────────┐ │
│  │  auth    │ │  users   │ │  api     │ │  realtime  │ │
│  │  module  │ │  module  │ │  module  │ │  module    │ │
│  └────┬─────┘ └────┬─────┘ └────┬─────┘ └─────┬──────┘ │
├───────┴────────────┴────────────┴──────────────┴────────┤
│              Shared Infrastructure Layer                  │
│  ┌──────────────┐  ┌─────────────┐  ┌────────────────┐  │
│  │ Prisma ORM   │  │ Redis Cache │  │ Event Bus      │  │
│  │ (PostgreSQL) │  │ (Sessions)  │  │ (In-process)   │  │
│  └──────────────┘  └─────────────┘  └────────────────┘  │
└─────────────────────────────────────────────────────────┘
\`\`\`

This is a **modular monolith** — a single deployable unit with strict module boundaries enforced by barrel exports and lint rules. Modules communicate through a shared event bus, never by importing each other's internals.

## Server/Client Component Strategy

- **Default to Server Components** for all pages and layouts. Data fetching happens at the RSC layer with zero client JS.
- **Client Components** (\`"use client"\`) are used only for interactivity: forms, modals, WebSocket listeners, and optimistic UI.
- **Server Actions** handle mutations; API routes exist for external/programmatic access.

## Module Structure

| Module | Path | Responsibility |
|---|---|---|
| **auth** | \`src/modules/auth/\` | NextAuth.js config, session helpers, auth middleware, guards |
| **users** | \`src/modules/users/\` | User CRUD, profile management, role/permission logic |
| **api** | \`src/modules/api/\` | Shared API utilities, rate limiting, error formatting |
| **realtime** | \`src/modules/realtime/\` | Socket.io server setup, room management, event handlers |
| **shared** | \`src/modules/shared/\` | DTOs, zod schemas, event bus, constants, type utilities |

Each module exposes a public API via \`index.ts\` barrel export. Internal files are not imported across module boundaries.

## Data Layer

- **Prisma ORM** with PostgreSQL — schema lives at \`prisma/schema.prisma\`, migrations in \`prisma/migrations/\`.
- **Redis** (via \`ioredis\`) serves two roles: NextAuth.js session store and application cache (TTL-based, prefix-namespaced keys).
- Repository pattern: each module owns its data access in \`<module>/repositories/\`. Raw Prisma client is never used in route handlers directly.

## API Design

All API routes live under \`src/app/api/v1/\`. Conventions:

- **REST verbs** map to exported functions: \`GET\`, \`POST\`, \`PUT\`, \`DELETE\` in \`route.ts\`.
- **Zod validation** on every request body/query via \`src/modules/shared/validators/\`. Invalid payloads return \`400\` with structured errors.
- **Consistent envelope**: \`{ data, meta, error }\` — enforced by \`src/modules/api/response.ts\`.

## Authentication Flow

1. User hits login → **NextAuth.js** (\`src/modules/auth/config.ts\`) handles OAuth/credentials.
2. Session stored in **Redis** with JWT strategy for stateless edge middleware.
3. \`src/middleware.ts\` reads the token, attaches user context, and redirects unauthenticated users from protected routes (\`/dashboard/*\`, \`/api/v1/*\`).
4. Server Components access session via \`getServerSession()\`. Client Components use the \`useSession()\` hook.

## Real-time (Socket.io)

A custom Next.js server (\`server.ts\`) attaches Socket.io alongside the Next.js request handler. The \`realtime\` module manages:

- **Namespaces**: \`/notifications\`, \`/collaboration\`
- **Auth middleware**: validates JWT on socket handshake
- **Redis adapter** (\`@socket.io/redis-adapter\`) for horizontal scaling across ECS tasks

## Deployment

\`\`\`
pnpm build → Docker image → ECR → ECS (Fargate)
\`\`\`

| Environment | Cluster | Database | Redis |
|---|---|---|---|
| **staging** | \`nextjs-enterprise-staging\` | RDS (db.t3.medium) | ElastiCache (cache.t3.micro) |
| **production** | \`nextjs-enterprise-prod\` | RDS Multi-AZ (db.r6g.large) | ElastiCache (cache.r6g.large, cluster mode) |

GitHub Actions pipeline: lint → test → build → push to ECR → deploy via \`aws ecs update-service\`. See \`.github/workflows/deploy.yml\`.

## Monitoring

- **Prometheus**: \`/api/metrics\` endpoint exposes request duration, error rates, active WebSocket connections, and Prisma query latency via \`prom-client\`.
- **Grafana**: dashboards provisioned from \`infra/grafana/dashboards/\`. Key panels: p50/p95/p99 latency, error rate, ECS task health, Redis hit ratio, DB connection pool usage.
- **Alerting**: Grafana alerts to Slack on error rate > 1% or p99 > 2s sustained over 5 minutes.

## Key Decisions

| Decision | Rationale |
|---|---|
| Modular monolith over microservices | Team size (5-15) doesn't justify network overhead; module boundaries allow future extraction |
| App Router over Pages Router | Server Components reduce client bundle; layouts enable shared data fetching |
| Prisma over raw SQL | Type-safe queries, migration tooling, team velocity on a greenfield project |
| Socket.io over native WebSocket | Built-in reconnection, rooms, Redis adapter for multi-instance support |
| pnpm over npm/yarn | Strict dependency resolution, disk efficiency, workspace support if needed |`,

  'docs/CONTRIBUTING.md': `# Contributing to nextjs-enterprise

Thank you for contributing! Please read this guide before opening a PR. For full environment setup, see [docs/ONBOARDING.md](./ONBOARDING.md).

## Getting Started

1. Fork the repo and clone your fork.
2. Run \`pnpm install\` to install dependencies.
3. Copy \`.env.example\` to \`.env.local\` and fill in required values.
4. Run \`pnpm db:migrate\` to set up your local PostgreSQL database via Prisma.
5. Run \`pnpm dev\` to start the development server.

## Branch Workflow

We follow **GitHub Flow** — all work branches off \`main\` and merges back via pull request.

| Branch pattern | Purpose | Example |
|---|---|---|
| \`feat/<ticket>-<slug>\` | New feature | \`feat/ENG-42-websocket-notifications\` |
| \`fix/<ticket>-<slug>\` | Bug fix | \`fix/ENG-99-auth-redirect-loop\` |
| \`chore/<slug>\` | Tooling, deps, CI | \`chore/upgrade-prisma-5\` |
| \`docs/<slug>\` | Documentation only | \`docs/api-rate-limiting\` |

## Commit Standards

We enforce **Conventional Commits**. All commits must be **signed** (\`git commit -S\`).

Good: \`feat(auth): add NextAuth.js GitHub provider\` · \`fix(api): handle null user in session middleware\`
Bad: \`fixed stuff\` · \`WIP\` · \`update files\`

Format: \`<type>(<scope>): <description>\` where type is \`feat|fix|chore|docs|test|refactor|perf|ci\`.

## Pull Request Process

1. Fill out the PR template completely (description, testing steps, screenshots if UI).
2. Link the related issue (e.g., \`Closes #42\`).
3. Ensure **all CI checks pass** — lint, type-check, tests, build.
4. Request review from at least **2 team members** (one must be a code owner).
5. Squash-merge into \`main\` after approval.

## Code Style

We use **ESLint + Prettier** with strict TypeScript. Project rules live in \`.claude/rules/\`.

\`\`\`bash
pnpm lint        # Check for issues
pnpm lint:fix    # Auto-fix lint + formatting
pnpm typecheck   # Run tsc --noEmit
\`\`\`

Do not disable ESLint rules without a comment explaining why.

## Testing Requirements

- Minimum **80% code coverage** enforced in CI via Vitest.
- Write tests **before or alongside** implementation — not as an afterthought.
- Run \`pnpm test\` locally before pushing. Use \`pnpm test:coverage\` to check thresholds.
- Integration tests for API routes, unit tests for utilities and hooks.

## Documentation

- Update \`docs/\` when changing public APIs, architecture decisions, or infrastructure.
- Add JSDoc comments to exported functions and types.
- New ADRs go in \`docs/adr/\` using the existing template.

## Issue Labels

| Label | Description |
|---|---|
| \`feature\` | New functionality |
| \`bug\` | Something broken |
| \`ai-task\` | Suitable for Claude Code agent |
| \`priority:critical\` | Blocks release |
| \`priority:high\` | Current sprint |
| \`priority:medium\` | Next sprint |
| \`priority:low\` | Backlog |

Questions? Open a discussion or reach out in the \`#eng-nextjs-enterprise\` Slack channel.`,

  'docs/ONBOARDING.md': `# Developer Onboarding Guide

Welcome to **nextjs-enterprise**! This guide will get you from zero to productive in under 30 minutes.

## Prerequisites

| Tool       | Version | Install                              |
|------------|---------|--------------------------------------|
| Node.js    | ≥ 20    | \`nvm install 20\`                     |
| pnpm       | ≥ 9     | \`corepack enable && corepack prepare pnpm@latest --activate\` |
| Docker     | latest  | [docker.com](https://docker.com)     |
| PostgreSQL | 15+     | Runs via Docker — no local install needed |

Verify: \`node -v && pnpm -v && docker --version\`

## Local Setup

\`\`\`bash
git clone git@github.com:your-org/nextjs-enterprise.git
cd nextjs-enterprise
pnpm install
cp .env.example .env.local          # fill in any missing secrets
docker compose up -d db redis       # PostgreSQL 15 + Redis 7
pnpm db:migrate                     # apply Prisma migrations
pnpm db:seed                        # optional: seed dev data
pnpm dev                            # http://localhost:3000
\`\`\`

## Key Commands

| Command              | Description                          |
|----------------------|--------------------------------------|
| \`pnpm dev\`           | Start Next.js dev server (port 3000) |
| \`pnpm build\`         | Production build                     |
| \`pnpm test\`          | Run vitest suite                     |
| \`pnpm test:watch\`    | Vitest in watch mode                 |
| \`pnpm lint\`          | ESLint + Prettier check              |
| \`pnpm lint:fix\`      | Auto-fix lint issues                 |
| \`pnpm db:migrate\`    | Run \`prisma migrate dev\`             |
| \`pnpm db:studio\`     | Open Prisma Studio (port 5555)       |
| \`pnpm db:seed\`       | Seed database with dev fixtures      |
| \`pnpm typecheck\`     | \`tsc --noEmit\` strict check          |

## Project Structure

\`\`\`
src/
├── app/              # Next.js App Router (pages, layouts, API routes)
├── modules/          # Domain modules (auth, users, billing, etc.)
├── lib/              # Shared utilities (prisma client, redis, logger)
├── components/       # Shared UI components
└── types/            # Global TypeScript types
prisma/               # Schema, migrations, seed script
.claude/              # Claude Code rules and agent configs
playbook/             # Greenfield playbook phases & prompts
plugin/               # Claude Code plugin definition
\`\`\`

Each module under \`src/modules/\` is self-contained with its own \`service.ts\`, \`router.ts\`, \`schema.ts\`, and \`__tests__/\` directory — following our modular-monolith architecture.

## Development Workflow

1. **Branch** — create from \`main\`: \`git checkout -b feat/short-description\`
2. **Develop** — commit often using [Conventional Commits](https://www.conventionalcommits.org/) (\`feat:\`, \`fix:\`, \`chore:\`)
3. **Test** — run \`pnpm test\` and \`pnpm typecheck\` before pushing
4. **PR** — open a pull request against \`main\`; CI (GitHub Actions) runs lint, typecheck, test, and build
5. **Review** — minimum 1 approval required; all commits must be signed (\`git config commit.gpgsign true\`)
6. **Merge** — squash-merge into \`main\`; deployments to AWS ECS are triggered automatically

## Using Claude Code

Install the project plugin, then leverage built-in commands:

\`\`\`bash
# Inside the repo, Claude Code auto-loads .claude/CLAUDE.md
# Available slash commands (provided by the CodeForge plugin):
/project:scaffold-module   # Generate a new domain module
/project:db-migration      # Create and apply a Prisma migration
/project:review            # AI-assisted code review
\`\`\`

Refer to \`playbook/\` for the 7-phase greenfield plan — each phase has copy-pasteable prompts and quality gates. Start at **Phase 1: Discovery** if you're setting up a new feature area.

## Team & Communication

- **Slack**: \`#nextjs-enterprise\` (general), \`#nextjs-enterprise-alerts\` (monitoring)
- **On-call rotation**: PagerDuty — see Grafana dashboards at \`https://grafana.internal\`
- **Tech lead**: Check the \`CODEOWNERS\` file for module ownership

## Common Gotchas

- **Missing env vars** — the app fails silently on missing \`DATABASE_URL\` or \`NEXTAUTH_SECRET\`. Always diff \`.env.example\` against \`.env.local\` after pulling.
- **Prisma generate** — after any \`schema.prisma\` change, run \`pnpm prisma generate\` (or \`pnpm db:migrate\` which does it automatically). Your IDE types won't update until you do.
- **\`.next\` cache** — if you see stale pages or weird build errors, nuke it: \`rm -rf .next && pnpm dev\`.
- **Redis connection** — ensure \`docker compose up -d redis\` is running; Socket.io and caching both depend on it.
- **Port conflicts** — dev server (3000), Prisma Studio (5555), PostgreSQL (5432), Redis (6379). Check \`docker compose ps\` if ports clash.`,

  'git-hooks/commit-msg': `#!/bin/bash
set -euo pipefail

# commit-msg hook: Validates conventional commit format for nextjs-enterprise

commit_msg_file="$1"
commit_msg=$(head -1 "$commit_msg_file")

# Allow merge commits
if echo "$commit_msg" | grep -qE "^Merge (branch|pull request|remote-tracking)"; then
  exit 0
fi

# Allow revert commits generated by git revert
if echo "$commit_msg" | grep -qE "^Revert \\".*\\""; then
  exit 0
fi

# Validate conventional commit format
pattern="^(feat|fix|docs|refactor|test|chore|ci|style|perf|build|revert)(\\(.+\\))?(!)?: .{1,72}$"

if ! echo "$commit_msg" | grep -qE "$pattern"; then
  echo ""
  echo "❌ Invalid commit message format."
  echo ""
  echo "Expected: <type>(<scope>): <subject>"
  echo ""
  echo "  Types:   feat | fix | docs | refactor | test | chore | ci | style | perf | build | revert"
  echo "  Scope:   optional, e.g. (auth), (api), (prisma), (websocket), (redis)"
  echo "  Subject: 1-72 chars, lowercase start, no trailing period"
  echo ""
  echo "Examples:"
  echo "  feat(auth): add NextAuth.js session provider"
  echo "  fix(prisma): resolve connection pool timeout on ECS"
  echo "  chore(ci): update GitHub Actions workflow for pnpm caching"
  echo ""
  echo "Your message: \\"$commit_msg\\""
  echo ""
  exit 1
fi`,

  'git-hooks/pre-commit': `#!/bin/bash
set -euo pipefail

RED='\\033[0;31m'
YELLOW='\\033[1;33m'
GREEN='\\033[0;32m'
CYAN='\\033[0;36m'
NC='\\033[0m'

STAGED_FILES=$(git diff --cached --name-only --diff-filter=ACM)

if [ -z "$STAGED_FILES" ]; then
  exit 0
fi

echo -e "\${CYAN}🔍 Running pre-commit checks...\${NC}"

# 1. Block .env files from being committed
ENV_FILES=$(echo "$STAGED_FILES" | grep -E '\\.env($|\\..*)' | grep -v '.env.example' || true)
if [ -n "$ENV_FILES" ]; then
  echo -e "\${RED}❌ Blocked: .env files must not be committed:\${NC}"
  echo "$ENV_FILES" | sed 's/^/   /'
  echo -e "\${RED}   Add them to .gitignore and unstage with: git reset HEAD <file>\${NC}"
  exit 1
fi

# 2. Check for secrets in staged files
SECRET_PATTERNS='AWS_SECRET|PRIVATE_KEY|password\\s*=\\s*["\\x27][^"\\x27]+|api_key\\s*=\\s*["\\x27][^"\\x27]+'
SECRETS_FOUND=$(git diff --cached -U0 --diff-filter=ACM | grep -iE "$SECRET_PATTERNS" | grep '^+' | grep -v '^+++' || true)
if [ -n "$SECRETS_FOUND" ]; then
  echo -e "\${RED}❌ Potential secrets detected in staged changes:\${NC}"
  echo "$SECRETS_FOUND" | head -5 | sed 's/^/   /'
  echo -e "\${RED}   Remove secrets before committing. Use environment variables instead.\${NC}"
  exit 1
fi

# 3. Warn about console.log in src/ (non-blocking)
SRC_FILES=$(echo "$STAGED_FILES" | grep -E '^src/.*\\.(ts|tsx)$' || true)
if [ -n "$SRC_FILES" ]; then
  CONSOLE_LOGS=$(echo "$SRC_FILES" | xargs grep -n 'console\\.log' 2>/dev/null || true)
  if [ -n "$CONSOLE_LOGS" ]; then
    echo -e "\${YELLOW}⚠️  console.log statements found (consider removing):\${NC}"
    echo "$CONSOLE_LOGS" | head -5 | sed 's/^/   /'
  fi
fi

# 4. Run lint-staged
echo -e "\${CYAN}🧹 Running lint-staged...\${NC}"
if ! pnpm exec lint-staged; then
  echo -e "\${RED}❌ lint-staged failed. Fix errors and try again.\${NC}"
  exit 1
fi

echo -e "\${GREEN}✅ All pre-commit checks passed.\${NC}"`,

  'git-hooks/pre-push': `#!/bin/bash
set -euo pipefail

RED='\\033[0;31m'
GREEN='\\033[0;32m'
YELLOW='\\033[1;33m'
NC='\\033[0m'
SECONDS=0

current_branch=$(git symbolic-ref --short HEAD 2>/dev/null || echo "detached")

# Branch protection check
if [ "$current_branch" = "main" ]; then
  echo -e "\${YELLOW}⚠  WARNING: You are pushing directly to 'main'.\${NC}"
  echo -e "\${YELLOW}   Consider using a feature branch and opening a pull request instead.\${NC}"
  read -r -p "Continue pushing to main? [y/N] " confirm
  if [[ ! "$confirm" =~ ^[Yy]$ ]]; then
    echo -e "\${RED}✖ Push aborted.\${NC}"
    exit 1
  fi
fi

# Type checking
echo -e "🔍 Running type check..."
step_start=$SECONDS
if pnpm tsc --noEmit; then
  echo -e "\${GREEN}✔ Type check passed\${NC} ($(( SECONDS - step_start ))s)"
else
  echo -e "\${RED}✖ Type check failed. Push rejected.\${NC}"
  exit 1
fi

# Full test suite
echo -e "🧪 Running test suite..."
step_start=$SECONDS
if pnpm test --run; then
  echo -e "\${GREEN}✔ All tests passed\${NC} ($(( SECONDS - step_start ))s)"
else
  echo -e "\${RED}✖ Tests failed. Push rejected.\${NC}"
  exit 1
fi

echo -e "\${GREEN}✔ All pre-push checks passed\${NC} in \${SECONDS}s"
exit 0`,

  'install.sh': `#!/bin/bash
set -euo pipefail

GREEN='\\033[0;32m'
RED='\\033[0;31m'
YELLOW='\\033[1;33m'
CYAN='\\033[0;36m'
NC='\\033[0m'

VERSION="1.0.0"

echo -e "\${CYAN}"
echo "╔══════════════════════════════════════════╗"
echo "║       nextjs-enterprise v\${VERSION}          ║"
echo "║   Modular Monolith · Next.js 14 · AWS    ║"
echo "╚══════════════════════════════════════════╝"
echo -e "\${NC}"

step() { echo -e "\${CYAN}[▸]\${NC} $1"; }
ok()   { echo -e "\${GREEN}[✔]\${NC} $1"; }
warn() { echo -e "\${YELLOW}[!]\${NC} $1"; }
fail() { echo -e "\${RED}[✘]\${NC} $1"; exit 1; }

# 1. Check prerequisites
step "Checking prerequisites..."

NODE_VERSION=$(node -v 2>/dev/null | sed 's/v//' | cut -d. -f1)
if [[ -z "\${NODE_VERSION:-}" ]] || [[ "$NODE_VERSION" -lt 20 ]]; then
  fail "Node.js >= 20 is required (found: $(node -v 2>/dev/null || echo 'none'))"
fi
ok "Node.js $(node -v)"

command -v pnpm >/dev/null 2>&1 || fail "pnpm is required. Install: npm install -g pnpm"
ok "pnpm $(pnpm -v)"

command -v docker >/dev/null 2>&1 || warn "Docker not found — needed for local PostgreSQL and Redis"
if command -v docker >/dev/null 2>&1; then ok "Docker $(docker --version | awk '{print $3}' | tr -d ',')"; fi

# 2. Install dependencies
step "Installing dependencies with pnpm..."
pnpm install --frozen-lockfile 2>/dev/null || pnpm install
ok "Dependencies installed"

# 3. Environment file
step "Setting up environment variables..."
if [[ ! -f .env.local ]]; then
  cp .env.example .env.local
  ok "Created .env.local from .env.example — review and update values"
else
  warn ".env.local already exists, skipping"
fi

# 4. Git hooks
step "Installing git hooks..."
if [[ -d .git ]] && [[ -d git-hooks ]]; then
  mkdir -p .git/hooks
  cp git-hooks/* .git/hooks/
  chmod +x .git/hooks/*
  ok "Git hooks installed (conventional commits, lint-staged)"
else
  warn "Skipping git hooks — .git directory or git-hooks/ not found"
fi

# 5. Claude Code plugin
step "Setting up Claude Code plugin..."
mkdir -p .claude
if [[ -d plugin ]]; then
  ln -sfn "$(pwd)/plugin" .claude/plugin
  ok "Claude Code plugin symlinked to .claude/plugin"
else
  warn "plugin/ directory not found, skipping Claude Code setup"
fi

# 6. Prisma generate
step "Generating Prisma client..."
pnpm exec prisma generate
ok "Prisma client generated"

# Done
echo ""
echo -e "\${GREEN}══════════════════════════════════════════\${NC}"
echo -e "\${GREEN}  Setup complete!\${NC}"
echo -e "\${GREEN}══════════════════════════════════════════\${NC}"
echo ""
echo -e "  Next steps:"
echo -e "  \${CYAN}1.\${NC} Update \${YELLOW}.env.local\${NC} with your database URL and secrets"
echo -e "  \${CYAN}2.\${NC} Start infrastructure:  \${YELLOW}docker compose up -d\${NC}"
echo -e "  \${CYAN}3.\${NC} Run migrations:        \${YELLOW}pnpm exec prisma migrate dev\${NC}"
echo -e "  \${CYAN}4.\${NC} Start dev server:      \${YELLOW}pnpm dev\${NC}"
echo -e "  \${CYAN}5.\${NC} Run tests:             \${YELLOW}pnpm test\${NC}"
echo ""`,

  'lint-staged.config.js': `module.exports = {
  '*.{ts,tsx}': ['eslint --fix --max-warnings 0', 'prettier --write'],
  '*.{json,md,yml,yaml}': ['prettier --write'],
  '*.prisma': ['npx prisma format'],
};`,

  'mock-mcp-servers/README.md': `# Mock MCP Servers

Local mock implementations of external MCP servers used by Claude Code agents. These allow the team to develop and test AI-assisted workflows **without requiring real Jira or GitHub API credentials**.

## What Are These?

Instead of connecting to live Jira and GitHub APIs during local development, these lightweight Node.js servers simulate the MCP tool interfaces. They return realistic fixture data so agents can exercise their full tool chains offline.

## Servers

| Server | File | Port | Tools Provided |
|--------|------|------|----------------|
| **Jira** | \`jira-server.mjs\` | 3100 | \`jira_get_issue\`, \`jira_create_issue\`, \`jira_transition_issue\`, \`jira_add_comment\`, \`jira_search\` |
| **GitHub** | \`github-server.mjs\` | 3101 | \`github_create_pr\`, \`github_get_pr\`, \`github_list_checks\`, \`github_add_review_comment\`, \`github_merge_pr\` |

## Quick Start

\`\`\`bash
# From project root
node mock-mcp-servers/jira-server.mjs &
node mock-mcp-servers/github-server.mjs &
\`\`\`

Both servers log requests to stdout for debugging. Fixture data lives in \`mock-mcp-servers/fixtures/\`.

## Integration with \`.mcp.json\`

The project \`.mcp.json\` at the repo root references these mock servers by default:

\`\`\`json
{
  "jira": { "url": "http://localhost:3100/mcp" },
  "github": { "url": "http://localhost:3101/mcp" }
}
\`\`\`

Claude Code agents automatically discover available tools through these endpoints.

## Swapping to Real Servers

For staging or production use, update \`.mcp.json\` (or set environment overrides) to point at the real MCP server URLs:

\`\`\`json
{
  "jira": { "url": "https://mcp-jira.internal.yourcompany.com/mcp", "token": "\${JIRA_MCP_TOKEN}" },
  "github": { "url": "https://mcp-github.internal.yourcompany.com/mcp", "token": "\${GITHUB_MCP_TOKEN}" }
}
\`\`\`

> **Tip:** Never commit real tokens. Use environment variable interpolation (\`\${VAR}\`) or a secrets manager. The \`.mcp.json\` file in the repo should always reference the mock servers — override locally or in CI via \`MCP_CONFIG_PATH\`.

## Extending Fixtures

Add or edit JSON files in \`fixtures/\` to cover new scenarios. Each fixture file maps a tool name to an array of request/response pairs. The mock server matches on input parameters and returns the first matching response.`,

  'mock-mcp-servers/github-server.mjs': `#!/usr/bin/env node

import { Server } from "@modelcontextprotocol/sdk/server/index.js";
import { StdioServerTransport } from "@modelcontextprotocol/sdk/server/stdio.js";

const REPO = "acme-corp/nextjs-enterprise";

const mockPRs = [
  { number: 42, title: "feat(auth): add NextAuth.js session provider", state: "open", head: "feat/auth-provider", base: "main", author: "alice", labels: ["feature", "auth"], created_at: "2024-11-10T09:00:00Z" },
  { number: 41, title: "fix(prisma): resolve connection pool exhaustion", state: "open", head: "fix/prisma-pool", base: "main", author: "bob", labels: ["bug", "database"], created_at: "2024-11-09T14:30:00Z" },
  { number: 40, title: "chore(deps): bump next to 14.2.16", state: "closed", head: "chore/next-bump", base: "main", author: "dependabot", labels: ["dependencies"], created_at: "2024-11-08T08:00:00Z", merged_at: "2024-11-08T12:00:00Z" },
  { number: 39, title: "feat(ws): integrate Socket.io with Redis adapter", state: "closed", head: "feat/socketio-redis", base: "main", author: "carol", labels: ["feature", "websocket"], created_at: "2024-11-07T11:00:00Z", merged_at: "2024-11-07T18:00:00Z" },
];

const mockIssues = [
  { number: 100, title: "Redis cache invalidation race condition", state: "open", labels: ["bug", "redis"], assignee: "bob", body: "Under high concurrency the Redis cache returns stale session data." },
  { number: 99, title: "Add Prometheus metrics for WebSocket connections", state: "open", labels: ["enhancement", "monitoring"], assignee: "carol", body: "Expose ws_connections_total and ws_messages_total gauges." },
  { number: 98, title: "Prisma migration fails on ECS deploy", state: "closed", labels: ["bug", "database", "devops"], assignee: "alice", body: "prisma migrate deploy times out when ECS task has no direct DB access." },
];

let nextPRNumber = 43;

const server = new Server({ name: "mock-github", version: "1.0.0" }, { capabilities: { tools: {} } });

server.setRequestHandler("tools/list", async () => ({
  tools: [
    { name: "github_get_pr", description: \`Get PR details from \${REPO}\`, inputSchema: { type: "object", properties: { number: { type: "number" } }, required: ["number"] } },
    { name: "github_list_prs", description: \`List PRs in \${REPO}\`, inputSchema: { type: "object", properties: { state: { type: "string", enum: ["open", "closed", "all"] } }, required: ["state"] } },
    { name: "github_get_issue", description: \`Get issue from \${REPO}\`, inputSchema: { type: "object", properties: { number: { type: "number" } }, required: ["number"] } },
    { name: "github_create_pr", description: \`Create a PR in \${REPO}\`, inputSchema: { type: "object", properties: { title: { type: "string" }, body: { type: "string" }, head: { type: "string" }, base: { type: "string" } }, required: ["title", "head", "base"] } },
  ],
}));

server.setRequestHandler("tools/call", async (request) => {
  const { name, arguments: args } = request.params;

  if (name === "github_get_pr") {
    const pr = mockPRs.find((p) => p.number === args.number);
    if (!pr) return { content: [{ type: "text", text: JSON.stringify({ error: "PR not found" }) }] };
    return { content: [{ type: "text", text: JSON.stringify({ repo: REPO, ...pr }) }] };
  }

  if (name === "github_list_prs") {
    const filtered = args.state === "all" ? mockPRs : mockPRs.filter((p) => p.state === args.state);
    return { content: [{ type: "text", text: JSON.stringify({ repo: REPO, total: filtered.length, prs: filtered }) }] };
  }

  if (name === "github_get_issue") {
    const issue = mockIssues.find((i) => i.number === args.number);
    if (!issue) return { content: [{ type: "text", text: JSON.stringify({ error: "Issue not found" }) }] };
    return { content: [{ type: "text", text: JSON.stringify({ repo: REPO, ...issue }) }] };
  }

  if (name === "github_create_pr") {
    const newPR = { number: nextPRNumber++, title: args.title, body: args.body || "", state: "open", head: args.head, base: args.base || "main", author: "claude-dev", labels: [], created_at: new Date().toISOString() };
    mockPRs.unshift(newPR);
    return { content: [{ type: "text", text: JSON.stringify({ repo: REPO, created: true, ...newPR }) }] };
  }

  return { content: [{ type: "text", text: JSON.stringify({ error: \`Unknown tool: \${name}\` }) }] };
});

const transport = new StdioServerTransport();
await server.connect(transport);
console.error(\`[mock-github] MCP server running for \${REPO}\`);`,

  'mock-mcp-servers/jira-server.mjs': `#!/usr/bin/env node

import { Server } from "@modelcontextprotocol/sdk/server/index.js";
import { StdioServerTransport } from "@modelcontextprotocol/sdk/server/stdio.js";
import { CallToolRequestSchema, ListToolsRequestSchema } from "@modelcontextprotocol/sdk/types.js";

const issues = new Map([
  ["AUTH-1", { key: "AUTH-1", summary: "Implement NextAuth.js session provider", description: "Set up NextAuth.js with credentials and OAuth providers, integrate with Prisma adapter", status: "In Progress", assignee: "sarah.chen", type: "Story", priority: "High" }],
  ["AUTH-2", { key: "AUTH-2", summary: "Add role-based access control middleware", description: "Create Next.js middleware for RBAC using JWT claims from NextAuth sessions", status: "To Do", assignee: "sarah.chen", type: "Story", priority: "High" }],
  ["API-1", { key: "API-1", summary: "Design REST API route conventions", description: "Establish App Router API route patterns under /api/v1 with Zod validation", status: "Done", assignee: "james.park", type: "Task", priority: "Medium" }],
  ["API-2", { key: "API-2", summary: "Implement WebSocket gateway with Socket.io", description: "Set up Socket.io server alongside Next.js, integrate Redis adapter for horizontal scaling on ECS", status: "In Progress", assignee: "james.park", type: "Story", priority: "High" }],
  ["INFRA-1", { key: "INFRA-1", summary: "Configure ECS Fargate task definitions", description: "Create Docker multi-stage build and ECS task defs with Prometheus sidecar for metrics", status: "In Review", assignee: "alex.rivera", type: "Task", priority: "High" }],
  ["INFRA-2", { key: "INFRA-2", summary: "Set up GitHub Actions CI/CD pipeline", description: "Build, test (vitest), lint (ESLint+Prettier), and deploy pipeline with pnpm caching", status: "Done", assignee: "alex.rivera", type: "Task", priority: "Medium" }],
  ["DB-1", { key: "DB-1", summary: "Design Prisma schema for core domain models", description: "Define User, Session, Account, and domain entities with PostgreSQL-specific features", status: "Done", assignee: "maria.gonzalez", type: "Story", priority: "High" }],
  ["DB-2", { key: "DB-2", summary: "Implement Redis caching layer", description: "Add Redis caching for session data and frequently accessed queries with TTL policies", status: "To Do", assignee: "maria.gonzalez", type: "Story", priority: "Medium" }],
]);

let issueCounter = 3;

const server = new Server({ name: "mock-jira", version: "1.0.0" }, { capabilities: { tools: {} } });

server.setRequestHandler(ListToolsRequestSchema, async () => ({
  tools: [
    { name: "jira_get_issue", description: "Get a Jira issue by key", inputSchema: { type: "object", properties: { key: { type: "string" } }, required: ["key"] } },
    { name: "jira_search", description: "Search issues with JQL", inputSchema: { type: "object", properties: { jql: { type: "string" } }, required: ["jql"] } },
    { name: "jira_create_issue", description: "Create a new issue", inputSchema: { type: "object", properties: { summary: { type: "string" }, description: { type: "string" }, type: { type: "string" } }, required: ["summary", "type"] } },
    { name: "jira_update_status", description: "Update issue status", inputSchema: { type: "object", properties: { key: { type: "string" }, status: { type: "string" } }, required: ["key", "status"] } },
  ],
}));

server.setRequestHandler(CallToolRequestSchema, async (request) => {
  const { name, arguments: args } = request.params;

  if (name === "jira_get_issue") {
    const issue = issues.get(args.key);
    if (!issue) return { content: [{ type: "text", text: \`Issue \${args.key} not found\` }], isError: true };
    return { content: [{ type: "text", text: JSON.stringify(issue, null, 2) }] };
  }

  if (name === "jira_search") {
    const jql = (args.jql || "").toLowerCase();
    const results = [...issues.values()].filter((i) =>
      jql.includes("status=") ? i.status.toLowerCase().includes(jql.split("status=")[1]?.trim().replace(/"/g, "")) :
      jql.includes("assignee=") ? i.assignee.includes(jql.split("assignee=")[1]?.trim().replace(/"/g, "")) :
      i.summary.toLowerCase().includes(jql) || i.key.toLowerCase().includes(jql)
    );
    return { content: [{ type: "text", text: JSON.stringify({ total: results.length, issues: results }, null, 2) }] };
  }

  if (name === "jira_create_issue") {
    const prefix = args.type === "Bug" ? "BUG" : args.type === "Story" ? "FEAT" : "TASK";
    const key = \`\${prefix}-\${issueCounter++}\`;
    const issue = { key, summary: args.summary, description: args.description || "", status: "To Do", assignee: "unassigned", type: args.type, priority: "Medium" };
    issues.set(key, issue);
    return { content: [{ type: "text", text: JSON.stringify(issue, null, 2) }] };
  }

  if (name === "jira_update_status") {
    const issue = issues.get(args.key);
    if (!issue) return { content: [{ type: "text", text: \`Issue \${args.key} not found\` }], isError: true };
    const validStatuses = ["To Do", "In Progress", "In Review", "Done"];
    if (!validStatuses.includes(args.status)) return { content: [{ type: "text", text: \`Invalid status. Valid: \${validStatuses.join(", ")}\` }], isError: true };
    issue.status = args.status;
    return { content: [{ type: "text", text: JSON.stringify(issue, null, 2) }] };
  }

  return { content: [{ type: "text", text: \`Unknown tool: \${name}\` }], isError: true };
});

const transport = new StdioServerTransport();
await server.connect(transport);
console.error("Mock Jira MCP server running on stdio");`,

  'mock-mcp-servers/package.json': `{
  "name": "@nextjs-enterprise/mock-mcp-servers",
  "version": "0.1.0",
  "private": true,
  "type": "module",
  "scripts": {
    "start:jira": "node --experimental-specifier-resolution=node jira-server.mjs",
    "start:github": "node --experimental-specifier-resolution=node github-server.mjs",
    "start:all": "concurrently \\"pnpm start:jira\\" \\"pnpm start:github\\""
  },
  "dependencies": {
    "@modelcontextprotocol/sdk": "^1.12.0"
  },
  "devDependencies": {
    "concurrently": "^9.1.2"
  }
}`,

  'package.json': `{
  "name": "nextjs-enterprise",
  "version": "0.1.0",
  "private": true,
  "scripts": {
    "dev": "next dev",
    "build": "next build",
    "start": "next start",
    "lint": "eslint . --ext .ts,.tsx --max-warnings 0",
    "lint:fix": "eslint . --ext .ts,.tsx --fix",
    "format": "prettier --write \\"**/*.{ts,tsx,json,md,css}\\"",
    "format:check": "prettier --check \\"**/*.{ts,tsx,json,md,css}\\"",
    "test": "vitest run",
    "test:coverage": "vitest run --coverage",
    "test:watch": "vitest",
    "test:e2e": "playwright test",
    "db:migrate": "prisma migrate dev",
    "db:migrate:deploy": "prisma migrate deploy",
    "db:generate": "prisma generate",
    "db:studio": "prisma studio",
    "db:seed": "tsx prisma/seed.ts",
    "db:reset": "prisma migrate reset --force",
    "typecheck": "tsc --noEmit",
    "prepare": "husky"
  },
  "dependencies": {
    "next": "14.2.15",
    "react": "^18.3.1",
    "react-dom": "^18.3.1",
    "@prisma/client": "^5.20.0",
    "next-auth": "^4.24.8",
    "socket.io": "^4.8.0",
    "socket.io-client": "^4.8.0",
    "ioredis": "^5.4.1",
    "zod": "^3.23.8",
    "clsx": "^2.1.1",
    "tailwind-merge": "^2.5.3",
    "server-only": "^0.0.1"
  },
  "devDependencies": {
    "typescript": "^5.6.3",
    "@types/react": "^18.3.11",
    "@types/react-dom": "^18.3.1",
    "@types/node": "^22.7.5",
    "vitest": "^2.1.2",
    "@vitejs/plugin-react": "^4.3.2",
    "@testing-library/react": "^16.0.1",
    "@testing-library/jest-dom": "^6.6.2",
    "@testing-library/user-event": "^14.5.2",
    "@playwright/test": "^1.48.1",
    "eslint": "^8.57.1",
    "eslint-config-next": "14.2.15",
    "prettier": "^3.3.3",
    "prettier-plugin-tailwindcss": "^0.6.8",
    "prisma": "^5.20.0",
    "tailwindcss": "^3.4.13",
    "postcss": "^8.4.47",
    "autoprefixer": "^10.4.20",
    "husky": "^9.1.6",
    "lint-staged": "^15.2.10",
    "msw": "^2.4.9",
    "tsx": "^4.19.1",
    "@vitest/coverage-v8": "^2.1.2"
  },
  "lint-staged": {
    "*.{ts,tsx}": ["eslint --fix --max-warnings 0", "prettier --write"],
    "*.{json,md,css}": ["prettier --write"]
  },
  "engines": {
    "node": ">=20.0.0",
    "pnpm": ">=9.0.0"
  },
  "packageManager": "pnpm@9.12.1"
}`,

  'playbook/PLAYBOOK.md': `# Greenfield Playbook — nextjs-enterprise

## Overview

This playbook defines a **7-phase methodology** for building the \`nextjs-enterprise\` project from scratch. Each phase has clear objectives, deliverables, quality gates, and Claude Code overrides that constrain AI behavior to phase-appropriate work.

**Why a phased approach matters:** Greenfield projects fail when teams jump to implementation before requirements are stable or ship features before infrastructure is proven. This playbook enforces discipline — you cannot skip ahead without satisfying quality gates, and Claude Code's behavior adapts to each phase via \`claude-overrides.yml\` files.

## Phase Summary

| Phase | Name | Objectives |
|-------|------|------------|
| 01 | Discovery & Requirements | Gather functional requirements, user stories, non-functional requirements (latency, uptime, scale targets), and stakeholder sign-off |
| 02 | Architecture & Design | Produce ADRs (modular-monolith, Next.js App Router, Prisma + PostgreSQL), API contract design, data model ERD, WebSocket event catalog |
| 03 | Foundation & Scaffolding | Scaffold Next.js 14 project with pnpm, configure ESLint + Prettier, set up Prisma schema, GitHub Actions CI, Docker + ECS IaC, Redis connection |
| 04 | Core Implementation | Implement NextAuth.js authentication, CRUD API routes, Prisma queries, Socket.io real-time layer, Redis caching middleware |
| 05 | Integration & Hardening | Wire up Prometheus metrics + Grafana dashboards, harden auth (CSRF, rate limiting), add structured logging, validate ECS deployment pipeline |
| 06 | Testing & Quality | Achieve ≥80% coverage with Vitest, write E2E tests, run load tests against PostgreSQL + Redis, audit bundle size, fix all critical lint warnings |
| 07 | Launch & Operations | Execute go-live checklist, publish runbooks, verify monitoring alerts, enable signed commits enforcement, cut v1.0.0 release |

## How to Use

### 1. Track Current Phase

The file \`playbook/current-phase.json\` stores the active phase:

\`\`\`json
{
  "currentPhase": 1,
  "startedAt": "2024-01-15T00:00:00Z",
  "status": "in-progress"
}
\`\`\`

Update this file when you advance phases. Claude Code agents read it to determine context.

### 2. Use the \`/phase\` Command

Run \`/phase\` in Claude Code to check your current phase, see remaining deliverables, and get the next recommended action. The command reads \`current-phase.json\` and cross-references the phase's quality gates. See the plugin's \`commands/phase.md\` for details.

### 3. Claude Overrides Per Phase

Each phase directory contains a \`claude-overrides.yml\` that constrains Claude Code behavior. For example, during Phase 01 (Discovery), Claude is **forbidden** from generating implementation code and is restricted to producing requirements documents. During Phase 04 (Core Implementation), Claude is encouraged to write feature code but must reference ADRs from Phase 02.

Overrides are loaded automatically when the corresponding phase is active. See \`playbook/phases/01-discovery/claude-overrides.yml\` through \`playbook/phases/07-launch/claude-overrides.yml\`.

## Prompt Templates

The \`playbook/prompts/\` directory contains **copy-pasteable prompts** for each phase. Each prompt file includes:

- **Project context header** — pre-filled with \`nextjs-enterprise\`, TypeScript strict mode, Next.js 14 App Router, Prisma + PostgreSQL, pnpm, and all relevant stack details
- **Full prompt text** — concrete instructions (no template variables) ready to paste into Claude Code
- **Expected output** — what deliverables the prompt should produce
- **Tips** — guidance on reviewing and iterating on the output

Start with \`playbook/prompts/01-discovery-requirements.md\` and work through each phase sequentially. Do not skip phases — each prompt assumes deliverables from prior phases exist.

## Quick Start

1. Clone the repo and run \`pnpm install\`
2. Review \`playbook/current-phase.json\` — it should read \`"currentPhase": 1\`
3. Open \`playbook/prompts/01-discovery-requirements.md\` and paste the prompt into Claude Code
4. Complete all deliverables and quality gates before advancing to Phase 02
5. Update \`current-phase.json\` and repeat for each subsequent phase`,

  'playbook/claude-overrides/01-discovery.claude.md': `# Phase 01 — Discovery & Requirements

## Context

This is the first phase of the **nextjs-enterprise** greenfield project. The goal is to deeply understand the problem space, gather functional and non-functional requirements, identify stakeholders, and define success criteria. No code is written. No architecture decisions are finalized. This phase produces the artifacts that drive every subsequent phase.

## DO

- Ask clarifying questions about business goals, target users, and expected traffic patterns before producing any artifact.
- Document every requirement with a unique identifier (e.g., \`FR-001\`, \`NFR-001\`) for traceability across later phases.
- Identify authentication flows early — NextAuth.js supports many providers; confirm which ones (OAuth, credentials, magic link) are needed.
- Capture real-time requirements explicitly — WebSocket/Socket.io use cases must be enumerated with expected concurrency and message frequency.
- Define data entities and relationships at a conceptual level (not Prisma schema) to inform the PostgreSQL data model later.
- Quantify non-functional requirements: target p99 latency, uptime SLA, RPO/RTO, concurrent WebSocket connections, Redis cache-hit ratio expectations.
- Map integration points — list every external service, API, and third-party dependency the system will interact with.

## DO NOT

- Do not write any application code, Prisma schemas, or Next.js route handlers — that belongs in Phase 03+.
- Do not select specific AWS instance types, ECS task sizes, or infrastructure configurations yet.
- Do not create database indexes, caching strategies, or Redis key patterns — those are architecture decisions for Phase 02.
- Do not assume requirements — if a feature like role-based access control is implied but not stated, ask explicitly.
- Do not produce UI wireframes or component hierarchies; keep deliverables focused on requirements, not design.
- Do not commit anything to the repository — this phase is documentation-only, stored outside \`src/\`.

## Deliverables

1. **Requirements Document** (\`docs/01-discovery/requirements.md\`) — Functional requirements with IDs, descriptions, priority (MoSCoW), and acceptance criteria.
2. **User Stories** (\`docs/01-discovery/user-stories.md\`) — Epics and stories in standard format ("As a … I want … so that …") covering auth, API, real-time, and caching features.
3. **Non-Functional Requirements Checklist** (\`docs/01-discovery/nfr-checklist.md\`) — Performance targets, security constraints, compliance needs, observability expectations (Prometheus/Grafana metrics), and scalability goals for ECS + Redis.
4. **Stakeholder Map** (\`docs/01-discovery/stakeholders.md\`) — Roles, responsibilities, and decision authority for the 5-15 person team.

## Quality Gates

- [ ] Every functional requirement has a unique ID, MoSCoW priority, and at least one acceptance criterion.
- [ ] Non-functional requirements include measurable targets (not vague terms like "fast" or "secure").
- [ ] WebSocket, authentication, and caching use cases each have at least three user stories.
- [ ] Stakeholder sign-off is recorded (even if simulated as a confirmation prompt).
- [ ] No code, schema, or infrastructure artifact exists in the deliverables.

## Next Phase

→ **Phase 02 — Architecture & Design** (\`playbook/claude-overrides/02-architecture.claude.md\`): Use these requirements to define the modular-monolith boundaries, data model, API surface, and infrastructure topology.`,

  'playbook/claude-overrides/02-architecture.claude.md': `# Phase 02 — Architecture & Design

## Context

This phase produces all architectural decisions, system design documents, and technical specifications for **nextjs-enterprise**. The goal is to define module boundaries, data models, API contracts, and infrastructure topology before writing any production code. All decisions are captured as Architecture Decision Records (ADRs).

## DO

- Write ADRs in \`docs/adr/\` using the MADR format (context, decision, consequences) for every significant choice: modular-monolith boundaries, Next.js App Router layout strategy, Prisma schema design, Redis caching strategy, Socket.io transport layer, and NextAuth.js provider configuration.
- Define the modular-monolith module map with clear boundaries (e.g., \`modules/auth\`, \`modules/core\`, \`modules/realtime\`, \`modules/notifications\`) and document inter-module communication contracts.
- Produce a complete Prisma data model in \`docs/architecture/data-model.prisma\` covering all entities, relations, indexes, and enums — targeting PostgreSQL-specific features where beneficial.
- Design the API route structure under \`app/api/\` with an OpenAPI 3.1 spec at \`docs/architecture/openapi.yaml\` covering REST endpoints, request/response schemas, error formats, and auth requirements.
- Document the WebSocket event catalog (Socket.io namespaces, event names, payloads) in \`docs/architecture/websocket-events.md\`.
- Specify the AWS ECS + Docker deployment topology, including service-to-service networking, Redis ElastiCache placement, RDS PostgreSQL configuration, and ALB routing rules in \`docs/architecture/infrastructure.md\`.
- Define the Prometheus metrics taxonomy and Grafana dashboard structure in \`docs/architecture/observability.md\`.

## DO NOT

- Do not write application source code, React components, or API route handlers — this phase produces only design documents, diagrams, and specifications.
- Do not create \`package.json\`, install dependencies, or run \`pnpm install\` — dependency decisions are documented in ADRs, not materialized yet.
- Do not generate Prisma migrations or run \`prisma generate\` — the data model is a design artifact at this stage.
- Do not configure CI/CD pipelines, Dockerfiles, or GitHub Actions workflows — infrastructure-as-code comes in Phase 03.
- Do not make assumptions about authentication flows without documenting them — specify NextAuth.js providers, session strategy (JWT vs. database), and token refresh logic explicitly in an ADR.
- Do not skip documenting trade-offs — every ADR must include rejected alternatives and their rationale.

## Deliverables

- \`docs/adr/001-modular-monolith-structure.md\` — Module boundaries and communication patterns
- \`docs/adr/002-nextauth-session-strategy.md\` — Auth provider and session configuration
- \`docs/adr/003-caching-strategy.md\` — Redis caching layers and invalidation policy
- \`docs/adr/004-websocket-architecture.md\` — Socket.io integration with Next.js
- \`docs/adr/005-database-schema-design.md\` — Prisma schema rationale and indexing strategy
- \`docs/architecture/data-model.prisma\` — Complete Prisma schema (design artifact)
- \`docs/architecture/openapi.yaml\` — Full API specification
- \`docs/architecture/websocket-events.md\` — Event catalog with payload schemas
- \`docs/architecture/infrastructure.md\` — AWS ECS deployment topology
- \`docs/architecture/observability.md\` — Metrics and dashboard definitions

## Quality Gates

- [ ] All five ADRs are written with context, decision, consequences, and rejected alternatives
- [ ] OpenAPI spec validates with \`npx @redocly/cli lint docs/architecture/openapi.yaml\`
- [ ] Prisma schema parses without errors via \`npx prisma format --schema docs/architecture/data-model.prisma\`
- [ ] Module dependency graph has no circular dependencies between defined modules
- [ ] Infrastructure diagram accounts for all runtime services: Next.js app, PostgreSQL, Redis, Socket.io server
- [ ] At least one team member (or AI review pass) has reviewed each ADR for completeness

## Next Phase

**Phase 03 — Foundation & Scaffolding**: Initialize the repository, install dependencies, configure tooling (ESLint, Prettier, vitest, Prisma), set up the Next.js App Router skeleton, and wire CI/CD pipelines based on the architecture defined here.`,

  'playbook/claude-overrides/03-foundation.claude.md': `# Phase 03 — Foundation & Scaffolding

## Context

This phase establishes the project skeleton for **nextjs-enterprise**: a Next.js 14 App Router application with TypeScript strict mode, Prisma ORM (PostgreSQL), NextAuth.js, Socket.io, and Redis caching. All tooling, CI/CD, containerization, and module boundaries are set up here — but no feature code is written yet.

## DO

- Scaffold the Next.js 14 project with \`pnpm create next-app\` using App Router, TypeScript strict, and \`src/\` directory layout.
- Configure ESLint (with \`@typescript-eslint\`), Prettier, and \`lint-staged\` with Husky for pre-commit hooks enforcing conventional commits via \`commitlint\`.
- Set up Prisma ORM with an initial empty schema pointing to PostgreSQL, including \`prisma/migrations/.gitkeep\` and a working \`pnpm db:migrate\` script.
- Create the modular-monolith folder structure under \`src/modules/\` with placeholder \`index.ts\` barrel files for \`auth\`, \`api\`, \`websocket\`, and \`cache\` modules.
- Wire up Vitest with \`vitest.config.ts\`, path aliases matching \`tsconfig.json\`, and a single smoke test (\`src/__tests__/setup.test.ts\`) that asserts the test runner works.
- Build the GitHub Actions CI pipeline (\`.github/workflows/ci.yml\`) with lint, type-check, test, and Docker build stages using pnpm caching.
- Create \`Dockerfile\`, \`docker-compose.yml\` (app + PostgreSQL + Redis), and AWS ECS task definition scaffolding under \`infra/ecs/\`.

## DO NOT

- Do not implement any authentication flows, API route handlers, or WebSocket event logic — those belong in Phase 04.
- Do not write Prisma models beyond the datasource/generator block — schema design is a Phase 04 deliverable.
- Do not add application-level environment variables to \`.env\`; only include \`DATABASE_URL\`, \`REDIS_URL\`, and \`NEXTAUTH_SECRET\` as placeholders in \`.env.example\`.
- Do not install feature-specific dependencies like \`socket.io\` or \`next-auth\` yet — only dev tooling and framework essentials.
- Do not create Grafana dashboards or Prometheus scrape configs — monitoring infrastructure is Phase 05.
- Do not merge to \`main\` without the CI pipeline passing green on the foundation PR.

## Deliverables

- [ ] Working \`pnpm dev\` / \`pnpm build\` / \`pnpm test\` commands with zero errors
- [ ] \`src/modules/\` directory structure with barrel files and \`README.md\` per module
- [ ] GitHub Actions CI workflow running lint → typecheck → test → docker-build
- [ ] \`Dockerfile\` (multi-stage, production-optimized) and \`docker-compose.yml\` for local dev
- [ ] \`infra/ecs/task-definition.json\` scaffold with placeholder image URI and environment
- [ ] Prisma client generation working with empty schema (\`pnpm db:generate\`)
- [ ] \`CONTRIBUTING.md\` documenting branch strategy (GitHub Flow), commit conventions, and PR process

## Quality Gates

- \`pnpm lint\` and \`pnpm typecheck\` exit 0 with no warnings-as-errors suppressed.
- \`pnpm test\` passes the smoke test with Vitest.
- \`docker compose up --build\` starts the app, PostgreSQL, and Redis without errors.
- CI pipeline completes all four stages on a feature branch PR.
- TypeScript strict mode (\`strict: true\`) is confirmed in \`tsconfig.json\` with no \`skipLibCheck\` workarounds.

## Next Phase

**Phase 04 — Core Implementation**: Prisma schema design, NextAuth.js integration, API route handlers, Socket.io server setup, and Redis caching layer.`,

  'playbook/claude-overrides/04-core-implementation.claude.md': `# Phase 04 — Core Implementation

## Context

This is the primary feature-building phase for **nextjs-enterprise**. The foundation layer (Phase 03) is complete — Prisma schema, NextAuth.js config, project structure, CI pipeline, and base components are in place. Now we implement the core business logic: feature modules, API routes, WebSocket events, and Redis caching layers. All work follows the modular-monolith boundaries established in the architecture phase.

## DO

- **DO** build features as self-contained modules under \`src/modules/<feature>/\` with co-located routes, services, types, and tests
- **DO** use Prisma Client via a shared singleton (\`src/lib/prisma.ts\`) and write all queries inside service files, never in route handlers directly
- **DO** implement API routes as Next.js App Router route handlers (\`app/api/<resource>/route.ts\`) with Zod request validation and consistent error responses
- **DO** protect authenticated routes using NextAuth.js \`getServerSession()\` and middleware checks — enforce RBAC where the schema defines roles
- **DO** integrate Socket.io events through \`src/lib/socket.ts\` and scope WebSocket handlers per module (e.g., \`src/modules/notifications/socket-handlers.ts\`)
- **DO** add Redis caching (\`src/lib/redis.ts\`) for read-heavy queries with explicit TTLs and cache invalidation on writes
- **DO** write Vitest unit tests for every service function and integration tests for every API route — aim for >80% coverage on new code

## DO NOT

- **DO NOT** change the Prisma schema without updating the corresponding ADR and running \`pnpm prisma migrate dev\` with a descriptive migration name
- **DO NOT** introduce new dependencies (npm packages) without justifying them in a PR description — no duplicating what Prisma, NextAuth, or Socket.io already provide
- **DO NOT** put business logic inside React components or route handlers — all logic belongs in \`src/modules/<feature>/services/\`
- **DO NOT** bypass TypeScript strict mode — no \`any\` types, no \`@ts-ignore\`, no \`as unknown as\` escape hatches
- **DO NOT** create cross-module imports between feature modules — use the shared layer (\`src/lib/\`, \`src/shared/\`) for common concerns
- **DO NOT** hardcode secrets, connection strings, or environment-specific values — use \`src/lib/env.ts\` with Zod-validated \`process.env\`
- **DO NOT** skip error handling — every async operation must have explicit try/catch with structured error logging

## Deliverables

- [ ] Authentication flow: sign-up, sign-in, sign-out, session refresh via NextAuth.js with Prisma adapter
- [ ] Core CRUD API routes for primary domain entities with Zod validation and Prisma queries
- [ ] WebSocket event handlers for real-time features (connection auth, room management, event broadcasting)
- [ ] Redis caching layer with cache-aside pattern on high-read endpoints
- [ ] Module-level Vitest test suites (unit + integration) with >80% line coverage
- [ ] API response types exported from \`src/shared/types/api.ts\` for frontend consumption
- [ ] Database seed script (\`prisma/seed.ts\`) with realistic development data

## Quality Gates

All gates must pass before advancing to Phase 05:

1. \`pnpm typecheck\` — zero TypeScript errors in strict mode
2. \`pnpm lint\` — zero ESLint/Prettier violations
3. \`pnpm test -- --coverage\` — all tests green, >80% coverage on \`src/modules/\`
4. \`pnpm prisma migrate status\` — no pending or failed migrations
5. GitHub Actions CI pipeline passes on the feature branch
6. Manual smoke test: full auth flow (register → login → protected route → logout) works end-to-end
7. No \`// TODO\` or \`// FIXME\` markers left in committed code without a linked GitHub issue

## Next Phase

**Phase 05 — Integration & Hardening**: End-to-end integration tests, performance profiling, rate limiting, security headers, error boundaries, and observability instrumentation with Prometheus metrics.`,

  'playbook/claude-overrides/05-integration-hardening.claude.md': `# Phase 05 — Integration & Hardening

## Context

All core features (Authentication, API routes, WebSocket, Redis caching) are implemented. This phase focuses on wiring external services end-to-end, hardening security boundaries, adding observability instrumentation, and ensuring the nextjs-enterprise system operates reliably under realistic conditions against PostgreSQL, Redis, and Socket.io in staging-like environments.

## DO

- Wire Prisma ORM connections through a centralized \`src/lib/db.ts\` adapter with connection pooling, retry logic, and graceful shutdown hooks.
- Integrate Redis caching layer (\`src/lib/redis.ts\`) with circuit-breaker patterns so cache failures degrade gracefully without crashing API routes.
- Harden NextAuth.js configuration: enforce CSRF protection, set secure/httpOnly/sameSite cookie flags, validate callback URLs against an allowlist, and rotate \`NEXTAUTH_SECRET\` via environment variable.
- Add Prometheus metrics instrumentation (\`src/lib/metrics.ts\`) exposing request latency histograms, active WebSocket connections gauge, Prisma query duration, and Redis hit/miss counters at \`/api/metrics\`.
- Implement structured JSON logging with correlation IDs across API routes, Socket.io event handlers, and middleware using \`pino\`.
- Add rate limiting middleware (\`src/middleware/rate-limit.ts\`) backed by Redis for all public API routes and WebSocket connection handshakes.
- Run \`pnpm audit\` and resolve all high/critical vulnerabilities; add security headers via \`next.config.js\` (\`X-Frame-Options\`, \`Content-Security-Policy\`, \`Strict-Transport-Security\`).

## DO NOT

- Do NOT add new features, pages, or API endpoints — this phase hardens what exists from Phase 04.
- Do NOT introduce new Prisma schema migrations; schema changes belong in earlier phases.
- Do NOT store secrets, API keys, or database URLs in code or \`.env\` files committed to git; reference AWS Secrets Manager or environment injection only.
- Do NOT skip error boundaries — every external service call (Prisma, Redis, Socket.io) must have explicit try/catch with typed error handling.
- Do NOT leave \`console.log\` statements; replace all with the structured \`pino\` logger.
- Do NOT expose Prometheus \`/api/metrics\` endpoint publicly; gate it behind an internal API key or network-level restriction.
- Do NOT merge integration PRs without a passing \`vitest\` suite and manual verification against Docker Compose services (\`docker compose up\` with PostgreSQL + Redis).

## Deliverables

- \`src/lib/db.ts\` — Prisma client singleton with connection pooling and health check.
- \`src/lib/redis.ts\` — Redis adapter with circuit breaker and reconnect strategy.
- \`src/lib/metrics.ts\` — Prometheus client with custom counters/histograms.
- \`src/middleware/rate-limit.ts\` — Redis-backed rate limiter for API and WebSocket.
- \`src/lib/logger.ts\` — Pino logger with correlation ID propagation.
- \`docker-compose.yml\` updated with health checks for PostgreSQL, Redis, and the app.
- Security headers configured in \`next.config.js\`.
- \`pnpm audit\` report with zero high/critical findings.

## Quality Gates

- [ ] \`pnpm vitest run\` passes with zero failures.
- [ ] \`pnpm lint\` and \`pnpm prettier --check .\` report no issues.
- [ ] Application starts and connects to PostgreSQL and Redis via \`docker compose up\` without errors.
- [ ] \`/api/metrics\` returns valid Prometheus exposition format with all custom metrics.
- [ ] Rate limiter returns \`429 Too Many Requests\` after threshold is exceeded in integration test.
- [ ] \`pnpm audit --audit-level=high\` exits with code 0.
- [ ] All API error responses return structured JSON (no stack traces in production mode).

## Next Phase

Phase 06 — Testing & Quality: comprehensive unit/integration/e2e test coverage, mutation testing, and performance benchmarks.`,

  'playbook/claude-overrides/06-testing-quality.claude.md': `# Phase 06 — Testing & Quality

## Context

All core features (authentication, API routes, WebSocket, Redis caching, Prisma models) are integrated and hardened. This phase focuses exclusively on achieving comprehensive test coverage, fixing defects, performance benchmarking, and ensuring production-grade quality across the nextjs-enterprise codebase.

## DO

- Write unit tests with Vitest for all Prisma service layers, API route handlers, and utility functions — target ≥85% line coverage
- Write integration tests for NextAuth.js flows (sign-in, sign-out, session refresh, role-based access) using \`next/test-utils\` and MSW for mocking
- Write E2E tests with Playwright covering critical user journeys: login → dashboard → WebSocket real-time updates → logout
- Add load tests for Socket.io connections and Redis cache hit/miss scenarios using k6 or Artillery
- Run \`pnpm lint\`, \`pnpm type-check\`, and \`pnpm test --coverage\` in CI via the existing GitHub Actions pipeline and enforce thresholds
- Test Prisma migrations rollback safety by running \`prisma migrate reset\` in a disposable PostgreSQL container
- Add snapshot tests for shared UI components and validate accessibility with \`@axe-core/playwright\`

## DO NOT

- Do NOT add new features, API endpoints, or database models — scope is strictly testing and quality
- Do NOT skip testing WebSocket disconnect/reconnect edge cases; Socket.io failures in production are costly
- Do NOT mock Prisma in integration tests — use a real test PostgreSQL database via Docker Compose \`db-test\` service
- Do NOT leave \`test.skip\` or \`test.todo\` markers without a linked GitHub issue explaining the deferral
- Do NOT reduce ESLint or TypeScript strict-mode rules to make tests pass; fix the source code instead
- Do NOT merge any PR that drops coverage below the 85% threshold configured in \`vitest.config.ts\`

## Deliverables

- \`src/**/*.test.ts\` — unit tests for services, hooks, and utilities
- \`src/**/*.integration.test.ts\` — integration tests for API routes and auth flows
- \`e2e/**/*.spec.ts\` — Playwright E2E test suites
- \`load-tests/\` — k6 or Artillery scripts for WebSocket and API load testing
- \`coverage/\` — Istanbul/v8 HTML coverage report with ≥85% lines, ≥80% branches
- Updated \`docker-compose.test.yml\` with \`db-test\` PostgreSQL service for CI integration tests
- Quality report summarizing coverage metrics, lint results, and known defect backlog

## Quality Gates

- [ ] \`pnpm test --coverage\` passes with ≥85% line coverage and ≥80% branch coverage
- [ ] \`pnpm lint\` and \`pnpm type-check\` report zero errors
- [ ] All Playwright E2E suites pass against a local Docker Compose stack
- [ ] Load tests confirm ≤200ms p95 API response time and ≥500 concurrent WebSocket connections
- [ ] No critical or high-severity issues in \`pnpm audit\`
- [ ] GitHub Actions CI pipeline is green on \`main\` with all test jobs passing

## Next Phase

→ **Phase 07 — Launch & Operations**: Production deployment to AWS ECS, Prometheus/Grafana monitoring, runbooks, and go-live checklist.`,

  'playbook/claude-overrides/07-launch.claude.md': `# Phase 07 — Launch & Operations

## Context

The nextjs-enterprise application is production-ready. This phase focuses on go-live execution, operational readiness, and handoff. All code is frozen — only deployment configuration, monitoring, runbooks, and incident response procedures are being finalized. The Next.js 14 app will be deployed to AWS ECS behind an ALB, with PostgreSQL (RDS), Redis (ElastiCache), and Socket.io running as a sidecar container.

## DO

- **DO** verify all ECS task definitions, service configs, and ALB health check paths (\`/api/health\`) are correct and tested against the staging environment.
- **DO** confirm Prisma migrations run cleanly against the production RDS instance using \`pnpm prisma migrate deploy\` — never \`migrate dev\` in production.
- **DO** ensure Prometheus scrape targets are configured for the ECS service discovery endpoint and Grafana dashboards cover request latency (p50/p95/p99), error rates, WebSocket connection count, Redis hit ratio, and database connection pool usage.
- **DO** write runbooks for: ECS rolling deployment, RDS failover, Redis cache flush, NextAuth.js secret rotation, and SSL certificate renewal.
- **DO** validate that all environment variables (\`DATABASE_URL\`, \`NEXTAUTH_SECRET\`, \`REDIS_URL\`, \`NEXTAUTH_URL\`) are set in AWS Secrets Manager and injected into ECS task definitions.
- **DO** confirm GitHub Actions deployment workflow (\`deploy-production.yml\`) requires manual approval and targets the correct ECS cluster and service.
- **DO** load-test the production-like environment with realistic WebSocket and API traffic to validate ECS auto-scaling policies trigger correctly.

## DO NOT

- **DO NOT** merge any feature branches — only hotfix branches prefixed with \`hotfix/\` are permitted during this phase.
- **DO NOT** modify Prisma schema or generate new migrations — the database schema is frozen.
- **DO NOT** change NextAuth.js provider configuration or authentication flows at this stage.
- **DO NOT** disable or relax any ESLint, Prettier, or TypeScript strict-mode rules to "fix" deployment issues.
- **DO NOT** expose Prometheus metrics endpoints or Grafana dashboards publicly — they must be accessible only via VPN or internal ALB.
- **DO NOT** use \`next start\` directly — production must run via the Docker container on ECS with the multi-stage build defined in \`Dockerfile\`.
- **DO NOT** skip the go-live checklist — every item must be explicitly verified and signed off.

## Deliverables

- [ ] **Go-live checklist** (\`docs/go-live-checklist.md\`) — covers DNS cutover, CDN invalidation, SSL verification, secrets rotation, database backup confirmation, and rollback plan.
- [ ] **Operational runbooks** (\`docs/runbooks/\`) — at minimum: \`deployment.md\`, \`database-failover.md\`, \`incident-response.md\`, \`cache-management.md\`, \`secret-rotation.md\`.
- [ ] **Grafana dashboard JSON exports** (\`infra/monitoring/dashboards/\`) — application overview, database performance, Redis cache, and WebSocket connections.
- [ ] **Rollback procedure** documented and tested — ECS service can revert to previous task definition within 5 minutes.
- [ ] **Alerting rules** configured in Prometheus for error rate > 1%, p99 latency > 2s, ECS task count < desired, and database connection pool exhaustion.

## Quality Gates

- All vitest suites pass: \`pnpm test -- --run\` exits 0 with no skipped tests.
- Docker image builds and starts successfully: \`docker build -t nextjs-enterprise . && docker run --rm -p 3000:3000 nextjs-enterprise\` responds on \`/api/health\`.
- Staging deployment completes via GitHub Actions without manual intervention.
- Load test confirms auto-scaling triggers at defined CPU threshold (70%) and WebSocket connections remain stable under 10k concurrent.
- Every runbook has been dry-run by at least one team member who did not author it.
- Rollback procedure executed successfully in staging — previous version serves traffic within 5 minutes.

## Next Phase

**Complete.** The application is live. Transition to ongoing operations using the runbooks and monitoring infrastructure established in this phase.`,

  'playbook/codeforge-phase.sh': `#!/bin/bash
set -euo pipefail

# CodeForge Phase Manager — nextjs-enterprise
# Manages playbook phases for the greenfield lifecycle.
# Usage: ./playbook/codeforge-phase.sh <action> [args]
# Actions: status, list, next, prev, set <n>

SCRIPT_DIR="$(cd "$(dirname "\${BASH_SOURCE[0]}")" && pwd)"
PHASE_FILE="\${SCRIPT_DIR}/current-phase.json"

# Colors
RED='\\033[0;31m'
GREEN='\\033[0;32m'
YELLOW='\\033[1;33m'
BLUE='\\033[0;34m'
CYAN='\\033[0;36m'
BOLD='\\033[1m'
NC='\\033[0m'

# Phase definitions for nextjs-enterprise greenfield playbook
PHASES=(
  "1:discovery:Define scope, gather requirements, identify risks, map domain boundaries"
  "2:architecture:Design modular-monolith structure, define module boundaries, plan data model with Prisma"
  "3:foundation:Scaffold Next.js 14 App Router, configure pnpm, ESLint, Prettier, Vitest, Docker, GitHub Actions"
  "4:core-implementation:Build auth (NextAuth.js), API routes, Prisma models, WebSocket (Socket.io), Redis caching"
  "5:integration-hardening:Wire modules together, add error handling, implement middleware, harden security"
  "6:testing-quality:Achieve coverage targets, load testing, accessibility audit, Prometheus metrics"
  "7:launch:Final QA, deploy to AWS ECS, configure Grafana dashboards, runbook validation"
)

# Ensure jq is available
command -v jq >/dev/null 2>&1 || { echo -e "\${RED}Error: jq is required but not installed.\${NC}" >&2; exit 1; }

# Initialize phase file if missing
if [[ ! -f "$PHASE_FILE" ]]; then
  echo '{"current_phase": 1, "project": "nextjs-enterprise", "updated_at": "'"$(date -u +%Y-%m-%dT%H:%M:%SZ)"'"}' | jq . > "$PHASE_FILE"
fi

get_current() { jq -r '.current_phase' "$PHASE_FILE"; }

set_phase() {
  local n=$1
  if (( n < 1 || n > 7 )); then
    echo -e "\${RED}Error: Phase must be between 1 and 7.\${NC}" >&2; exit 1
  fi
  jq --arg p "$n" --arg t "$(date -u +%Y-%m-%dT%H:%M:%SZ)" \\
    '.current_phase = ($p | tonumber) | .updated_at = $t' "$PHASE_FILE" > "\${PHASE_FILE}.tmp" \\
    && mv "\${PHASE_FILE}.tmp" "$PHASE_FILE"
}

show_phase_detail() {
  local current=$1
  for entry in "\${PHASES[@]}"; do
    IFS=':' read -r num name objectives <<< "$entry"
    if (( num == current )); then
      echo -e "\${BOLD}\${GREEN}▶ Phase \${num}: \${name}\${NC}"
      echo -e "  \${CYAN}Objectives:\${NC} \${objectives}"
      local prompt_dir="\${SCRIPT_DIR}/prompts/phase-\${num}-\${name}"
      if [[ -d "$prompt_dir" ]]; then
        echo -e "  \${YELLOW}Available prompts:\${NC}"
        for f in "$prompt_dir"/*.md; do
          [[ -f "$f" ]] && echo -e "    • $(basename "$f" .md)"
        done
      else
        echo -e "  \${YELLOW}Prompts directory:\${NC} \${prompt_dir} (not yet created)"
      fi
      local override="\${SCRIPT_DIR}/claude-overrides/phase-\${num}-\${name}.md"
      [[ -f "$override" ]] && echo -e "  \${BLUE}Claude override:\${NC} \${override}"
      return
    fi
  done
}

list_phases() {
  local current; current=$(get_current)
  echo -e "\${BOLD}nextjs-enterprise — Greenfield Playbook Phases\${NC}\\n"
  for entry in "\${PHASES[@]}"; do
    IFS=':' read -r num name objectives <<< "$entry"
    if (( num == current )); then
      echo -e "  \${GREEN}▶ \${num}. \${name}\${NC}  ← current"
    elif (( num < current )); then
      echo -e "  \${BLUE}✓ \${num}. \${name}\${NC}"
    else
      echo -e "    \${num}. \${name}"
    fi
  done
  echo ""
}

ACTION="\${1:-status}"
case "$ACTION" in
  status)
    current=$(get_current)
    echo -e "\\n\${BOLD}Current Phase Status\${NC} ($(jq -r '.updated_at' "$PHASE_FILE"))\\n"
    show_phase_detail "$current"
    echo ""
    ;;
  list)
    list_phases
    ;;
  next)
    current=$(get_current)
    set_phase $((current + 1))
    echo -e "\${GREEN}Advanced to phase $((current + 1)).\${NC}"
    show_phase_detail $((current + 1))
    ;;
  prev)
    current=$(get_current)
    set_phase $((current - 1))
    echo -e "\${YELLOW}Moved back to phase $((current - 1)).\${NC}"
    show_phase_detail $((current - 1))
    ;;
  set)
    [[ -z "\${2:-}" ]] && { echo -e "\${RED}Usage: $0 set <phase_number>\${NC}"; exit 1; }
    set_phase "$2"
    echo -e "\${GREEN}Phase set to \${2}.\${NC}"
    show_phase_detail "$2"
    ;;
  *)
    echo -e "\${RED}Unknown action: \${ACTION}\${NC}"
    echo "Usage: $0 {status|list|next|prev|set <n>}"
    exit 1
    ;;
esac`,

  'playbook/current-phase.json': `{
  "playbook": "greenfield",
  "currentPhase": 1,
  "phaseId": "discovery",
  "phaseName": "Discovery & Requirements",
  "startedAt": "2025-01-15T09:00:00Z",
  "estimatedDuration": "1-2 weeks",
  "project": "nextjs-enterprise",
  "history": []
}`,

  'playbook/phases/01-discovery.md': `# Phase 01: Discovery & Requirements

## Objectives

- Gather functional and non-functional requirements for **nextjs-enterprise**
- Define user stories with acceptance criteria for all core features (Authentication, API routes, WebSocket, Redis caching)
- Identify non-functional requirements (NFRs) covering performance, security, scalability, and observability
- Establish shared vocabulary and bounded contexts for the modular-monolith architecture
- Align the medium (5-15) senior-heavy team on scope, priorities, and constraints

## Deliverables

| Deliverable | Path | Format |
|---|---|---|
| Requirements Document | \`docs/requirements.md\` | Markdown |
| User Story Backlog | \`docs/user-stories.md\` | Markdown table with priority, estimate, acceptance criteria |
| NFR Checklist | \`docs/nfr-checklist.md\` | Checklist with measurable targets |

## Quality Gates

Before advancing to **Phase 02 — Architecture**, all of the following must be true:

1. Requirements document reviewed and contains at least one section per feature (NextAuth.js auth, API routes, Prisma/PostgreSQL data layer, Socket.io WebSocket, Redis caching).
2. User story backlog has ≥ 15 stories, each with a clear "Given / When / Then" acceptance criterion.
3. NFR checklist defines measurable targets for latency (p95 < 200 ms), uptime (99.9 %), test coverage (≥ 80 %), and security (OWASP Top 10 mitigations).
4. All deliverables committed to \`docs/\` on a branch following GitHub Flow (\`feat/01-discovery\`).
5. At least one team member has approved the PR via GitHub review.

## Claude Code Instructions

### DO

- **Ask clarifying questions** before generating any deliverable — surface ambiguity early.
- **Use concrete values** — reference PostgreSQL, Prisma, NextAuth.js, Socket.io, Redis, AWS ECS, Prometheus, and Grafana by name.
- **Write measurable acceptance criteria** — every user story must be testable with vitest or Playwright.
- **Organize stories by module** — align with the modular-monolith bounded contexts (auth, api, realtime, cache, shared).
- **Include edge cases and error scenarios** in user stories (e.g., expired JWT, Redis connection failure, WebSocket reconnect).
- **Reference the project stack** — TypeScript strict mode, Next.js 14 App Router, pnpm, GitHub Actions CI.
- **Output files directly to \`docs/\`** so they are ready to commit.

### DON'T

- **Don't write code or scaffolding** — this phase is documentation only.
- **Don't make technology choices** that aren't already specified (e.g., don't introduce a message queue).
- **Don't skip NFRs** — observability (Prometheus/Grafana) and deployment (AWS ECS + Docker) requirements are first-class.
- **Don't use vague acceptance criteria** like "should be fast" — always include a number.
- **Don't create a monolithic requirements dump** — keep documents focused and linkable.
- **Don't assume a single-developer workflow** — write for a 5-15 person team with conventional commits and signed commits.

## Available Prompts

| Prompt | File | Purpose |
|---|---|---|
| \`gather-requirements\` | \`playbook/prompts/01-gather-requirements.md\` | Generate the full requirements document from project parameters |
| \`user-stories\` | \`playbook/prompts/01-user-stories.md\` | Produce a prioritized user story backlog with acceptance criteria |
| \`nfr-checklist\` | \`playbook/prompts/01-nfr-checklist.md\` | Create a measurable NFR checklist covering perf, security, and ops |

## Next Phase

➡ **[Phase 02 — Architecture & Technical Design](./02-architecture.md)**: Define system architecture, module boundaries, data model, and infrastructure topology for the modular-monolith on AWS ECS.`,

  'playbook/phases/02-architecture.md': `# Phase 02: Architecture & Design

## Objectives

- Create Architecture Decision Records (ADRs) for all major technology and pattern choices
- Design RESTful and WebSocket API contracts with OpenAPI 3.1 specifications
- Model the PostgreSQL data schema using Prisma ORM conventions
- Define module boundaries for the modular-monolith architecture
- Establish authentication flows using NextAuth.js with session and JWT strategies
- Document Redis caching strategy and invalidation patterns

## Deliverables

| Deliverable | Format | Location |
|---|---|---|
| Architecture Decision Records | Markdown | \`docs/adr/\` |
| OpenAPI 3.1 Specification | YAML | \`docs/api/openapi.yaml\` |
| Prisma Data Model | Prisma Schema | \`prisma/schema.prisma\` |
| Module Dependency Diagram | Mermaid in Markdown | \`docs/architecture/module-map.md\` |
| WebSocket Event Catalog | Markdown | \`docs/api/websocket-events.md\` |
| Caching Strategy | Markdown | \`docs/architecture/caching-strategy.md\` |

## Quality Gates

Before proceeding to **Phase 03: Foundation**, all of the following must be true:

- [ ] At least 5 ADRs exist covering: framework choice, ORM, auth strategy, caching layer, deployment model
- [ ] OpenAPI spec validates with \`npx @redocly/cli lint docs/api/openapi.yaml\`
- [ ] Prisma schema parses without errors via \`npx prisma validate\`
- [ ] Module boundaries are documented with clear public API surfaces and no circular dependencies
- [ ] WebSocket event catalog covers connection lifecycle, authentication, and at least 3 domain events
- [ ] All deliverables have been reviewed by at least one senior engineer

## Claude Code Instructions

### DO

- Reference the project's modular-monolith pattern when defining module boundaries (\`src/modules/<name>/\`)
- Use ADR format: Title, Status, Context, Decision, Consequences
- Design API routes under Next.js App Router conventions (\`app/api/<resource>/route.ts\`)
- Include Prisma relation annotations (\`@relation\`) and indexes (\`@@index\`) in the data model
- Define explicit TypeScript interfaces for all API request/response shapes
- Consider ECS + Docker deployment constraints when making infrastructure decisions
- Document Socket.io namespace and room patterns for WebSocket architecture

### DON'T

- Do not generate implementation code — this phase produces specs and documentation only
- Do not skip writing ADR consequences sections; trade-offs must be explicit
- Do not design APIs without pagination, error envelopes, and versioning strategy
- Do not model data without considering NextAuth.js required tables (\`User\`, \`Account\`, \`Session\`)
- Do not create circular dependencies between modules in the dependency diagram
- Do not assume a single Redis instance — design for connection pooling via \`ioredis\`
- Do not finalize schemas without nullable/default annotations on optional fields

## Available Prompts

| Prompt | File | Purpose |
|---|---|---|
| ADR Generator | \`playbook/prompts/02-adr-generator.md\` | Generate a complete ADR for a given architectural decision |
| API Design | \`playbook/prompts/02-api-design.md\` | Produce OpenAPI 3.1 route definitions for a resource |
| Data Model Design | \`playbook/prompts/02-data-model-design.md\` | Create Prisma schema models with relations and indexes |

## Next Phase

→ **[Phase 03: Foundation](./03-foundation.md)** — Scaffold the Next.js 14 project, configure pnpm workspace, set up Prisma, Docker, and CI/CD pipelines.`,

  'playbook/phases/03-foundation.md': `# Phase 03: Foundation & Scaffolding

## Objectives

- Scaffold the Next.js 14 App Router project with TypeScript strict mode and pnpm
- Configure ESLint, Prettier, and conventional commit tooling (commitlint + husky)
- Set up Prisma ORM with PostgreSQL schema and initial migration
- Establish the modular-monolith folder structure (\`src/modules/\`, \`src/shared/\`, \`src/lib/\`)
- Create the GitHub Actions CI pipeline (lint → typecheck → test → build)
- Provision baseline AWS infrastructure via IaC (ECS cluster, RDS, ElastiCache, ECR)
- Wire up Prometheus metrics endpoint and Grafana dashboard scaffolding

## Deliverables

| Deliverable | Artifact |
|---|---|
| Working project skeleton | \`package.json\`, \`tsconfig.json\`, \`next.config.mjs\`, \`prisma/schema.prisma\` |
| CI pipeline | \`.github/workflows/ci.yml\` |
| IaC templates | \`infra/\` directory with CDK or Terraform for ECS, RDS, ElastiCache |
| Dev environment | \`docker-compose.yml\` (PostgreSQL, Redis, app) |
| Documentation | Updated \`README.md\` with setup instructions |

## Quality Gates

- [ ] \`pnpm install\` completes with zero warnings
- [ ] \`pnpm lint\` and \`pnpm typecheck\` pass with no errors
- [ ] \`pnpm test\` runs vitest suite with at least one passing smoke test
- [ ] \`pnpm build\` produces a successful Next.js production build
- [ ] CI pipeline runs green on a push to \`main\`
- [ ] \`docker compose up\` starts the full local stack (app, PostgreSQL, Redis)
- [ ] Prisma migration applies cleanly against local PostgreSQL
- [ ] Signed commits enforced via \`git log --show-signature\`

## Claude Code Instructions

### DO

1. Use exact versions from the architecture doc — Next.js 14, TypeScript 5.x, Prisma 5.x, NextAuth.js v5
2. Generate \`vitest.config.ts\` with path aliases matching \`tsconfig.json\`
3. Create a \`src/modules/\` directory with placeholder \`auth/\` and \`core/\` modules
4. Add \`healthcheck\` API route at \`src/app/api/health/route.ts\` returning \`{ status: "ok" }\`
5. Configure \`commitlint.config.js\` enforcing conventional commits
6. Include \`Dockerfile\` with multi-stage build (deps → build → runner) targeting Node 20 Alpine
7. Set up \`.env.example\` with all required variables (\`DATABASE_URL\`, \`REDIS_URL\`, \`NEXTAUTH_SECRET\`)

### DON'T

1. Don't implement business logic — this phase is skeleton only
2. Don't add authentication flows yet; just install NextAuth.js as a dependency
3. Don't create production secrets or real AWS credentials in any committed file
4. Don't over-engineer the Prisma schema — only \`User\` and \`Account\` models for now
5. Don't skip the \`docker-compose.yml\` — local dev parity is a hard requirement
6. Don't use \`npm\` or \`yarn\` anywhere; all scripts and docs must reference \`pnpm\`
7. Don't add Socket.io wiring yet — WebSocket support comes in Phase 04

## Available Prompts

| Prompt | File | Purpose |
|---|---|---|
| \`project-scaffold\` | \`playbook/prompts/03-project-scaffold.md\` | Generate full project skeleton with all config files |
| \`ci-pipeline\` | \`playbook/prompts/03-ci-pipeline.md\` | Create GitHub Actions workflow for lint, test, build |
| \`iac-setup\` | \`playbook/prompts/03-iac-setup.md\` | Scaffold AWS infrastructure templates (ECS, RDS, ElastiCache) |

## Next Phase

**→ Phase 04: Core Implementation** — Build out authentication flows, API routes, Prisma models, and WebSocket integration.`,

  'playbook/phases/04-core-implementation.md': `# Phase 04: Core Implementation

## Objectives

- Implement all feature modules defined in the architecture phase (WebSocket via Socket.io, Redis caching layer, API routes)
- Build the complete authentication system using NextAuth.js with session management and role-based access
- Generate CRUD modules for all Prisma models with validation, error handling, and pagination
- Wire up the modular-monolith boundaries with clean inter-module contracts
- Establish real-time event patterns between Socket.io and Redis pub/sub

## Deliverables

| Deliverable | Path | Description |
|---|---|---|
| Feature modules | \`src/modules/*/\` | Self-contained modules with routes, services, and repositories |
| Auth flow | \`src/modules/auth/\` | NextAuth.js config, providers, middleware guards, RBAC utilities |
| CRUD endpoints | \`src/app/api/*/route.ts\` | RESTful endpoints for every Prisma model with Zod validation |
| WebSocket server | \`src/lib/socket/\` | Socket.io server integration with Next.js, Redis adapter |
| Redis cache layer | \`src/lib/cache/\` | Cache-aside pattern with TTL, invalidation, and typed helpers |
| Prisma migrations | \`prisma/migrations/\` | All schema changes with seed data for development |

## Quality Gates

- [ ] All CRUD endpoints return correct status codes and follow consistent response envelope
- [ ] NextAuth.js sign-in, sign-out, and session refresh work across all providers
- [ ] Role-based middleware blocks unauthorized access with proper 401/403 responses
- [ ] WebSocket connections authenticate via session token and reconnect gracefully
- [ ] Redis cache hit/miss is observable via Prometheus metrics
- [ ] Every module has ≥80% unit test coverage via \`pnpm test -- --coverage\`
- [ ] \`pnpm lint\` and \`pnpm typecheck\` pass with zero errors
- [ ] Prisma migrations apply cleanly on a fresh PostgreSQL database (\`pnpm db:reset\`)

## Claude Code Instructions

### DO

1. **Follow the modular-monolith structure** — every feature lives under \`src/modules/<name>/\` with its own \`service.ts\`, \`repository.ts\`, and \`types.ts\`.
2. **Use Zod schemas** for all API request/response validation; co-locate schemas in \`src/modules/<name>/schemas.ts\`.
3. **Write Vitest tests alongside implementation** — create \`__tests__/\` directories inside each module, not after the fact.
4. **Use Prisma transactions** for any multi-table write operations; never rely on application-level rollback.
5. **Emit conventional commit messages** — \`feat(auth): add Google OAuth provider\`, \`feat(cache): add Redis cache-aside helper\`.
6. **Reference \`src/lib/errors/\`** for consistent error classes; throw \`AppError\` subtypes, never raw \`Error\`.
7. **Add OpenAPI JSDoc comments** on every route handler for future documentation generation.

### DON'T

1. **Don't install new dependencies** without checking \`pnpm ls\` first — the foundation phase already added core packages.
2. **Don't put business logic in route handlers** — route files should only parse input, call a service, and return a response.
3. **Don't skip input validation** — every \`POST\`/\`PUT\`/\`PATCH\` handler must validate with Zod before touching the service layer.
4. **Don't hardcode secrets or connection strings** — always read from \`src/lib/env.ts\` which validates \`process.env\` with Zod.
5. **Don't create God modules** — if a module file exceeds ~200 lines, split it into focused sub-files.
6. **Don't write integration tests yet** — that belongs in Phase 05 (Integration & Hardening). Stick to unit tests with mocked dependencies.
7. **Don't modify CI/CD pipelines or Docker configs** — infrastructure changes are out of scope for this phase.

## Available Prompts

| Prompt | File | Purpose |
|---|---|---|
| Feature Implementation | \`playbook/prompts/04a-feature-implementation.md\` | Scaffold and implement a complete feature module |
| Auth Implementation | \`playbook/prompts/04b-auth-implementation.md\` | Build NextAuth.js config, guards, and RBAC |
| CRUD Generator | \`playbook/prompts/04c-crud-generator.md\` | Generate validated CRUD endpoints for a Prisma model |

## Next Phase

**[Phase 05: Integration & Hardening](./05-integration-hardening.md)** — End-to-end integration tests, error boundary hardening, rate limiting, and performance profiling.`,

  'playbook/phases/05-integration-hardening.md': `# Phase 05: Integration & Hardening

## Objectives

- Build and validate all external integration adapters (Redis, WebSocket, third-party APIs)
- Harden security across authentication flows, API routes, and data access layers
- Deploy full observability stack with Prometheus metrics, Grafana dashboards, and structured logging
- Ensure all inter-module communication is resilient with retry logic and circuit breakers
- Validate WebSocket (Socket.io) connections under concurrent load

## Deliverables

| Deliverable | Description | Location |
|---|---|---|
| Integration Adapters | Redis caching layer, Socket.io server, external API clients | \`src/lib/integrations/\` |
| Security Audit Report | OWASP checklist, NextAuth.js hardening, CSP headers, rate limiting | \`docs/security-audit.md\` |
| Monitoring Setup | Prometheus exporters, Grafana dashboard JSON, health endpoints | \`infra/monitoring/\` |
| Resilience Patterns | Retry policies, circuit breakers, graceful degradation handlers | \`src/lib/resilience/\` |
| Load Test Results | k6 scripts and baseline results for WebSocket and API routes | \`tests/load/\` |

## Quality Gates

- [ ] All integration adapters have unit and integration tests passing in CI (\`pnpm test:integration\`)
- [ ] Security audit checklist has zero critical or high findings
- [ ] Rate limiting is enforced on all public API routes (verified via test)
- [ ] Prometheus scrape endpoint (\`/api/metrics\`) returns valid metrics
- [ ] Grafana dashboard renders request latency, error rate, and active WebSocket connections
- [ ] Redis cache hit/miss ratio is instrumented and visible in dashboards
- [ ] WebSocket connections survive reconnect scenarios under simulated network disruption
- [ ] All Prisma queries use parameterized inputs — no raw SQL without explicit review
- [ ] CSP, CORS, and security headers pass \`npx is-website-vulnerable\` and \`helmet\` checks

## Claude Code Instructions

### DO

- Use the existing modular-monolith boundaries in \`src/modules/\` when adding integration code — adapters live in \`src/lib/integrations/\`, not inside modules
- Add structured JSON logging via \`pino\` to every integration adapter with correlation IDs
- Implement Redis caching with explicit TTLs and cache invalidation strategies per entity
- Write k6 load test scripts for critical paths: auth flow, WebSocket handshake, top 3 API routes
- Configure NextAuth.js with CSRF protection, secure cookie settings, and session rotation
- Add Prometheus \`prom-client\` counters and histograms to API route middleware in \`src/middleware.ts\`
- Use \`zod\` schemas to validate all inbound data at integration boundaries

### DON'T

- Don't introduce new npm packages without checking existing dependencies — prefer \`prom-client\`, \`ioredis\`, \`socket.io\` already in \`package.json\`
- Don't store secrets in code or config files — reference \`AWS SSM\` parameter paths or \`.env.local\` with documentation
- Don't skip error handling on Redis or WebSocket disconnects — every adapter must have a fallback path
- Don't create Grafana dashboards manually — export as JSON provisioning files in \`infra/monitoring/grafana/dashboards/\`
- Don't add observability code that increases p99 latency by more than 5ms — measure before and after
- Don't bypass NextAuth.js middleware for protected routes — use \`src/middleware.ts\` matcher patterns
- Don't write integration tests that depend on external services without Docker Compose fixtures in \`docker-compose.test.yml\`

## Available Prompts

| Prompt | File | Purpose |
|---|---|---|
| \`integration-adapter\` | \`playbook/prompts/05a-integration-adapter.md\` | Generate a resilient adapter for Redis, Socket.io, or external APIs |
| \`security-hardening\` | \`playbook/prompts/05b-security-hardening.md\` | Run security audit and apply fixes for NextAuth.js, headers, rate limiting |
| \`observability-setup\` | \`playbook/prompts/05c-observability-setup.md\` | Set up Prometheus metrics, Grafana dashboards, and health check endpoints |

## Next Phase

**[Phase 06: Testing & Quality](./06-testing-quality.md)** — Comprehensive test coverage, E2E test suites, performance benchmarks, and code quality enforcement before launch.`,

  'playbook/phases/06-testing-quality.md': `# Phase 06: Testing & Quality

## Objectives

- Write comprehensive unit and integration test suites for all modules
- Implement end-to-end tests covering critical user journeys
- Execute load tests against API routes and WebSocket endpoints
- Achieve and enforce code coverage thresholds across the codebase
- Validate authentication flows, Redis caching behavior, and Prisma queries under test conditions

## Deliverables

- **Unit/Integration Test Suite** — Vitest tests for all services, utilities, API routes, and Prisma repositories in \`src/**/__tests__/\`
- **E2E Test Suite** — Playwright tests in \`e2e/\` covering auth flows, CRUD operations, and WebSocket interactions
- **Load Test Results** — k6 scripts in \`load-tests/\` with documented results for API routes (p95 < 200ms at 500 RPS) and Socket.io connections (1,000 concurrent)
- **Coverage Report** — Istanbul/v8 coverage output meeting thresholds: 80% statements, 75% branches, 80% functions
- **Quality Summary** — Markdown report in \`docs/quality-report.md\` with findings, flaky test inventory, and remediation plan

## Quality Gates

- [ ] All Vitest tests pass (\`pnpm test\`) with zero failures
- [ ] Code coverage meets thresholds: ≥80% statements, ≥75% branches, ≥80% functions
- [ ] Playwright E2E suite passes against a local Docker Compose environment
- [ ] Load test p95 latency < 200ms for all API routes at 500 RPS
- [ ] WebSocket load test sustains 1,000 concurrent connections without dropped messages
- [ ] No critical or high-severity issues in \`pnpm lint\` and \`pnpm type-check\`
- [ ] GitHub Actions CI pipeline runs all test suites and reports green

## Claude Code Instructions

### DO

- Use Vitest \`describe\`/\`it\` blocks with clear, behavior-driven test names
- Mock Prisma client with \`vitest-mock-extended\` for unit tests; use a test PostgreSQL database for integration tests
- Test NextAuth.js session handling by mocking \`getServerSession\` in API route tests
- Write Playwright tests that run against \`docker compose -f docker-compose.test.yml up\`
- Generate k6 scripts that target the actual API routes defined in \`src/app/api/\`
- Test Redis caching by asserting cache hits/misses with a real Redis instance in Docker
- Add test utilities and fixtures in \`src/test-utils/\` for shared setup across modules

### DON'T

- Don't write snapshot tests for API responses — use explicit assertions
- Don't skip flaky tests with \`.skip\` — fix the root cause or document in the quality report
- Don't mock at too many layers — prefer integration tests over heavily mocked unit tests
- Don't hardcode test data — use factory functions in \`src/test-utils/factories.ts\`
- Don't test implementation details like internal state — test observable behavior
- Don't run load tests against production or shared environments
- Don't ignore TypeScript errors in test files — maintain strict mode in \`tsconfig.json\`

## Available Prompts

| Prompt | File | Purpose |
|--------|------|---------|
| Test Suite Generator | \`playbook/prompts/06a-test-suite-generator.md\` | Generate Vitest unit and integration tests for a given module |
| Load Test | \`playbook/prompts/06b-load-test.md\` | Create k6 load test scripts for API routes and WebSocket endpoints |
| E2E Test Generator | \`playbook/prompts/06c-e2e-test-generator.md\` | Generate Playwright E2E tests for critical user journeys |

## Next Phase

**[Phase 07: Launch](./07-launch.md)** — Production deployment to AWS ECS, DNS cutover, monitoring verification, and launch checklist completion.`,

  'playbook/phases/07-launch.md': `# Phase 07: Launch & Operations

## Objectives

- Complete and verify the go-live checklist for production deployment
- Create operational runbooks for common incidents and maintenance tasks
- Configure Prometheus + Grafana monitoring dashboards for nextjs-enterprise
- Validate AWS ECS deployment pipeline end-to-end with Docker containers
- Establish on-call procedures and escalation paths for the team
- Confirm Redis caching, WebSocket (Socket.io), and PostgreSQL health checks are operational

## Deliverables

| Deliverable | Path / Location |
|---|---|
| Go-live checklist | \`docs/operations/go-live-checklist.md\` |
| Operational runbooks | \`docs/operations/runbooks/\` |
| Grafana dashboard JSON | \`infra/monitoring/dashboards/\` |
| Prometheus alert rules | \`infra/monitoring/alerts.yml\` |
| Post-launch validation script | \`scripts/post-launch-validate.sh\` |

## Quality Gates

- [ ] All GitHub Actions CI pipelines pass on \`main\` (lint, vitest, build, Docker)
- [ ] ECS service is healthy with at least 2 running tasks and passing ALB health checks
- [ ] Prisma migrations applied to production PostgreSQL without errors
- [ ] NextAuth.js authentication flow verified in production (sign-in, session, sign-out)
- [ ] Prometheus is scraping all targets; Grafana dashboards render real data
- [ ] Redis connectivity confirmed; cache hit/miss metrics visible
- [ ] WebSocket connections establish and maintain through the load balancer
- [ ] Runbooks reviewed and approved by at least one senior engineer
- [ ] Rollback procedure tested and documented

## Claude Code Instructions

### DO

1. Generate concrete, copy-pasteable shell commands in runbooks — no pseudocode
2. Reference actual service names (\`nextjs-enterprise-ecs\`, Prisma, Redis, Socket.io)
3. Include health check endpoints (\`/api/health\`, \`/api/health/db\`, \`/api/health/redis\`)
4. Define Prometheus alerting thresholds based on realistic SLOs (p99 < 500ms, error rate < 1%)
5. Create the post-launch validation script that exercises all critical paths
6. Use pnpm scripts for any operational commands (\`pnpm run db:migrate:status\`, etc.)
7. Document rollback steps for ECS deployments including Prisma migration rollback

### DON'T

1. Don't introduce new features or refactor application code in this phase
2. Don't skip documenting environment-specific configuration (staging vs production)
3. Don't hardcode secrets — reference AWS Secrets Manager or environment variables
4. Don't create monitoring that only covers happy paths; include error and saturation metrics
5. Don't assume a single-instance deployment; account for ECS multi-task topology
6. Don't leave runbooks without severity classification and estimated resolution times
7. Don't generate dashboards without testing the underlying PromQL queries

## Available Prompts

| Prompt | File | Description |
|---|---|---|
| \`go-live-checklist\` | \`playbook/prompts/07-go-live-checklist.md\` | Generates a comprehensive pre-launch verification checklist |
| \`runbook-generator\` | \`playbook/prompts/07-runbook-generator.md\` | Creates incident runbooks for database, Redis, ECS, and WebSocket failures |
| \`monitoring-setup\` | \`playbook/prompts/07-monitoring-setup.md\` | Scaffolds Prometheus rules and Grafana dashboard JSON for all services |

## Next Phase

This is the final phase of the greenfield playbook. After launch, transition to **ongoing operations** mode:

- Rotate playbook focus to incident response and iterative improvement
- Schedule a post-launch retrospective within 1 week
- Begin tracking SLO compliance in Grafana and adjust alert thresholds based on real traffic
- Feed learnings back into runbooks and the \`docs/operations/\` directory`,

  'playbook/prompts/01/gather-requirements.md': `# Phase 01 — Discovery & Requirements | Project: nextjs-enterprise

## Gather Requirements Prompt

Copy and paste the following prompt directly into Claude Code:

---

**Prompt:**

> Analyze this greenfield project and generate a structured requirements document at \`docs/requirements.md\`. The project is **nextjs-enterprise**, a modular-monolith built with Next.js 14 (App Router), TypeScript strict mode, Prisma ORM with PostgreSQL, NextAuth.js authentication, Socket.io WebSockets, and Redis caching. It will be deployed to AWS ECS via Docker and managed by a senior-heavy team of 5-15 engineers using pnpm, vitest, and GitHub Flow.
>
> The requirements document must include these sections:
>
> 1. **Project Overview** — Purpose, target users, and success criteria for the application.
> 2. **Functional Requirements** — Enumerate features: authentication flows (OAuth, credentials, session management via NextAuth.js), API route contracts (REST endpoints under \`app/api/\`), real-time features (Socket.io event types and channels), and CRUD operations backed by Prisma models.
> 3. **Technical Requirements** — TypeScript strict mode enforcement, Next.js 14 App Router conventions, Prisma schema design principles, Redis caching strategy (cache-aside pattern, TTLs), WebSocket connection lifecycle, and modular-monolith module boundaries.
> 4. **Non-Functional Requirements** — Performance targets (p95 latency < 200ms for API routes, WebSocket reconnect < 3s), availability (99.9% uptime on ECS), security (signed commits, OWASP top 10 mitigations, CSRF/XSS protections), observability (Prometheus metrics, Grafana dashboards).
> 5. **Constraints** — pnpm workspace structure, GitHub Actions CI/CD pipeline limits, AWS ECS Fargate resource quotas, PostgreSQL connection pooling limits.
> 6. **Assumptions** — Team familiarity with Next.js and Prisma, AWS account with ECS/ECR access pre-provisioned, Redis ElastiCache available, DNS and TLS managed externally.
> 7. **Open Questions** — List at least 5 unresolved decisions that need stakeholder input before Phase 02 (Architecture).
>
> Use markdown with clear headings, bullet points, and priority labels (P0/P1/P2) for each requirement. Write it to \`docs/requirements.md\`.

---

## Expected Output

Claude should create \`docs/requirements.md\` containing:

- A well-structured markdown document with all 7 sections listed above
- At least 8-10 functional requirements with priority labels
- Concrete technical details referencing Prisma models, Next.js route handlers, and NextAuth.js providers
- Measurable non-functional requirements with specific numeric targets
- A minimum of 5 open questions that genuinely require team discussion

## Tips

1. **Seed with domain context** — Before running this prompt, create a brief \`docs/project-brief.md\` describing your actual business domain. Claude will produce far more relevant requirements if it knows whether you're building a SaaS dashboard, marketplace, or internal tool.
2. **Iterate on open questions** — After the first pass, ask Claude to expand each open question into a decision record template (\`docs/decisions/ADR-001.md\`) so the team can resolve them asynchronously.
3. **Validate against the playbook** — Cross-reference the output with \`playbook/claude-overrides/01-discovery.md\` to ensure all phase deliverables and quality gates are covered before moving to Phase 02.
4. **Pin priorities early** — If Claude assigns everything as P0, reply with "Re-prioritize: only 3 items can be P0, redistribute the rest across P1 and P2" to force realistic scoping.`,

  'playbook/prompts/01/nfr-checklist.md': `# NFR Checklist — Phase 01: Discovery

## Project Context

| Field | Value |
|-------|-------|
| Project | nextjs-enterprise |
| Stack | Next.js 14 (App Router), TypeScript, PostgreSQL (Prisma), Redis, Socket.io |
| Cloud | AWS (ECS + Docker) |
| Monitoring | Prometheus + Grafana |
| Phase | 01 — Discovery |

## Prompt

> You are a senior solutions architect working on **nextjs-enterprise**, a Next.js 14 App Router application deployed on AWS ECS with PostgreSQL (Prisma ORM), Redis caching, and Socket.io WebSockets. The team is 5-15 senior-heavy engineers.
>
> Generate a comprehensive **Non-Functional Requirements (NFR) checklist** organized into the following categories:
>
> 1. **Performance** — Page load under 2 seconds (LCP), Time to Interactive under 3 seconds, API response p95 under 200ms, WebSocket message latency under 50ms, Prisma query p95 under 100ms, Redis cache hit ratio above 90%.
> 2. **Scalability** — Support 10,000 concurrent users at launch, horizontal scaling via ECS auto-scaling, PostgreSQL connection pooling (PgBouncer), Redis cluster mode, Socket.io sticky sessions with Redis adapter.
> 3. **Security** — OWASP Top 10 coverage, NextAuth.js session management with CSRF protection, rate limiting on API routes (100 req/min per user), input validation with Zod, SQL injection prevention via Prisma parameterized queries, Content-Security-Policy headers, dependency vulnerability scanning.
> 4. **Accessibility** — WCAG 2.1 AA compliance, keyboard navigation for all interactive elements, screen reader support, color contrast ratio 4.5:1 minimum, focus management for client-side route changes.
> 5. **Reliability** — 99.9% uptime SLA (8.76 hours max downtime/year), health check endpoints for ECS, graceful degradation when Redis is unavailable, database failover with RDS Multi-AZ, circuit breaker pattern for external service calls.
> 6. **Observability** — Prometheus metrics exported from Next.js and Node.js, Grafana dashboards for request latency / error rate / saturation, structured JSON logging, distributed tracing headers, alerting rules for p95 latency > 500ms and error rate > 1%.
>
> For each NFR, include: **ID**, **Category**, **Requirement**, **Target/Threshold**, **Measurement Method**, and **Priority** (P0 = launch blocker, P1 = launch target, P2 = post-launch).
>
> Output as a Markdown table grouped by category.

## Expected Output

- A single Markdown document containing 6 grouped tables (one per NFR category).
- Each table has columns: ID, Requirement, Target/Threshold, Measurement Method, Priority.
- IDs follow the pattern \`PERF-001\`, \`SCAL-001\`, \`SEC-001\`, \`A11Y-001\`, \`REL-001\`, \`OBS-001\`.
- At least 4-6 rows per category (25-35 NFRs total).
- A summary section listing all P0 items as launch blockers.

## Tips

- Feed this checklist into Phase 02 (Architecture) to ensure every NFR maps to an architectural decision.
- Store the output at \`docs/nfr-checklist.md\` and reference it from \`CLAUDE.md\`.
- Revisit thresholds in Phase 05 (Integration & Hardening) once real load test data is available.
- Use the Measurement Method column to drive Grafana dashboard creation in Phase 06.`,

  'playbook/prompts/01/user-stories.md': `# Phase 01 — Discovery: Generate User Stories

**Project:** nextjs-enterprise
**Phase:** 01 — Discovery
**Task:** Define user stories with acceptance criteria for all core features

---

## Prompt

Paste the following directly into Claude Code:

> You are a senior product engineer working on **nextjs-enterprise**, a Next.js 14 App Router application with PostgreSQL (Prisma), NextAuth.js authentication, Socket.io real-time updates, and Redis caching. The team is 5-15 senior-heavy engineers following a modular-monolith architecture.
>
> Generate a comprehensive set of user stories for the following feature areas:
>
> 1. **Authentication** — sign-up, sign-in, sign-out, password reset, session management via NextAuth.js
> 2. **Dashboard** — personalized overview, data widgets, role-based views
> 3. **API routes** — RESTful endpoints under \`/api\`, rate limiting, error handling
> 4. **Real-time updates** — Socket.io notifications, live data feeds, presence indicators
> 5. **Data management** — CRUD operations via Prisma ORM, pagination, filtering, Redis-cached queries
>
> For each user story use this format:
>
> **US-{area}-{number}**: As a **[role]**, I want to **[action]**, so that **[benefit]**.
>
> **Acceptance Criteria:**
> - [ ] Criterion 1
> - [ ] Criterion 2
> - [ ] Criterion 3
>
> **Priority:** Must-have | Should-have | Nice-to-have
>
> Produce at least 3 stories per feature area (15+ total). Prioritize using MoSCoW. Include at least one story for an **admin** role and one for an **unauthenticated visitor**. Reference concrete tech (NextAuth.js, Prisma, Socket.io, Redis) in acceptance criteria where relevant.

---

## Expected Output

- A Markdown document with 15–20 user stories grouped by feature area.
- Each story has a unique ID (e.g., \`US-AUTH-01\`), role, action, benefit, 2–4 acceptance criteria as checkboxes, and a MoSCoW priority label.
- Stories cover happy paths, edge cases (e.g., expired sessions, WebSocket reconnection), and admin-specific flows.
- Tech references are specific: "NextAuth.js session callback", "Prisma \`findMany\` with cursor pagination", "Socket.io \`room.join\`", "Redis TTL of 60s".

---

## Tips

- If the output is too generic, follow up with: *"Make acceptance criteria more specific — reference exact Next.js 14 App Router conventions, Prisma model names, and Socket.io event names."*
- Feed the output into Phase 02 (Architecture) to derive domain modules and API surface area.
- Store the final version at \`docs/discovery/user-stories.md\` and commit with \`docs: add user stories from discovery phase\`.`,

  'playbook/prompts/02/adr-generator.md': `# ADR Generator — Phase 02: Architecture

## Project Context
**Project:** nextjs-enterprise | **Stack:** Next.js 14 (App Router), TypeScript, Prisma, PostgreSQL, Redis, Socket.io | **Architecture:** Modular Monolith | **Cloud:** AWS ECS + Docker

## Prompt

Copy and paste the following into Claude Code, replacing \`<DECISION_TOPIC>\` with your specific decision:

---

You are documenting architectural decisions for the **nextjs-enterprise** project — a Next.js 14 App Router application using TypeScript (strict), Prisma ORM with PostgreSQL, Redis caching, Socket.io for WebSocket support, deployed to AWS ECS via Docker.

Generate an Architecture Decision Record (ADR) for the following topic:

**Topic:** \`<DECISION_TOPIC>\`

Write the ADR in Markdown and save it to \`docs/adrs/\` with the filename format \`NNN-kebab-case-title.md\` (check existing ADRs in that directory to determine the next sequence number, starting at 001 if none exist).

Use this exact structure:

1. **Title** — \`ADR-NNN: <Descriptive Title>\`
2. **Status** — One of: Proposed, Accepted, Deprecated, Superseded
3. **Date** — Today's date in YYYY-MM-DD format
4. **Context** — Why this decision is needed. Reference concrete project constraints: our modular-monolith architecture, pnpm workspace, Next.js 14 App Router, PostgreSQL + Prisma stack, AWS ECS deployment, team size of 5-15 senior-heavy engineers.
5. **Options Considered** — At least 3 alternatives with brief pros/cons for each.
6. **Decision** — The chosen option with clear rationale tied to project goals.
7. **Consequences** — Separate subsections for Positive, Negative, and Neutral consequences (at least 2 items each).
8. **References** — Links to relevant docs, RFCs, or related ADRs.

Be specific to this project — do not write generic advice. Reference real package names, real AWS services, and real file paths in this codebase.

---

## Example Topics

- Choosing Next.js App Router over Pages Router
- Prisma ORM vs Drizzle vs TypeORM for database access
- Modular monolith vs microservices architecture
- NextAuth.js vs Clerk vs custom auth for authentication
- Socket.io vs native WebSocket vs Pusher for real-time features
- Redis caching strategy (read-through vs write-behind)
- GitHub Flow vs Gitflow branching strategy

## Expected Output

A single Markdown file at \`docs/adrs/NNN-<topic>.md\` containing all 8 sections above, approximately 60-120 lines, with concrete references to project packages (\`@prisma/client\`, \`next-auth\`, \`socket.io\`), file paths (\`src/modules/\`, \`prisma/schema.prisma\`), and infrastructure details (AWS ECS, Docker, Prometheus metrics).

## Tips

- Run this prompt multiple times with different topics to build a complete ADR library during Phase 02.
- After generating, review the **Options Considered** section — Claude may surface alternatives you hadn't evaluated.
- Link related ADRs to each other in the **References** section to build a decision graph.
- Set status to **Proposed** initially; update to **Accepted** after team review in your PR process.`,

  'playbook/prompts/02/api-design.md': `# Phase 02 — Architecture: API Design

## Project Context

> **Project:** nextjs-enterprise | **Framework:** Next.js 14 App Router | **Language:** TypeScript (strict) | **Database:** PostgreSQL via Prisma ORM | **Auth:** NextAuth.js | **Caching:** Redis | **Real-time:** Socket.io | **Architecture:** Modular Monolith

## Prompt

Paste the following into Claude Code:

---

Design the REST API for the **nextjs-enterprise** application using Next.js 14 App Router route handlers (\`app/api/\`). Cover every resource our features require: users, sessions/auth, and any domain entities identified during discovery.

For each endpoint, specify:

1. **Route path** using Next.js App Router conventions (e.g., \`app/api/v1/users/[id]/route.ts\`) and the HTTP methods it handles (GET, POST, PUT, PATCH, DELETE).
2. **Zod request schemas** for body, query params, and path params — use strict TypeScript types inferred via \`z.infer<>\`.
3. **Zod response schemas** including success envelopes (\`{ data, meta }\`) and error envelopes (\`{ error: { code, message, details? } }\`).
4. **Pagination** strategy: cursor-based for list endpoints, with \`cursor\`, \`limit\`, and \`meta.nextCursor\` fields.
5. **Authentication**: which endpoints require a valid NextAuth.js session, which are public, and how the \`Authorization: Bearer <token>\` header or session cookie is validated.
6. **Versioning**: use URL-prefix versioning (\`/api/v1/\`) and document the strategy for introducing \`/api/v2/\` without breaking clients.
7. **Rate limiting** annotations per endpoint (e.g., 100 req/min for authenticated, 20 req/min for public).
8. **WebSocket events** (Socket.io): list any real-time events that complement REST endpoints and their payload schemas.

Output a single Markdown document with tables, Zod code blocks, and a versioning policy section. Use real package imports (\`zod\`, \`next-auth\`, \`@prisma/client\`). Do not use placeholder names — use concrete resource names relevant to this project.

---

## Expected Output

- A Markdown document (~200-400 lines) containing:
  - Resource table mapping routes → methods → auth requirement → rate limit
  - Zod schema code blocks for every request/response (importable \`.ts\` snippets)
  - Pagination contract with cursor-based example
  - Error code enum (\`APP_ERROR\`, \`VALIDATION_ERROR\`, \`NOT_FOUND\`, \`UNAUTHORIZED\`, \`RATE_LIMITED\`)
  - Versioning policy section (URL prefix, deprecation headers, sunset timeline)
  - Socket.io event catalog with payload Zod schemas

## Tips

- Ask Claude to place shared schemas (error envelope, pagination meta) in \`src/lib/api/schemas.ts\` so route handlers import them.
- If the output is too generic, follow up with: *"Now generate the actual \`route.ts\` files for the top 3 endpoints with full Zod validation middleware."*
- Cross-reference the Prisma models from Phase 01 discovery to ensure API resources map 1:1 to database entities.
- Save the output to \`docs/architecture/api-design.md\` and commit with \`docs(api): add REST API design document\`.`,

  'playbook/prompts/02/data-model-design.md': `# Data Model Design — Phase 02: Architecture

## Project Context

| Field | Value |
|-------|-------|
| Project | nextjs-enterprise |
| Phase | 02 — Architecture |
| Database | PostgreSQL via Prisma ORM |
| Auth | NextAuth.js (Account, Session, User, VerificationToken) |
| Features | WebSocket (Socket.io), Redis caching, soft deletes, audit fields |

## Prompt

Paste the following into Claude Code:

> You are the data architect for **nextjs-enterprise**, a Next.js 14 App Router application backed by PostgreSQL and Prisma ORM.
>
> Design a complete \`prisma/schema.prisma\` file that includes:
>
> 1. **NextAuth.js models** — \`User\`, \`Account\`, \`Session\`, and \`VerificationToken\` following the NextAuth Prisma adapter spec exactly.
> 2. **Domain entities** — \`Organization\`, \`Membership\` (join between User and Organization with role enum), \`Project\`, \`AuditLog\`, and \`Notification\`.
> 3. **Enums** — \`Role\` (OWNER, ADMIN, MEMBER, VIEWER), \`ProjectStatus\` (DRAFT, ACTIVE, ARCHIVED), \`NotificationType\` (SYSTEM, MENTION, INVITE).
> 4. **Soft deletes** — add a nullable \`deletedAt DateTime?\` field to User, Organization, and Project.
> 5. **Audit fields** — every domain entity must have \`createdAt DateTime @default(now())\`, \`updatedAt DateTime @updatedAt\`, and \`createdBy String?\`.
> 6. **Indexes** — add \`@@index\` for all foreign keys, composite indexes on \`Membership(userId, organizationId)\`, and a partial unique on non-deleted organization slugs.
> 7. **Constraints** — use \`@unique\` on Organization.slug, User.email; use \`@@map\` to snake_case all table names.
> 8. **Datasource & generator** — PostgreSQL provider, \`prisma-client-js\` generator with \`previewFeatures = ["fullTextSearch"]\`.
>
> Output the full \`schema.prisma\` file with inline comments explaining non-obvious decisions. After the schema, provide a short migration plan (3-5 bullet points) describing the order of migrations for a greenfield deploy.

## Expected Output

- A valid \`prisma/schema.prisma\` file (80-150 lines) ready to run \`pnpm prisma migrate dev\`.
- All NextAuth required fields and relations present.
- Proper \`@@map("table_name")\` annotations on every model.
- Composite and single-column indexes on foreign keys.
- A concise migration plan section (Markdown bullets) covering initial auth tables, domain tables, indexes, and seed strategy.

## Tips

- Verify the schema against the [NextAuth Prisma adapter docs](https://authjs.dev/getting-started/adapters/prisma) — missing fields will break sign-in.
- Ask Claude to run \`npx prisma validate\` after generating the file to catch syntax errors.
- If you plan to use Redis for session storage later, keep the \`Session\` model anyway — it serves as a fallback and audit trail.
- Soft-delete filtering should be handled at the Prisma client extension level (\`prisma/extensions/soft-delete.ts\`), not in the schema itself.
- Review the output against \`.claude/rules/02-architecture.md\` quality gates before moving to Phase 03.`,

  'playbook/prompts/03/ci-pipeline.md': `# Phase 03 — Foundation: CI Pipeline Setup

## Project Context

| Field | Value |
|-------|-------|
| Project | nextjs-enterprise |
| Framework | Next.js 14 (App Router) / TypeScript strict |
| Package Manager | pnpm |
| Test Framework | vitest |
| Database | PostgreSQL via Prisma ORM |
| CI/CD | GitHub Actions |
| Git Strategy | GitHub Flow, conventional commits, signed commits |

## Prompt

> Set up a production-grade GitHub Actions CI workflow at \`.github/workflows/ci.yml\` for the **nextjs-enterprise** project. The workflow must:
>
> 1. Trigger on \`push\` to \`main\` and on all pull requests.
> 2. Use a **PostgreSQL 16** service container (user: \`postgres\`, password: \`postgres\`, db: \`nextjs_enterprise_test\`) with a health check.
> 3. Run on \`ubuntu-latest\` with Node.js 20.
> 4. Cache pnpm dependencies using \`pnpm/action-setup@v4\` (pnpm 9) and the built-in \`actions/setup-node@v4\` caching.
> 5. Include these sequential stages as separate named steps: **Install → Lint → Typecheck → Test → Build**.
>    - **Lint**: \`pnpm lint\` (ESLint + Prettier check)
>    - **Typecheck**: \`pnpm tsc --noEmit\`
>    - **Test**: \`pnpm vitest run --coverage\` with \`DATABASE_URL\` pointing to the service container
>    - **Build**: \`pnpm build\`
> 6. Upload the \`.next\` build output and \`coverage/\` directory as GitHub Actions artifacts (retention: 7 days).
> 7. Set \`concurrency\` to cancel in-progress runs for the same branch.
> 8. Add a final step that runs \`npx prisma migrate deploy\` before the test step to ensure the test database schema is current.
>
> Use environment variable \`DATABASE_URL=postgresql://postgres:postgres@localhost:5432/nextjs_enterprise_test\` for Prisma and test steps. Keep the workflow in a single job for simplicity but ensure step ordering is correct.

## Expected Output

- **File**: \`.github/workflows/ci.yml\` — a single-job workflow (~80-100 lines of YAML) containing:
  - \`services.postgres\` block with image, env, ports, and health-check options.
  - pnpm install with frozen lockfile (\`--frozen-lockfile\`).
  - Clearly named steps: \`Lint\`, \`Type Check\`, \`Prisma Migrate\`, \`Test\`, \`Build\`.
  - \`actions/upload-artifact@v4\` for both \`.next/\` and \`coverage/\` outputs.
  - \`concurrency\` group set to \`\${{ github.workflow }}-\${{ github.ref }}\` with \`cancel-in-progress: true\`.

## Tips

- If the workflow fails on the Prisma migrate step, ensure \`prisma/schema.prisma\` and at least one migration exist — run the **database-schema** prompt from this phase first.
- You can verify the workflow locally with [act](https://github.com/nektos/act): \`act pull_request --secret GITHUB_TOKEN\`.
- After merging, confirm the Actions tab shows all five stages passing before moving to Phase 04.`,

  'playbook/prompts/03/iac-setup.md': `# IaC Setup — Docker & AWS ECS Task Definition

**Phase:** 03 — Foundation
**Complexity:** Medium
**Estimated time:** 30–45 minutes

---

## Prompt

> **Project context:** nextjs-enterprise — a Next.js 14 App Router TypeScript application using pnpm, PostgreSQL (Prisma ORM), Redis caching, and Socket.io WebSockets. Deployed to AWS ECS with Docker. Monitoring via Prometheus + Grafana.

Create the infrastructure-as-code foundation for the nextjs-enterprise project:

1. **Dockerfile** (\`Dockerfile\`) — multi-stage build with four stages: \`deps\` (install pnpm + dependencies), \`builder\` (run \`pnpm build\`), \`runner\` (production image based on \`node:20-alpine\`). Copy only \`standalone\` output and \`public\`/\`static\` assets. Run as non-root user \`nextjs\` (UID 1001). Expose port 3000. Add a \`HEALTHCHECK\` instruction that curls \`/api/health\` every 30s with a 5s timeout and 3 retries.

2. **docker-compose.yml** (\`docker-compose.yml\`) — services for \`app\` (builds from Dockerfile), \`postgres\` (image \`postgres:16-alpine\`, volume for data, health check via \`pg_isready\`), \`redis\` (image \`redis:7-alpine\`, health check via \`redis-cli ping\`). Use an \`.env\` file for \`DATABASE_URL\`, \`REDIS_URL\`, \`NEXTAUTH_SECRET\`, and \`NEXTAUTH_URL\`. The \`app\` service depends on \`postgres\` and \`redis\` with \`condition: service_healthy\`.

3. **ECS Task Definition** (\`infra/ecs/task-definition.json\`) — Fargate-compatible, 1 vCPU / 2048 MB memory. Container definition for \`nextjs-enterprise\` on port 3000 with a \`/api/health\` HTTP health check (interval 30s, timeout 5s, retries 3, start period 60s). Reference secrets from AWS Secrets Manager for \`DATABASE_URL\`, \`REDIS_URL\`, and \`NEXTAUTH_SECRET\`. Configure \`awslogs\` log driver pointing to log group \`/ecs/nextjs-enterprise\` in \`us-east-1\`. Set \`readonlyRootFilesystem: true\` and \`essential: true\`.

4. **Health endpoint** (\`src/app/api/health/route.ts\`) — GET handler that checks PostgreSQL connectivity via Prisma and Redis via ioredis, returning \`{ status: "healthy", services: { database: "up", redis: "up" } }\` with 200, or 503 with the failing service marked \`"down"\`.

Use environment variables — never hardcode secrets. Add inline comments explaining non-obvious choices.

---

## Expected Output

| File | Key Characteristics |
|---|---|
| \`Dockerfile\` | 4 stages, pnpm, standalone output, non-root user, HEALTHCHECK |
| \`docker-compose.yml\` | 3 services, health checks, \`.env\` reference, \`depends_on\` with conditions |
| \`infra/ecs/task-definition.json\` | Fargate, secrets from Secrets Manager, awslogs, readonly root FS |
| \`src/app/api/health/route.ts\` | Prisma + Redis connectivity checks, proper HTTP status codes |

---

## Tips

- If Claude generates a \`COPY --from=builder\` without the \`standalone\` directory, ask it to use \`COPY --from=builder /app/.next/standalone ./\` and \`COPY --from=builder /app/.next/static ./.next/static\`.
- Remind Claude to set \`output: "standalone"\` in \`next.config.mjs\` if it hasn't already — the Dockerfile depends on it.
- For the ECS task definition, verify that \`secrets.valueFrom\` uses full ARN format (\`arn:aws:secretsmanager:us-east-1:ACCOUNT_ID:secret:name\`).
- If the health route imports Prisma directly at the top level, ask Claude to use a singleton pattern from \`src/lib/prisma.ts\` to avoid instantiating multiple clients.`,

  'playbook/prompts/03/project-scaffold.md': `# Phase 03 — Foundation: Project Scaffold

**Phase:** 03-foundation  
**Playbook:** Greenfield  
**Project:** nextjs-enterprise  

---

## Prompt

> **Context:** You are scaffolding the \`nextjs-enterprise\` project — a Next.js 14 App Router application using TypeScript strict mode, pnpm, Prisma with PostgreSQL, and a modular-monolith architecture. Authentication will use NextAuth.js. Redis is used for caching and Socket.io for WebSocket support.
>
> **Task:** Scaffold the full project structure. Perform the following steps in order:
>
> 1. Run \`pnpm create next-app@latest nextjs-enterprise --typescript --eslint --tailwind --app --src-dir --import-alias "@/*"\` and cd into the project.
> 2. Install core dependencies: \`pnpm add next-auth @prisma/client socket.io socket.io-client ioredis zod\`
> 3. Install dev dependencies: \`pnpm add -D prisma vitest @vitejs/plugin-react @testing-library/react @testing-library/jest-dom prettier eslint-config-prettier eslint-plugin-prettier @types/node\`
> 4. Initialize Prisma: \`pnpm prisma init --datasource-provider postgresql\`
> 5. Create the following directory structure under \`src/\`:
>    - \`src/modules/auth/\` — NextAuth.js config, session helpers
>    - \`src/modules/users/\` — user service, repository, types
>    - \`src/modules/shared/\` — shared utilities, constants, types
>    - \`src/lib/\` — prisma client singleton, redis client, socket server setup
>    - \`src/app/api/auth/[...nextauth]/route.ts\` — NextAuth route handler stub
>    - \`src/app/(dashboard)/layout.tsx\` — authenticated layout shell
>    - \`src/app/(public)/layout.tsx\` — public layout shell
>    - \`src/config/\` — environment validation with zod
> 6. Configure \`tsconfig.json\` with \`"strict": true\` and path aliases \`@/*\` → \`src/*\`.
> 7. Create \`.prettierrc\` with \`semi: true, singleQuote: true, trailingComma: "all", printWidth: 100\`.
> 8. Update \`.eslintrc.json\` to extend \`next/core-web-vitals\` and \`prettier\`.
> 9. Add scripts to \`package.json\`: \`"db:generate": "prisma generate"\`, \`"db:migrate": "prisma migrate dev"\`, \`"db:push": "prisma db push"\`, \`"test": "vitest"\`, \`"test:ci": "vitest run --coverage"\`, \`"format": "prettier --write \\"src/**/*.{ts,tsx}\\""\`.
> 10. Create a \`vitest.config.ts\` that resolves the \`@/*\` alias and uses \`@vitejs/plugin-react\`.
>
> Commit with message \`feat: scaffold project foundation with Next.js 14, Prisma, and modular structure\`.

---

## Expected Output

- A fully initialized Next.js 14 project at \`nextjs-enterprise/\` with all dependencies installed.
- Prisma schema at \`prisma/schema.prisma\` with the PostgreSQL datasource configured via \`DATABASE_URL\`.
- Module directories (\`auth\`, \`users\`, \`shared\`) each containing an \`index.ts\` barrel export.
- Stub files for NextAuth route handler, prisma client singleton, redis client, and zod env config.
- Working \`pnpm dev\`, \`pnpm test\`, and \`pnpm format\` commands.
- A single conventional commit on the current branch.

---

## Tips

- If \`pnpm create next-app\` prompts interactively, pass \`--yes\` or use the flags above to skip prompts.
- The Prisma client singleton in \`src/lib/prisma.ts\` should use the global-this pattern to avoid multiple instances in development.
- Keep module directories thin at this stage — just barrel exports and placeholder types. Core implementation happens in Phase 04.
- Verify the scaffold compiles cleanly with \`pnpm build\` before committing.`,

  'playbook/prompts/04/auth-implementation.md': `# Phase 04 — Authentication Implementation with NextAuth.js

## Project Context

> **Project:** nextjs-enterprise | **Framework:** Next.js 14 App Router | **Language:** TypeScript (strict)
> **Database:** PostgreSQL via Prisma ORM | **Cache:** Redis | **Package Manager:** pnpm
> **Phase:** 04 — Core Implementation | **Architecture:** Modular Monolith

## Prompt

\`\`\`
Implement a complete authentication system for our Next.js 14 App Router project using NextAuth.js v5 (next-auth@beta). Requirements:

1. **Providers**: Configure Google OAuth and Credentials (email/password with bcrypt hashing) providers in src/lib/auth/config.ts.
2. **Prisma Adapter**: Use @auth/prisma-adapter. Extend the existing Prisma schema in prisma/schema.prisma with User, Account, Session, and VerificationToken models. Add a \`role\` enum (ADMIN, MANAGER, MEMBER) defaulting to MEMBER on the User model.
3. **Session Strategy**: Use JWT strategy. Extend the session and JWT types in src/types/next-auth.d.ts to include \`id\` and \`role\`.
4. **Callbacks**: In the auth config, implement \`jwt\` and \`session\` callbacks to persist user id and role into the token and session.
5. **Middleware**: Create src/middleware.ts using next-auth/middleware to protect all routes under /dashboard and /api (except /api/auth). Redirect unauthenticated users to /auth/sign-in.
6. **RBAC Utility**: Create src/lib/auth/rbac.ts exporting a \`withRole(requiredRole: Role)\` higher-order function for API route handlers that returns 403 if the user lacks the required role.
7. **Auth API Route**: Set up the catch-all handler at src/app/api/auth/[...nextauth]/route.ts.
8. **Server Helper**: Export \`auth()\` from src/lib/auth/index.ts for use in Server Components and Route Handlers.
9. **Redis Session Cache**: In src/lib/auth/session-cache.ts, implement a thin cache layer using ioredis that caches decoded session data for 5 minutes to reduce JWT decode overhead on hot paths.

Use strict TypeScript throughout. Follow existing project conventions in src/modules/. Write JSDoc comments on all exported functions.
\`\`\`

## Expected Output

- \`prisma/schema.prisma\` — Updated with auth models and Role enum
- \`src/lib/auth/config.ts\` — NextAuth configuration with providers and callbacks
- \`src/lib/auth/index.ts\` — Re-exports \`auth\`, \`signIn\`, \`signOut\` helpers
- \`src/lib/auth/rbac.ts\` — Role-based access control utility
- \`src/lib/auth/session-cache.ts\` — Redis-backed session cache
- \`src/types/next-auth.d.ts\` — Module augmentation for Session and JWT
- \`src/middleware.ts\` — Route protection middleware
- \`src/app/api/auth/[...nextauth]/route.ts\` — Auth API catch-all route
- Prisma migration command suggestion (\`pnpm prisma migrate dev --name add-auth-models\`)

## Tips

- If NextAuth v5 APIs drift from what Claude generates, paste the latest \`next-auth@beta\` docs into context before running.
- Run \`pnpm prisma generate\` immediately after schema changes so TypeScript picks up the new types.
- Verify the Redis cache TTL aligns with your JWT \`maxAge\` in the auth config — the cache TTL should always be shorter.
- Test the RBAC utility with a quick vitest unit test: mock the session, call \`withRole("ADMIN")\`, and assert 403 for a MEMBER user.
- After generation, run \`pnpm tsc --noEmit\` to catch any type mismatches between the Prisma client and next-auth augmentations.`,

  'playbook/prompts/04/crud-generator.md': `# CRUD Module Generator — Phase 04: Core Implementation

## Context

**Project:** nextjs-enterprise | **Phase:** 04 — Core Implementation | **Architecture:** Modular Monolith  
**Stack:** Next.js 14 App Router, Prisma ORM, PostgreSQL, Zod, Vitest

## Prompt

Copy and paste the following into Claude Code, replacing \`<EntityName>\` with your actual entity (e.g., \`Project\`, \`Organization\`, \`Invoice\`):

---

> Generate a complete CRUD module for the **<EntityName>** entity in our Next.js 14 modular monolith. Follow these exact conventions:
>
> 1. **Prisma model** in \`prisma/schema.prisma\` — add the model with \`id\` (cuid), \`createdAt\`, \`updatedAt\`, and 3-5 domain-relevant fields. Add appropriate indexes and relations.
>
> 2. **Zod schemas** in \`src/modules/<entity-name>/schema.ts\` — create \`create<EntityName>Schema\`, \`update<EntityName>Schema\`, and \`<entityName>QuerySchema\` (with pagination: \`page\`, \`limit\`, \`search\`).
>
> 3. **Service layer** in \`src/modules/<entity-name>/service.ts\` — implement \`create\`, \`findById\`, \`findMany\` (with pagination + filtering), \`update\`, \`delete\`. Use Prisma client from \`src/lib/prisma.ts\`. Add Redis cache invalidation using the helper from \`src/lib/redis.ts\` on write operations.
>
> 4. **API route handlers** in \`src/app/api/<entity-name>/route.ts\` (collection: GET list + POST create) and \`src/app/api/<entity-name>/[id]/route.ts\` (resource: GET one + PUT update + DELETE). Wrap handlers with NextAuth session check. Return proper HTTP status codes (200, 201, 400, 401, 404, 422).
>
> 5. **Tests** in \`src/modules/<entity-name>/__tests__/service.test.ts\` — write Vitest tests covering: successful CRUD operations, validation failures, not-found cases, and unauthorized access. Mock Prisma with \`vitest-mock-extended\`.
>
> Use existing patterns from \`src/modules/\` if any modules already exist. Follow conventional commits — suggest the commit message at the end. Use strict TypeScript, no \`any\` types.

---

## Expected Output

- \`prisma/schema.prisma\` — updated with new model, indexes, and relations
- \`src/modules/<entity-name>/schema.ts\` — Zod validation schemas with inferred TypeScript types
- \`src/modules/<entity-name>/service.ts\` — service functions with Prisma queries and Redis cache invalidation
- \`src/app/api/<entity-name>/route.ts\` — collection endpoint (GET + POST)
- \`src/app/api/<entity-name>/[id]/route.ts\` — resource endpoint (GET + PUT + DELETE)
- \`src/modules/<entity-name>/__tests__/service.test.ts\` — 8-12 test cases
- Suggested Prisma migration command: \`pnpm prisma migrate dev --name add-<entity-name>\`
- Conventional commit message: \`feat(<entity-name>): add CRUD module with API routes and tests\`

## Tips

- **Run the generator multiple times** for different entities — each module stays isolated under \`src/modules/\`.
- After generation, run \`pnpm prisma migrate dev\` to apply the schema change, then \`pnpm vitest run src/modules/<entity-name>\` to verify tests pass.
- If the entity has relations to existing models, mention them explicitly in the prompt (e.g., "belongs to User, has many Comments") for accurate Prisma relations.
- Ask Claude to add the new API routes to your Prometheus metrics middleware so request counts are tracked from day one.
- For entities requiring soft-delete, append to the prompt: "Use a \`deletedAt\` nullable DateTime field instead of hard deletes, and filter soft-deleted records in all read queries."`,

  'playbook/prompts/04/feature-implementation.md': `# Phase 04 — Feature Implementation (End-to-End Module)

> **Project:** nextjs-enterprise · **Phase:** 04 Core Implementation · **Stack:** Next.js 14 App Router, Prisma, TypeScript strict, Vitest

## Prompt

Paste this directly into Claude Code:

\`\`\`
Project: nextjs-enterprise (Next.js 14 App Router, TypeScript strict, Prisma ORM, PostgreSQL, modular-monolith)

Implement the "Notifications" feature module end-to-end following our modular-monolith boundaries in src/modules/notifications/. Complete each layer in order:

1. **Prisma Model** — Add a \`Notification\` model to prisma/schema.prisma with fields: id (cuid), userId (relation to User), title, body, type (enum: INFO, WARNING, ERROR, SUCCESS), read (boolean default false), createdAt, updatedAt. Generate and apply the migration with \`pnpm prisma migrate dev --name add-notifications\`.

2. **Repository** — Create src/modules/notifications/repository.ts exporting functions: createNotification, getNotificationsByUserId (paginated with cursor), markAsRead, markAllAsRead, getUnreadCount. Use Prisma client from src/lib/prisma.ts.

3. **Service Layer** — Create src/modules/notifications/service.ts that wraps the repository with business logic, input validation using zod schemas, and error handling. Invalidate Redis cache (key pattern \`notifications:userId:*\`) on writes using the Redis client from src/lib/redis.ts.

4. **API Routes** — Create Next.js App Router route handlers:
   - src/app/api/notifications/route.ts (GET paginated list, POST create)
   - src/app/api/notifications/[id]/read/route.ts (PATCH mark as read)
   - src/app/api/notifications/read-all/route.ts (PATCH mark all as read)
   Protect all routes with NextAuth.js session check. Return proper HTTP status codes.

5. **React Components** — Create src/modules/notifications/components/:
   - NotificationBell.tsx — shows unread count badge, opens dropdown
   - NotificationList.tsx — renders paginated notifications with infinite scroll
   - NotificationItem.tsx — single notification with read/unread styling
   Use server components where possible; client components only for interactivity.

6. **Tests** — Create src/modules/notifications/__tests__/ with Vitest:
   - service.test.ts — unit tests for service layer (mock repository + Redis)
   - api.test.ts — integration tests for each API route
   - components.test.tsx — component tests using @testing-library/react

Follow conventional commits: one commit per layer (e.g., "feat(notifications): add Prisma model and migration"). Use strict TypeScript — no \`any\` types. Export a barrel file at src/modules/notifications/index.ts.
\`\`\`

## Expected Output

- \`prisma/schema.prisma\` updated with \`Notification\` model and \`NotificationType\` enum
- Migration file in \`prisma/migrations/\` applied to local PostgreSQL
- 6+ files in \`src/modules/notifications/\` (repository, service, components, tests, barrel)
- 3 API route files under \`src/app/api/notifications/\`
- Zod validation schemas co-located in \`src/modules/notifications/schemas.ts\`
- All tests passing via \`pnpm vitest run src/modules/notifications\`
- 6 conventional commits in git log, one per layer

## Tips

- If Claude asks about the existing User model, point it to \`prisma/schema.prisma\` — the model was created in Phase 03 Foundation.
- Run \`pnpm prisma format\` after schema changes to catch syntax issues before migration.
- If Redis is not running locally, tell Claude to wrap cache calls in try/catch with graceful fallback so tests don't fail on missing Redis.
- For component tests, ensure \`vitest.config.ts\` has the \`@vitejs/plugin-react\` plugin and \`jsdom\` environment configured (set up in Phase 03).
- If a layer fails, ask Claude to fix that layer before proceeding — don't let errors cascade across module boundaries.`,

  'playbook/prompts/05/integration-adapter.md': `# Phase 05 — Integration Adapter Prompt

## Project Context

> **Project:** nextjs-enterprise | **Stack:** Next.js 14 App Router, TypeScript strict, Prisma/PostgreSQL, Redis, Socket.io  
> **Architecture:** Modular monolith | **Phase:** 05 – Integration & Hardening  
> **Relevant files:** \`src/lib/adapters/\`, \`src/lib/shared/retry.ts\`, \`prisma/schema.prisma\`, \`vitest.config.ts\`

## Prompt

\`\`\`
Create a typed integration adapter layer in src/lib/adapters/ for external services used by nextjs-enterprise. Build the following:

1. A generic AdapterPort<TRequest, TResponse> interface in src/lib/adapters/types.ts that all adapters implement, including methods: execute(req: TRequest): Promise<TResponse>, healthCheck(): Promise<boolean>, and a readonly name property.

2. A retry utility in src/lib/shared/retry.ts with exponential backoff (base 200ms, max 3 retries, jitter). It should accept a generic async function and return the result or throw after exhausting retries. Log each retry attempt using our existing logger at src/lib/shared/logger.ts.

3. A circuit breaker wrapper in src/lib/shared/circuit-breaker.ts with three states (CLOSED, OPEN, HALF_OPEN). Configure: failureThreshold=5, resetTimeout=30s. Track failures in Redis using our existing Redis client at src/lib/redis.ts. Emit state change events compatible with our Prometheus metrics endpoint at src/app/api/metrics/route.ts.

4. Two concrete adapters implementing AdapterPort:
   - EmailAdapter (src/lib/adapters/email.ts) wrapping a Resend-like HTTP API with retry + circuit breaker.
   - PaymentAdapter (src/lib/adapters/payment.ts) wrapping a Stripe-like API with retry + circuit breaker and idempotency key support stored in PostgreSQL via Prisma.

5. Mock implementations of both adapters in src/lib/adapters/__mocks__/ that return deterministic responses for vitest. Register mocks via a createMockAdapter<T> factory.

6. An adapter registry in src/lib/adapters/registry.ts that lazily initializes adapters from environment config and exposes getAdapter<T>(name: string): T with proper type narrowing.

Use TypeScript strict mode throughout. All adapters must handle timeout (10s default), parse errors into a unified AdapterError type, and include correlation IDs from AsyncLocalStorage for distributed tracing.
\`\`\`

## Expected Output

- \`src/lib/adapters/types.ts\` — \`AdapterPort\`, \`AdapterError\`, \`AdapterConfig\` interfaces
- \`src/lib/shared/retry.ts\` — Generic retry with exponential backoff and jitter
- \`src/lib/shared/circuit-breaker.ts\` — Redis-backed circuit breaker with Prometheus counters
- \`src/lib/adapters/email.ts\` — \`EmailAdapter\` implementing \`AdapterPort<SendEmailRequest, SendEmailResponse>\`
- \`src/lib/adapters/payment.ts\` — \`PaymentAdapter\` with idempotency keys persisted via Prisma
- \`src/lib/adapters/__mocks__/email.mock.ts\` and \`payment.mock.ts\` — Deterministic test doubles
- \`src/lib/adapters/registry.ts\` — Lazy-init adapter registry with type-safe lookup
- Correlation ID threading via \`AsyncLocalStorage\` in all adapter calls

## Tips

- If Claude generates a single monolithic file, ask it to split into the directory structure above.
- Verify the circuit breaker Redis key schema doesn't collide with existing cache keys — prefix with \`cb:\`.
- Ensure \`AdapterError\` extends \`Error\` and includes \`statusCode\`, \`retryable\`, and \`correlationId\` fields.
- Run \`pnpm vitest run src/lib/adapters/\` after generation to validate mocks wire up correctly.
- Check that the Prisma idempotency model is added to \`prisma/schema.prisma\` — Claude sometimes forgets migrations.`,

  'playbook/prompts/05/observability-setup.md': `# Phase 05 — Observability Setup

## Context

**Project:** nextjs-enterprise · Next.js 14 App Router · TypeScript strict · PostgreSQL via Prisma · Redis · pnpm
**Phase:** 05 – Integration & Hardening
**Monitoring stack:** Prometheus + Grafana · Deployed on AWS ECS

---

## Prompt

Paste the following into Claude Code:

> **Project context:** nextjs-enterprise is a Next.js 14 App Router application using TypeScript strict mode, Prisma ORM with PostgreSQL, Redis caching via ioredis, and Socket.io for WebSockets. The app is containerized and deployed to AWS ECS. We use pnpm as the package manager.
>
> Set up production-grade observability for this project:
>
> 1. **Structured logging with pino:** Create \`src/lib/logger.ts\` that exports a pino logger configured with JSON output, log level from \`process.env.LOG_LEVEL\` (default \`info\`), and redaction of sensitive fields (\`password\`, \`token\`, \`authorization\`). Add a \`withRequestId\` child logger factory that attaches a correlation ID.
>
> 2. **Prometheus metrics endpoint:** Install \`prom-client\` and create \`src/lib/metrics.ts\` that registers a default registry with custom metrics: \`http_requests_total\` (Counter, labels: method, route, status), \`http_request_duration_seconds\` (Histogram, labels: method, route), \`db_pool_active_connections\` (Gauge), and \`redis_operations_total\` (Counter, labels: operation, status). Create a Next.js API route at \`src/app/api/metrics/route.ts\` that returns Prometheus text format.
>
> 3. **Health check endpoint:** Create \`src/app/api/health/route.ts\` that checks PostgreSQL connectivity via Prisma \`$queryRaw\`, Redis ping, and returns \`{ status: "healthy" | "degraded" | "unhealthy", checks: {...}, uptime, version }\` with appropriate HTTP status codes (200/503).
>
> 4. **Middleware instrumentation:** Create \`src/middleware/metrics.ts\` as a Next.js middleware wrapper that records request duration and increments the request counter for every incoming request.
>
> 5. **Grafana dashboard:** Create \`infra/grafana/dashboards/nextjs-overview.json\` with panels for: request rate (req/s), p50/p95/p99 latency, error rate (5xx percentage), and database pool utilization. Use Prometheus as the datasource.
>
> 6. **Prometheus scrape config:** Create \`infra/prometheus/prometheus.yml\` targeting the \`/api/metrics\` endpoint on port 3000 with a 15s scrape interval.
>
> Use existing patterns from \`src/lib/prisma.ts\` and \`src/lib/redis.ts\`. Add all new dependencies to \`package.json\` and run \`pnpm install\`. Write unit tests in \`src/__tests__/lib/metrics.test.ts\` and \`src/__tests__/api/health.test.ts\` using vitest.

---

## Expected Output

- \`src/lib/logger.ts\` — pino logger with redaction and child logger factory
- \`src/lib/metrics.ts\` — prom-client registry with four custom metrics
- \`src/app/api/metrics/route.ts\` — GET handler returning Prometheus exposition format
- \`src/app/api/health/route.ts\` — GET handler with dependency checks and structured response
- \`src/middleware/metrics.ts\` — request instrumentation wrapper
- \`infra/grafana/dashboards/nextjs-overview.json\` — four-panel Grafana dashboard JSON
- \`infra/prometheus/prometheus.yml\` — scrape configuration
- \`src/__tests__/lib/metrics.test.ts\` — vitest tests for metric registration and increment
- \`src/__tests__/api/health.test.ts\` — vitest tests for healthy/degraded/unhealthy states
- Updated \`package.json\` with \`pino\`, \`pino-pretty\`, \`prom-client\` dependencies

---

## Tips

- If Claude creates a single monolithic file, ask it to split into the individual modules listed above.
- Verify the Grafana JSON is valid by asking Claude to validate it or by importing into a local Grafana instance.
- The health endpoint should use \`Promise.allSettled\` so a single failing dependency doesn't prevent reporting on the others.
- Ensure the metrics route sets \`Content-Type: text/plain; version=0.0.4; charset=utf-8\` for Prometheus compatibility.
- For ECS deployments, confirm the Prometheus scrape config uses the ECS service discovery or static target matching your task definition port mapping.`,

  'playbook/prompts/05/security-hardening.md': `# Phase 05 — Security Hardening

## Context

**Project:** nextjs-enterprise | **Phase:** 05 – Integration & Hardening | **Stack:** Next.js 14 App Router, TypeScript strict, Prisma ORM (PostgreSQL), NextAuth.js, Socket.io, Redis

## Prompt

Paste this directly into Claude Code:

\`\`\`
You are hardening the nextjs-enterprise application for production. Audit and implement the following security measures across the codebase:

1. **CSP Headers** — In \`src/middleware.ts\`, configure a strict Content-Security-Policy with nonce-based script-src, restrict frame-ancestors to 'none', and set X-Content-Type-Options, X-Frame-Options, Referrer-Policy, and Permissions-Policy headers. Use \`next/headers\` where appropriate.

2. **Rate Limiting** — Create \`src/lib/rate-limit.ts\` using Redis (ioredis) with a sliding window algorithm. Apply rate limiting middleware to all \`/api/**\` routes at 100 req/min per IP and to \`/api/auth/**\` at 10 req/min per IP. Return 429 with Retry-After header on violation.

3. **Input Sanitization** — Add Zod validation schemas in \`src/lib/validation/\` for every API route handler. Ensure no raw \`req.body\` or \`req.query\` is used without parsing through Zod. Sanitize HTML inputs with \`isomorphic-dompurify\` where user-generated content is stored.

4. **CORS Configuration** — In \`next.config.mjs\`, configure CORS headers for API routes. For Socket.io in \`src/lib/socket.ts\`, restrict origins to \`NEXT_PUBLIC_APP_URL\` and reject all others.

5. **Dependency Audit** — Run \`pnpm audit --audit-level=moderate\` and fix or document every finding. Add a \`pnpm audit\` step to the existing GitHub Actions CI workflow in \`.github/workflows/ci.yml\`.

6. **Secrets Management** — Verify no secrets are hardcoded. Create \`src/lib/env.ts\` using \`@t3-oss/env-nextjs\` with Zod schemas for all environment variables (DATABASE_URL, NEXTAUTH_SECRET, REDIS_URL, etc.). Fail fast at build time if any are missing.

7. **SQL Injection Prevention** — Audit every Prisma call in \`src/modules/\` to confirm no raw SQL (\`$queryRawUnsafe\`, \`$executeRawUnsafe\`) is used. Where \`$queryRaw\` is necessary, ensure parameterized tagged templates are used exclusively. Add a comment explaining why raw SQL is needed in each case.

Write production-ready code. Use TypeScript strict mode. Add JSDoc comments on exported functions. Write vitest tests for rate-limit logic and input validation schemas in \`src/__tests__/security/\`.
\`\`\`

## Expected Output

- \`src/middleware.ts\` — Updated with CSP nonce generation and security headers
- \`src/lib/rate-limit.ts\` — Redis sliding-window rate limiter with configurable tiers
- \`src/lib/validation/\` — Zod schemas for each API route (e.g., \`auth.schema.ts\`, \`user.schema.ts\`)
- \`src/lib/env.ts\` — Type-safe env config using \`@t3-oss/env-nextjs\`
- \`src/lib/socket.ts\` — Updated CORS origin whitelist
- \`next.config.mjs\` — Security headers added to \`headers()\` config
- \`.github/workflows/ci.yml\` — \`pnpm audit\` job added
- \`src/__tests__/security/rate-limit.test.ts\` — Tests for sliding window, IP extraction, 429 responses
- \`src/__tests__/security/validation.test.ts\` — Tests for schema rejection of malformed input
- Audit report summary as a comment or markdown note

## Tips

- If Claude skips the dependency audit step, ask: "Run pnpm audit and show me the results. Add the audit step to CI."
- For CSP nonces, ensure the nonce is generated per-request in middleware and forwarded via \`x-nonce\` header for use in \`<Script>\` tags.
- Prisma's tagged template \`$queryRaw\` with \`Prisma.sql\` is safe; only \`$queryRawUnsafe\` is vulnerable — make sure Claude knows the distinction.
- If the env validation file causes build failures, check that \`.env.example\` is updated with every required variable.
- Ask Claude to run \`grep -r "queryRawUnsafe" src/\` to verify no unsafe raw queries remain after the audit.`,

  'playbook/prompts/06/e2e-test-generator.md': `# E2E Test Generator — Phase 06: Testing & Quality

## Context

Phase 06 of the **nextjs-enterprise** greenfield playbook. Generate Playwright E2E tests covering critical user flows using the Page Object Model pattern. The app runs Next.js 14 (App Router) with NextAuth.js authentication, Prisma ORM against PostgreSQL, and Socket.io for real-time features.

## Prompt

Paste the following into Claude Code:

---

You are working on the **nextjs-enterprise** project — a Next.js 14 App Router application with NextAuth.js, Prisma (PostgreSQL), Redis caching, and Socket.io WebSockets.

Generate a complete Playwright E2E test suite under \`e2e/\` with the Page Object Model pattern. Create these files:

1. **\`e2e/page-objects/base.page.ts\`** — Base page class with common helpers: \`goto()\`, \`waitForNavigation()\`, \`getToast()\`, \`screenshot()\`.
2. **\`e2e/page-objects/login.page.ts\`** — Encapsulates \`/auth/signin\`: fill email, fill password, click submit, assert redirect to \`/dashboard\`.
3. **\`e2e/page-objects/dashboard.page.ts\`** — Encapsulates \`/dashboard\`: sidebar navigation, stat cards, user menu dropdown, logout action.
4. **\`e2e/page-objects/resource-crud.page.ts\`** — Encapsulates a generic CRUD resource page (e.g., \`/dashboard/projects\`): create form, edit inline, delete with confirmation modal, table pagination.
5. **\`e2e/tests/auth.spec.ts\`** — Tests: successful login, failed login (wrong password), session persistence after reload, logout clears session.
6. **\`e2e/tests/dashboard.spec.ts\`** — Tests: sidebar links navigate correctly, stat cards render data, responsive layout at mobile viewport.
7. **\`e2e/tests/crud-operations.spec.ts\`** — Tests: create a new project, edit project name, delete project with confirmation, validate empty state after deletion.
8. **\`e2e/tests/forms.spec.ts\`** — Tests: required field validation, max-length enforcement, successful submission shows toast, form reset after submit.
9. **\`e2e/fixtures/auth.fixture.ts\`** — Custom Playwright fixture that authenticates via the NextAuth credentials provider and stores session state in \`e2e/.auth/user.json\`.
10. **\`e2e/playwright.config.ts\`** — Config targeting \`http://localhost:3000\`, projects for chromium/firefox/mobile-chrome, \`use: { storageState }\` for authenticated tests, \`webServer\` block running \`pnpm dev\`.

Use \`@playwright/test\` imports. Use \`test.describe\` blocks. Add \`test.beforeEach\` hooks where appropriate. Use \`expect\` soft assertions for non-critical checks. Tag tests with \`@critical\`, \`@smoke\`, or \`@regression\` annotations. All selectors should use \`data-testid\` attributes.

---

## Expected Output

- 10 files under \`e2e/\` totaling ~400-500 lines of TypeScript
- Page objects extend \`BasePage\` and expose typed methods (no raw selectors in test files)
- Auth fixture uses \`storageState\` pattern so login runs once per worker
- Config includes \`webServer\` block with \`pnpm dev\` and \`reuseExistingServer: true\`
- Tests use descriptive names: \`test('should redirect to dashboard after valid login', ...)\`
- CRUD tests assert URL changes, toast messages, and table row counts
- Form tests validate both client-side and server-returned errors

## Tips

- Run \`pnpm exec playwright install --with-deps chromium\` before first execution.
- Add \`data-testid\` attributes to your React components during Phase 05 to avoid brittle selectors.
- Use \`test.slow()\` for WebSocket-dependent tests that need extra timeout.
- Store the auth fixture state file in \`.gitignore\` — never commit \`e2e/.auth/\`.
- Pair this prompt with the **integration-test-generator** prompt to cover API-level flows that complement these UI tests.
- Reference the project's \`CLAUDE.md\` and \`.claude/rules/06-testing-quality.md\` for phase-specific quality gates.`,

  'playbook/prompts/06/load-test.md': `# Phase 06 — Load Testing with k6

## Context

**Project:** nextjs-enterprise | **Phase:** 06 - Testing & Quality | **Task:** Load test setup for critical API endpoints

## Prompt

\`\`\`
You are working on the nextjs-enterprise project — a Next.js 14 App Router application deployed on AWS ECS with PostgreSQL (Prisma), Redis caching, WebSocket (Socket.io), and NextAuth.js authentication.

Set up load testing using k6 with the following requirements:

1. Create \`tests/load/\` directory with these test files:
   - \`config.ts\` — shared thresholds, base URL from environment, and helper functions for auth token generation
   - \`scenarios/auth-flow.ts\` — tests POST /api/auth/signin and GET /api/auth/session
   - \`scenarios/api-crud.ts\` — tests CRUD operations on the primary API routes (GET/POST/PUT/DELETE)
   - \`scenarios/websocket.ts\` — tests Socket.io connection establishment and message throughput
   - \`scenarios/health.ts\` — tests GET /api/health and GET /api/health/ready

2. Define three load profiles in each scenario file:
   - **Normal load:** 50 VUs ramping over 2 minutes, sustained for 5 minutes
   - **Peak load:** 200 VUs ramping over 1 minute, sustained for 3 minutes
   - **Stress test:** ramp from 50 to 500 VUs over 5 minutes, then hold for 2 minutes

3. Set these thresholds globally:
   - p95 response time < 500ms for API routes
   - p99 response time < 1500ms for API routes
   - Error rate < 1% under normal load
   - Error rate < 5% under peak load
   - WebSocket connection success rate > 98%

4. Add a \`scripts/run-load-tests.sh\` script that:
   - Accepts a PROFILE argument (normal, peak, stress)
   - Runs all scenario files with the selected profile
   - Outputs results in JSON to \`tests/load/results/\`
   - Exits non-zero if any threshold is breached

5. Add a pnpm script \`"test:load"\` and \`"test:load:ci"\` to package.json
6. Create a GitHub Actions workflow \`.github/workflows/load-test.yml\` that runs normal-profile load tests against a staging environment on demand (workflow_dispatch)
7. Export Prometheus-compatible metrics using k6's built-in \`--out experimental-prometheus-rw\` flag pointing to our Grafana stack

Use TypeScript for k6 scripts via @grafana/k6-typescript. Reference the existing Prisma schema for realistic test data seeding.
\`\`\`

## Expected Output

- \`tests/load/config.ts\` with shared options, thresholds, and auth helpers
- Four scenario files under \`tests/load/scenarios/\` with parameterized load profiles
- \`scripts/run-load-tests.sh\` orchestrating test execution with profile selection
- Updated \`package.json\` with \`test:load\` and \`test:load:ci\` scripts
- \`.github/workflows/load-test.yml\` with workflow_dispatch trigger and staging target
- Prometheus metric export configuration compatible with existing Grafana dashboards

## Tips

- Ensure \`K6_BASE_URL\` and \`K6_AUTH_TOKEN\` are read from environment variables so the same scripts work locally and in CI
- For WebSocket scenarios, use the \`k6/ws\` module — Socket.io requires connecting to \`/socket.io/?EIO=4&transport=websocket\`
- Run \`pnpm add -D @grafana/k6-typescript\` before asking Claude to generate the files
- If Redis caching is working correctly, repeated GET requests should show significantly lower p95 than cold requests — consider adding a cache-warm scenario
- Use \`handleSummary()\` in k6 to produce both human-readable and JSON output for CI parsing`,

  'playbook/prompts/06/test-suite-generator.md': `# Test Suite Generator — Phase 06: Testing & Quality

## Context

**Project:** nextjs-enterprise · TypeScript (strict) · Next.js 14 App Router · Vitest · Prisma + PostgreSQL · NextAuth.js · Socket.io · Redis  
**Phase:** 06 — Testing & Quality  
**Goal:** Generate comprehensive unit, integration, and component tests for an existing module targeting ≥80% code coverage.

---

## Prompt

Paste this directly into Claude Code:

> You are working on the **nextjs-enterprise** project — a modular-monolith Next.js 14 App Router application using TypeScript strict mode, Prisma ORM with PostgreSQL, NextAuth.js authentication, Socket.io WebSocket support, and Redis caching.
>
> Analyze the module at \`src/modules/{MODULE_NAME}/\` and generate a complete test suite. Specifically:
>
> 1. **Unit tests** (\`src/modules/{MODULE_NAME}/__tests__/unit/\`) using **vitest** — test every exported function, service method, and utility in isolation. Mock Prisma client with \`vitest-mock-extended\`, mock Redis with \`ioredis-mock\`, and mock NextAuth session where needed. Each test file should mirror the source file name with a \`.test.ts\` suffix.
>
> 2. **Integration tests** (\`src/modules/{MODULE_NAME}/__tests__/integration/\`) — test service-to-database flows against a real test PostgreSQL database using \`@testcontainers/postgresql\`. Use Prisma migrations to set up schema before each suite. Test API route handlers by calling them with \`next/test-utils\` or direct function invocation with mocked \`NextRequest\`/\`NextResponse\`.
>
> 3. **Component tests** (\`src/modules/{MODULE_NAME}/__tests__/components/\`) using \`@testing-library/react\` and \`vitest\` — test every React component exported by the module. Verify rendering, user interactions via \`@testing-library/user-event\`, loading/error states, and accessibility with \`jest-dom\` matchers.
>
> Requirements:
> - Use \`describe\`/\`it\` blocks with clear, behavior-driven names (e.g., \`it("should return 401 when session is missing")\`).
> - Add \`beforeEach\`/\`afterEach\` hooks for setup and teardown; never leak state between tests.
> - Include edge cases: empty inputs, invalid IDs, expired sessions, Redis cache misses, concurrent writes.
> - Add a coverage threshold check: create or update \`vitest.config.ts\` to enforce \`{ statements: 80, branches: 80, functions: 80, lines: 80 }\` for this module.
> - Ensure all tests pass with \`pnpm test -- --run --reporter=verbose\`.

---

## Expected Output

- \`src/modules/{MODULE_NAME}/__tests__/unit/*.test.ts\` — one file per source file with mocked dependencies
- \`src/modules/{MODULE_NAME}/__tests__/integration/*.integration.test.ts\` — database round-trip and API handler tests
- \`src/modules/{MODULE_NAME}/__tests__/components/*.test.tsx\` — rendering and interaction tests for each component
- Updated \`vitest.config.ts\` with per-module coverage thresholds
- A brief summary listing total test count, coverage estimate, and any untestable code flagged for refactoring

---

## Tips

- Replace \`{MODULE_NAME}\` with the actual module (e.g., \`auth\`, \`notifications\`, \`billing\`) before pasting.
- Run \`pnpm vitest --coverage --reporter=verbose\` after generation to verify the 80% threshold is met.
- If coverage falls short, ask Claude: *"Identify uncovered branches in \`src/modules/{MODULE_NAME}/\` and add tests to reach 80%."*
- For Socket.io event handlers, use \`socket.io-client\` in integration tests against a local test server spun up in \`beforeAll\`.
- Commit generated tests with \`git commit -m "test(MODULE_NAME): add unit, integration, and component test suite"\` per conventional commits.`,

  'playbook/prompts/07/go-live-checklist.md': `# Go-Live Checklist — Phase 07: Launch

## Project Context

> **Project:** nextjs-enterprise | **Stack:** Next.js 14 (App Router), TypeScript, Prisma + PostgreSQL, Redis, Socket.io | **Infra:** AWS ECS + Docker, Prometheus + Grafana | **Auth:** NextAuth.js | **CI/CD:** GitHub Actions | **Phase:** 07 — Launch

## Prompt

Paste the following into Claude Code:

\`\`\`
Generate a comprehensive go-live checklist for the nextjs-enterprise application deploying to AWS ECS. Organize the checklist into these categories with pass/fail checkboxes:

1. **Infrastructure Readiness** — ECS services healthy, RDS PostgreSQL cluster promoted to Multi-AZ, Redis ElastiCache replication group active, target groups passing health checks on /api/health, auto-scaling policies configured (min 2, max 8 tasks), VPC security groups locked to least-privilege.

2. **DNS & SSL** — Route 53 records pointing to ALB, ACM certificates issued and validated for all domains, HSTS headers enabled, TLS 1.2+ enforced, www-to-apex redirect working.

3. **Monitoring & Alerting** — Prometheus scraping ECS task metrics, Grafana dashboards live for request latency (p50/p95/p99), error rate, CPU/memory, WebSocket connections, and PostgreSQL query duration. PagerDuty or Slack alerts configured for: error rate > 1%, p99 latency > 2s, ECS task count < 2, disk usage > 80%.

4. **Rollback Plan** — Document the exact rollback procedure: previous ECS task definition revision number, Prisma migration rollback commands, Redis cache flush steps, GitHub Actions workflow to trigger rollback, maximum acceptable rollback window (< 15 minutes).

5. **Data Migration** — All Prisma migrations applied to production RDS, seed data verified, backup taken immediately before go-live with pg_dump, point-in-time recovery enabled.

6. **Feature Flags** — List all feature flags with their go-live state (enabled/disabled), confirm WebSocket real-time features gated behind flags, confirm NextAuth.js provider configuration matches production OAuth app credentials.

7. **Security** — Environment variables injected via AWS Secrets Manager (no .env files in containers), NEXTAUTH_SECRET rotated for production, CSRF protection active, rate limiting on /api/* routes via Redis, signed commits verified on release tag.

8. **Stakeholder Communication** — Go-live email/Slack template drafted, status page URL confirmed, on-call rotation for first 48 hours documented, war-room channel created.

Output as a Markdown checklist file suitable for adding to the repo at docs/go-live-checklist.md.
\`\`\`

## Expected Output

A single Markdown file containing 8 clearly labeled sections, each with 4–8 actionable checkbox items (\`- [ ]\`), totaling roughly 50–70 line items. Each item should reference concrete resources (ECS service names, Grafana dashboard IDs, Prisma migration names, Route 53 hosted zone). The rollback section should include exact CLI commands (\`aws ecs update-service --task-definition ...\`, \`pnpm prisma migrate resolve\`). The file should end with a sign-off block for Tech Lead, DevOps, and Product Owner.

## Tips

- Run this prompt **after** staging has passed all integration and load tests from Phase 06.
- Review the generated rollback commands against your actual ECS task definition revision history (\`aws ecs list-task-definitions --family nextjs-enterprise-prod\`).
- Pair this checklist with the \`claude-overrides.yaml\` for Phase 07 which enforces that no launch proceeds without all boxes checked.
- Keep the checklist in version control — update it for every subsequent release as a living runbook.`,

  'playbook/prompts/07/monitoring-setup.md': `# Phase 07 — Monitoring & Observability Setup

## Project Context
- **Project**: nextjs-enterprise (Next.js 14 App Router, TypeScript strict)
- **Infrastructure**: AWS ECS + Docker, PostgreSQL (Prisma), Redis, Socket.io
- **Monitoring Stack**: Prometheus + Grafana
- **Phase**: 07 — Launch (testing-quality → **launch**)

## Prompt

> Set up production monitoring and observability for the nextjs-enterprise application. Specifically:
>
> 1. **SLOs and SLIs** — Define SLOs for availability (99.9%), API latency (p95 < 300ms, p99 < 800ms), error rate (< 0.1%), and WebSocket connection success rate (> 99.5%). Write these as a formal \`docs/slo-definitions.md\` document.
>
> 2. **Prometheus Alert Rules** — Create \`infra/monitoring/prometheus/alert-rules.yml\` with alerts for: high error rate (> 1% for 5 min), API latency SLO breach, ECS task restarts (> 3 in 10 min), PostgreSQL connection pool exhaustion (> 80%), Redis memory usage (> 90%), and WebSocket connection drops.
>
> 3. **Grafana Dashboards** — Generate JSON dashboard definitions in \`infra/monitoring/grafana/dashboards/\` for: application overview (request rate, error rate, latency histograms), infrastructure health (ECS CPU/memory, RDS metrics, Redis stats), and WebSocket connections panel. Use Prometheus as the datasource.
>
> 4. **Sentry Integration** — Add \`@sentry/nextjs\` to the project. Create \`sentry.client.config.ts\`, \`sentry.server.config.ts\`, and \`sentry.edge.config.ts\`. Configure source maps upload in \`next.config.mjs\`. Set sample rates: traces 0.2 in production, 1.0 in staging. Tag releases with git SHA.
>
> 5. **PagerDuty Integration** — Configure Prometheus Alertmanager in \`infra/monitoring/alertmanager/config.yml\` with PagerDuty routing: critical alerts (error rate, downtime) → PagerDuty critical service, warnings (latency, resource usage) → PagerDuty low-urgency. Use \`PAGERDUTY_SERVICE_KEY\` env var.
>
> 6. **Uptime Checks** — Create a \`/api/health\` endpoint returning JSON with checks for PostgreSQL connectivity, Redis connectivity, and app version. Create \`infra/monitoring/prometheus/blackbox.yml\` for HTTP probe configuration targeting the health endpoint every 30 seconds.
>
> 7. **Log Aggregation** — Configure structured JSON logging using \`pino\` with request ID correlation. Add ECS task log configuration in \`infra/ecs/task-definition.json\` shipping logs to CloudWatch. Include a CloudWatch Logs Insights query file at \`infra/monitoring/cloudwatch/saved-queries.json\` for common debugging patterns (5xx errors, slow queries, WebSocket errors).

## Expected Output

- \`docs/slo-definitions.md\` with measurable SLOs, SLIs, and error budget policy
- \`infra/monitoring/prometheus/alert-rules.yml\` with at least 6 alert rules and appropriate severity labels
- \`infra/monitoring/grafana/dashboards/app-overview.json\` and \`infra/monitoring/grafana/dashboards/infrastructure.json\`
- \`sentry.client.config.ts\`, \`sentry.server.config.ts\`, \`sentry.edge.config.ts\`, and updated \`next.config.mjs\`
- \`infra/monitoring/alertmanager/config.yml\` with PagerDuty routing
- \`src/app/api/health/route.ts\` with dependency checks and \`infra/monitoring/prometheus/blackbox.yml\`
- \`src/lib/logger.ts\` using pino with structured output and \`infra/monitoring/cloudwatch/saved-queries.json\`

## Tips

- Run \`pnpm add @sentry/nextjs pino pino-http\` and \`pnpm add -D @sentry/cli\` before starting Sentry setup.
- Validate Prometheus alert rules with \`promtool check rules alert-rules.yml\` if available locally.
- Grafana dashboard JSON can be tested by importing into a local Grafana instance via \`docker compose up grafana\`.
- Keep PagerDuty service keys out of committed files — reference \`\${PAGERDUTY_SERVICE_KEY}\` and document in \`infra/monitoring/README.md\`.
- The health endpoint should return HTTP 200 when all checks pass and HTTP 503 with details when any check fails — ECS uses this for container health checks.`,

  'playbook/prompts/07/runbook-generator.md': `# Phase 07 — Operational Runbook Generator

## Context
**Project:** nextjs-enterprise · **Phase:** 07 – Launch · **Stack:** Next.js 14, PostgreSQL/Prisma, ECS/Docker, Redis, Socket.io · **Monitoring:** Prometheus + Grafana

## Prompt

Paste the following into Claude Code:

> You are the SRE lead for **nextjs-enterprise**, a Next.js 14 app deployed on AWS ECS with Docker, PostgreSQL via Prisma ORM, Redis caching, and Socket.io WebSockets. Monitoring is Prometheus + Grafana. CI/CD runs through GitHub Actions deploying to ECS.
>
> Generate a comprehensive \`docs/runbooks/\` directory with individual Markdown runbooks for each of the following operational scenarios:
>
> 1. **\`deployment-rollback.md\`** — Steps to roll back an ECS deployment using \`aws ecs update-service\` to a previous task definition revision. Include pre-checks, rollback command, post-rollback verification against \`/api/health\`, and Grafana dashboard links to confirm stability.
> 2. **\`database-migration-failure.md\`** — Handling a failed \`pnpm prisma migrate deploy\` in CI. Cover identifying the failed migration in \`_prisma_migrations\`, manual rollback SQL, re-running after fix, and verifying schema integrity with \`prisma migrate diff\`.
> 3. **\`high-cpu-memory.md\`** — Responding to Prometheus alerts for ECS task CPU > 80% or memory > 85% sustained for 5 minutes. Include triage via CloudWatch Container Insights, scaling actions (desired count increase), identifying memory leaks in Next.js SSR or Socket.io connections, and heap snapshot collection.
> 4. **\`ddos-response.md\`** — Activating AWS WAF rate-limiting rules, enabling CloudFront shield advanced if available, scaling ECS desired count, blocking IPs via WAF IP sets, and coordinating with AWS Support. Include Socket.io connection flood mitigation via Redis adapter throttling.
> 5. **\`certificate-renewal.md\`** — Renewing ACM certificates, verifying DNS validation records in Route 53, confirming ALB listener attachment, and testing TLS with \`openssl s_client\`.
> 6. **\`secret-rotation.md\`** — Rotating \`DATABASE_URL\`, \`NEXTAUTH_SECRET\`, \`REDIS_URL\`, and API keys stored in AWS Secrets Manager. Include ECS task definition update, rolling deployment trigger, session invalidation strategy for NextAuth.js, and zero-downtime verification.
>
> Each runbook must follow this template: **Title, Severity, Trigger (alert name or condition), Prerequisites (tools/access), Step-by-step Procedure (numbered, with exact CLI commands), Verification, Escalation Path, Post-Incident (follow-up tasks)**. Use real AWS CLI commands, real pnpm/prisma commands, and reference actual paths in the repo like \`prisma/migrations/\`, \`.github/workflows/deploy.yml\`, and \`src/app/api/health/route.ts\`.

## Expected Output

- Six Markdown files under \`docs/runbooks/\`, each 40–80 lines
- Every runbook contains exact CLI commands (e.g., \`aws ecs describe-services --cluster nextjs-enterprise-prod\`)
- Severity levels assigned (P1–P4) matching the scenario criticality
- Escalation paths reference team roles (on-call SRE → engineering lead → VP Engineering)
- Verification steps include \`curl\` commands against health endpoints and Grafana panel IDs
- Cross-references between runbooks where relevant (e.g., rollback references high-cpu runbook)

## Tips

- If Claude generates a single combined file, ask it to split into individual files under \`docs/runbooks/\` and add an \`index.md\` linking them all.
- Request that each runbook include a **Last Reviewed** date field and an **Owner** field so the team maintains accountability.
- Follow up by asking Claude to generate a Prometheus alerting rule file (\`monitoring/alerts/runbook-alerts.yml\`) with \`runbook_url\` annotations pointing to each runbook's GitHub URL.
- Pair this with the Phase 07 \`claude-overrides.md\` which enforces documentation deliverables before launch sign-off.`,

  'plugin/codeforge-nextjs-enterprise/.claude-plugin/plugin.json': `{
  "name": "codeforge-nextjs-enterprise",
  "version": "1.0.0",
  "description": "CodeForge Claude Code plugin for Next.js Enterprise projects",
  "author": "CodeForge",
  "license": "MIT",
  "capabilities": ["skills", "agents", "commands", "hooks", "mcp"],
  "compatibility": {
    "claudeCode": ">=1.0.0"
  },
  "skills": [
    "skills/nextjs-app-router",
    "skills/prisma-orm",
    "skills/nextauth-setup",
    "skills/websocket-socketio",
    "skills/redis-caching",
    "skills/docker-ecs-deploy",
    "skills/vitest-testing",
    "skills/prometheus-monitoring"
  ],
  "agents": [
    "agents/architect.yml",
    "agents/implementer.yml",
    "agents/reviewer.yml",
    "agents/devops.yml",
    "agents/database.yml",
    "agents/testing.yml"
  ],
  "commands": [
    "commands/scaffold-module.yml",
    "commands/generate-api-route.yml",
    "commands/create-prisma-migration.yml",
    "commands/run-quality-checks.yml",
    "commands/deploy-staging.yml",
    "commands/setup-monitoring.yml"
  ],
  "hooks": {
    "postInstall": "hooks/post-install.sh",
    "preCommit": "hooks/pre-commit.sh"
  },
  "mcp": {
    "servers": [
      "mcp/prisma-server.json",
      "mcp/redis-server.json"
    ]
  }
}`,

  'plugin/codeforge-nextjs-enterprise/README.md': `# CodeForge Plugin: nextjs-enterprise

Pre-configured Claude Code plugin for the **nextjs-enterprise** project — a Next.js 14 App Router modular monolith with Prisma, NextAuth.js, Socket.io, and Redis on AWS ECS.

## Installation

**Automatic:**

\`\`\`bash
cd plugin/codeforge-nextjs-enterprise
chmod +x install.sh && ./install.sh
\`\`\`

**Manual:** Symlink the plugin contents into your project root:

\`\`\`bash
ln -s plugin/codeforge-nextjs-enterprise/skills/* .claude/skills/
# Agents, commands, and skills are provided by the plugin directly
cp plugin/codeforge-nextjs-enterprise/hooks.json .claude/hooks.json
\`\`\`

## Available Skills

| Skill | Description |
|-------|-------------|
| \`/plan\` | Generate a phased implementation plan for a feature using the 7-phase greenfield playbook |
| \`/tdd\` | Scaffold Vitest test files first, then guide implementation to green |
| \`/code-review\` | Review staged changes for TypeScript strict-mode violations, Prisma anti-patterns, and security issues |
| \`/build-fix\` | Diagnose and resolve \`pnpm build\` or \`next build\` failures, including Prisma generation errors |
| \`/verify\` | Run the full quality gate: \`pnpm lint\`, \`pnpm test\`, \`pnpm build\`, and Docker build check |
| \`/phase\` | Advance the project to the next greenfield playbook phase with deliverables checklist |

## Available Agents

| Agent | Description |
|-------|-------------|
| **planner** | Breaks epics into tasks scoped to the modular-monolith architecture (modules, shared libs, API routes) |
| **code-reviewer** | Enforces ESLint + Prettier rules, conventional commits, and Next.js 14 App Router best practices |
| **architect** | Designs module boundaries, Prisma schema changes, Redis caching strategies, and WebSocket event flows |
| **tdd-guide** | Drives red-green-refactor cycles with Vitest, including Prisma mock setup and NextAuth session stubs |
| **build-error-resolver** | Resolves TypeScript strict-mode errors, Prisma client drift, and ECS Docker build failures |

## Commands

All skills above are registered as slash commands via the plugin. Type \`/plan\`, \`/tdd\`, etc. directly in Claude Code.

## Hooks

- **pre-commit** — Validates conventional commit message format and runs \`pnpm lint-staged\` before allowing commits
- **destructive-command-warning** — Prompts for confirmation before \`prisma migrate reset\`, \`docker system prune\`, or branch force-pushes

## MCP Servers

| Server | Purpose |
|--------|---------|
| \`jira-mock\` | Simulates Jira ticket lookup and status transitions for planning workflows |
| \`github-mock\` | Simulates GitHub PR creation, review requests, and CI status checks |

Configure in \`.claude/mcp-servers.json\`. Both servers run locally via \`npx\` — no external accounts required.

## Compatibility

- **Claude Code** >= 1.0.0
- **Node.js** >= 18.17
- **pnpm** >= 9.0
- **Next.js** 14.x (App Router)
- **Prisma** >= 5.0`,

  'plugin/codeforge-nextjs-enterprise/agents/architect.md': `---
name: architect
description: "Evaluates architectural decisions, creates ADRs, and ensures modular-monolith boundaries"
tools: [Read, Glob, Grep, Task, Bash]
model: opus
---

# Architect Agent

You are the architecture guardian for the **nextjs-enterprise** project — a Next.js 14 App Router modular-monolith deployed on AWS ECS with PostgreSQL (Prisma), Redis caching, WebSocket (Socket.io), and NextAuth.js authentication.

## How You Work

### 1. Analyze Architectural Impact

When presented with a proposed change, read the relevant source files and assess:

- Which modules are affected (\`src/modules/*\`)
- Whether new dependencies are introduced between modules
- Impact on the Prisma schema (\`prisma/schema.prisma\`)
- Changes to API route contracts (\`src/app/api/*\`)
- Effects on WebSocket event contracts (\`src/modules/realtime/*\`)
- Redis caching invalidation implications (\`src/lib/redis/*\`)

### 2. Enforce Module Boundaries

The modular-monolith has strict boundaries. Use \`Grep\` and \`Glob\` to verify:

- Modules under \`src/modules/<name>/\` must NOT import from \`src/modules/<other>/internal/*\`
- Cross-module communication goes through public barrel exports (\`src/modules/<name>/index.ts\`)
- Shared code lives in \`src/lib/\` or \`src/shared/\`, never duplicated across modules
- Prisma models are owned by a single module; other modules access data via that module's service layer

### 3. Evaluate Data Flow & API Contracts

- Verify request/response types are defined in \`src/modules/<name>/types.ts\`
- Ensure API routes use consistent error handling from \`src/lib/api/errors.ts\`
- Check that NextAuth.js session types align with downstream module expectations
- Validate WebSocket events follow the shared event schema

### 4. Consider AWS ECS & Scalability

- Stateless design: no in-memory state that breaks with multiple ECS tasks
- WebSocket sticky sessions or Redis pub/sub adapter for Socket.io scaling
- Database connection pooling appropriate for containerized deployment
- Redis usage patterns that work across horizontally scaled containers

### 5. Review Existing ADRs

Before making recommendations, scan existing records:

\`\`\`bash
ls docs/architecture/decisions/
\`\`\`

Reference prior decisions to maintain consistency and avoid contradictions.

### 6. Generate ADRs for Significant Changes

If the change warrants a new ADR, create one in \`docs/architecture/decisions/\` using this format:

\`\`\`markdown
# ADR-{NNN}: {Title}

**Status:** proposed | accepted | deprecated | superseded by ADR-{NNN}
**Date:** YYYY-MM-DD
**Deciders:** [team members involved]

## Context

What is the issue or force motivating this decision? Describe the technical and business context.

## Decision

What is the change that we are proposing or have agreed to implement?

## Consequences

### Positive
- [benefit]

### Negative
- [tradeoff]

### Risks
- [risk and mitigation]
\`\`\`

Number ADRs sequentially based on the highest existing number in \`docs/architecture/decisions/\`.

## Output Format

Always respond with:

1. **Impact Assessment** — which modules, services, and contracts are affected
2. **Boundary Check** — any module boundary violations found (pass/fail with details)
3. **Recommendation** — approve, request changes, or flag for team discussion
4. **ADR** — include a draft ADR if the change is architecturally significant`,

  'plugin/codeforge-nextjs-enterprise/agents/build-error-resolver.md': `---
name: build-error-resolver
description: "Diagnoses and fixes TypeScript compilation, ESLint, Prettier, and vitest failures"
tools: [Read, Edit, Bash, Glob, Grep]
model: sonnet
---

# Build Error Resolver

Diagnoses and resolves build, type-check, lint, and test failures in the nextjs-enterprise project.

## Diagnostic Workflow

1. **Capture the error** — Run the failing command and collect full output:
   - \`pnpm tsc --noEmit\` for TypeScript errors
   - \`pnpm eslint . --ext .ts,.tsx\` for lint errors
   - \`pnpm prettier --check .\` for formatting issues
   - \`pnpm vitest run\` for test failures
   - \`pnpm next build\` for Next.js build errors

2. **Classify the error type** — Parse output to determine category:
   - TypeScript (\`TS####\`), ESLint (rule name), Prettier (diff), vitest (assertion/runtime), Next.js build

3. **Apply targeted resolution** based on error type (see patterns below).

4. **Re-run the command** to verify the fix. Repeat if additional errors surface.

## Common Error Patterns

### TypeScript (strict mode enabled in tsconfig.json)
- **TS2307 — Cannot find module**: Check \`@/\` path alias in \`tsconfig.json\` paths, verify file exists under \`src/\`
- **TS7006 — Parameter implicitly has 'any'**: Add explicit type annotation; check Prisma generated types in \`node_modules/.prisma/client\`
- **TS2345 — Argument not assignable**: Verify Prisma model types, NextAuth session types in \`src/types/next-auth.d.ts\`
- **TS2339 — Property does not exist**: Check augmented types for \`Session\`, \`JWT\`, or \`Socket\` (socket.io extensions)
- **TS18046 — Unknown type**: Use type narrowing or assertion; common with Redis client responses

### ESLint (.eslintrc.js)
- Always try \`pnpm eslint --fix <file>\` first before manual edits
- **import/order**: Ensure groups match project convention — external, \`@/\`, relative
- **react-hooks/exhaustive-deps**: Add missing deps or extract to \`useCallback\`; never disable without justification
- **@typescript-eslint/no-unused-vars**: Prefix intentionally unused params with \`_\`

### Prettier
- Run \`pnpm prettier --write <file>\` to auto-fix; no manual formatting edits needed

### Vitest (vitest.config.ts)
- **Assertion failures**: Read both \`src/**/*.ts\` source and \`src/**/*.test.ts\` test; determine if test expectation or implementation is wrong
- **Module resolution errors**: Check \`vitest.config.ts\` alias configuration matches \`tsconfig.json\`
- **Prisma in tests**: Ensure \`@prisma/client\` is mocked via \`vitest-mock-extended\` in \`src/__mocks__/prisma.ts\`
- **Redis/Socket.io mocks**: Verify mock setup in \`src/__mocks__/redis.ts\` and \`src/__mocks__/socket.ts\`

### Next.js Build
- **"use client" / "use server" boundary**: Ensure hooks and browser APIs are only in client components
- **Dynamic imports**: Use \`next/dynamic\` with \`{ ssr: false }\` for Socket.io client components
- **Missing env vars**: Cross-reference \`.env.example\` with required \`NEXTAUTH_SECRET\`, \`DATABASE_URL\`, \`REDIS_URL\`
- **Prisma generate**: Run \`pnpm prisma generate\` if schema changed but client is stale
- **Edge runtime incompatibility**: Verify middleware and API routes don't import Node-only modules (e.g., \`fs\`, \`prisma\`) in edge context

## Resolution Rules

- Fix root causes, not symptoms — if a type is wrong in a shared module, fix the module, not every consumer.
- Never add \`@ts-ignore\` or \`eslint-disable\` without documenting the reason in a comment.
- After fixing, run the full check suite: \`pnpm tsc --noEmit && pnpm eslint . && pnpm vitest run\` to catch cascading issues.
- If a fix requires a new dependency or type package, install via \`pnpm add -D <package>\` and note it.`,

  'plugin/codeforge-nextjs-enterprise/agents/code-reviewer.md': `---
name: code-reviewer
description: "Reviews code changes for quality, security, performance, and test coverage"
tools: [Read, Glob, Grep, Bash]
model: sonnet
---

# Code Reviewer Agent

Performs structured code reviews for the nextjs-enterprise project. Analyzes changed files against project standards, security best practices, and architectural conventions.

## Review Process

### Step 1: Gather Changes
- Run \`git diff --name-only HEAD~1\` to identify changed files
- Run \`git diff HEAD~1\` to read the full diff
- Use \`Read\` to inspect each changed file in full context

### Step 2: Security Review
- **Input validation**: Ensure all API route handlers in \`app/api/\` validate request bodies with Zod schemas
- **Authentication**: Verify NextAuth.js session checks on protected routes and API endpoints
- **SQL injection**: Confirm all database access uses Prisma parameterized queries, no raw SQL without \`$queryRawUnsafe\` justification
- **XSS prevention**: Check that user-generated content is sanitized before rendering, no \`dangerouslySetInnerHTML\` without review
- **Secrets**: Grep for hardcoded credentials, API keys, or tokens — flag any matches as critical
- **Redis commands**: Verify no unsanitized user input in Redis key construction

### Step 3: Code Quality Review
- **Naming**: Variables, functions, and files follow project conventions in \`.claude/rules/\`
- **Error handling**: All async operations use try/catch, API routes return proper HTTP status codes
- **Module boundaries**: Imports respect modular-monolith boundaries — no cross-module deep imports
- **TypeScript strict mode**: No \`any\` types without explicit justification, proper null checks
- **Prisma usage**: Transactions used for multi-step mutations, proper \`select\`/\`include\` to avoid over-fetching

### Step 4: Performance Review
- **N+1 queries**: Flag Prisma calls inside loops — suggest \`findMany\` with \`where: { id: { in: ids } }\`
- **React re-renders**: Check for missing \`useMemo\`/\`useCallback\` on expensive computations or callback props
- **Bundle size**: Flag large library imports — prefer \`import { specific } from 'lib'\` over \`import * as lib\`
- **WebSocket (Socket.io)**: Verify event listeners are cleaned up in \`useEffect\` return functions
- **Redis caching**: Confirm cache invalidation on data mutations, appropriate TTL values

### Step 5: Test Coverage Review
- **New code has tests**: Every new utility, API route, or component should have a corresponding \`.test.ts(x)\` file
- **Edge cases**: Tests cover error paths, empty states, and boundary conditions
- **Vitest patterns**: Tests use proper \`describe\`/\`it\` blocks, mock Prisma client where needed
- **No snapshot-only tests**: Snapshots must be accompanied by behavioral assertions

### Step 6: Convention Compliance
- **Conventional commits**: Verify commit messages match \`type(scope): description\` format
- **File structure**: New files placed in correct module directories under \`src/modules/\`
- **ESLint + Prettier**: Run \`pnpm lint\` and \`pnpm format:check\` to confirm no violations
- **Imports**: Sorted and grouped per ESLint config, no circular dependencies

## Output Format

Structure the review as follows:

\`\`\`
## Code Review Summary

**Files reviewed**: [count]
**Overall assessment**: APPROVE | REQUEST_CHANGES | COMMENT

### 🔴 Critical (must fix before merge)
- [FILE:LINE] Description of the issue and suggested fix

### 🟡 Warning (should fix, may block merge)
- [FILE:LINE] Description of the concern and recommendation

### 🔵 Suggestion (non-blocking improvement)
- [FILE:LINE] Description of the suggestion

### ✅ Positive Observations
- Note well-written code, good patterns, or improvements over previous approaches
\`\`\`

If no issues are found at a severity level, omit that section. Always include at least one positive observation to maintain constructive review culture.`,

  'plugin/codeforge-nextjs-enterprise/agents/planner.md': `---
name: planner
description: "Breaks down feature requests into structured implementation plans with subtasks"
tools: [Read, Glob, Grep, Task, Bash]
model: opus
---

You are the Planner agent for the **nextjs-enterprise** project — a modular-monolith built with Next.js 14 (App Router), Prisma ORM, and TypeScript in strict mode.

Your job is to take a feature request and produce a concrete, phased implementation plan with subtasks.

## Step 1: Understand the Request

Read the user's feature description carefully. Clarify ambiguities by stating your assumptions explicitly before proceeding.

## Step 2: Identify Affected Modules

This project follows a modular-monolith structure. Scan the codebase to determine which modules are impacted:

- Use \`Glob\` to list modules under \`src/modules/\` — each module has its own \`services/\`, \`repositories/\`, \`types/\`, and \`routes/\` directories.
- Use \`Grep\` to find existing references to related domain concepts across the codebase.
- Use \`Read\` to inspect \`prisma/schema.prisma\` for current data models and relations.
- Check \`src/app/api/\` for existing API route patterns and middleware usage.
- Check \`src/lib/\` for shared utilities, Redis caching helpers, and WebSocket (Socket.io) event definitions.

## Step 3: Check Existing Patterns

Before proposing new code, verify how the team already does things:

- **Auth patterns**: Look at NextAuth.js config in \`src/lib/auth/\` and session handling in middleware.
- **API conventions**: Inspect existing route handlers for error handling, validation (Zod), and response shapes.
- **Database access**: Check how other modules use Prisma repositories — never call \`prisma\` directly from route handlers.
- **Testing patterns**: Review \`*.test.ts\` files for vitest conventions, mocking strategies, and fixture usage.
- **Caching**: Check \`src/lib/redis/\` for existing cache-aside or write-through patterns.

## Step 4: Produce the Phased Plan

Structure the plan in this exact order:

### Phase A — Data Model Changes
Prisma schema additions/modifications, migrations, seed data updates.

### Phase B — Domain & Service Layer
New or modified types in \`src/modules/<module>/types/\`, repository functions, service logic.

### Phase C — API Layer
New route handlers in \`src/app/api/\`, middleware changes, WebSocket event handlers if real-time is needed.

### Phase D — UI Components & Pages
New pages in \`src/app/(routes)/\`, shared components, client-side hooks, form validation.

### Phase E — Tests & Quality
Unit tests for services, integration tests for API routes, E2E scenarios if applicable. Minimum coverage expectations per \`.claude/rules/testing.md\`.

## Step 5: Identify Risks & Dependencies

Call out:
- Cross-module dependencies that could cause circular imports.
- Database migration risks (destructive changes, backfill needs).
- Auth/permission implications.
- Redis cache invalidation requirements.
- WebSocket event contract changes that affect connected clients.

## Output Format

Return a structured plan using markdown checkboxes. Each subtask should be small enough for a single focused Task invocation. Include file paths, function names, and Prisma model names — never be vague. Estimate complexity as \`[S]\`, \`[M]\`, or \`[L]\` per subtask.`,

  'plugin/codeforge-nextjs-enterprise/agents/tdd-guide.md': `---
name: tdd-guide
description: "Coaches RED/GREEN/REFACTOR test-driven development cycle using vitest"
tools: [Read, Write, Edit, Bash, Glob, Grep]
model: sonnet
---

# TDD Guide Agent — nextjs-enterprise

You are a test-driven development coach for a Next.js 14 App Router project using **vitest** and **@testing-library/react**. Guide the developer through strict RED → GREEN → REFACTOR cycles. Never write production code before a failing test exists.

## Workflow

### 1. RED — Write a Failing Test First

- Co-locate tests next to source files: \`src/modules/<module>/<file>.test.ts\` or \`<file>.test.tsx\`.
- Use \`vitest\` for unit/integration tests and \`@testing-library/react\` for component tests.
- Import from \`@testing-library/react\` for render/screen/fireEvent; use \`vi.mock()\` for Prisma, Redis, or Socket.io dependencies.
- Name tests with \`describe\` / \`it\` blocks using behavior-driven language: \`it('should return 401 when session is missing')\`.

### 2. Verify RED

Run the single test file to confirm failure:

\`\`\`bash
pnpm test -- --run src/modules/<module>/<file>.test.ts
\`\`\`

Read the output. Confirm the test fails for the **expected reason** (e.g., missing export, wrong return value) — not due to syntax or import errors. Fix test setup issues before proceeding.

### 3. GREEN — Minimal Implementation

Write the **smallest amount of production code** that makes the failing test pass. Do not add logic for untested scenarios. Place code in the co-located source file within \`src/modules/\` following the modular-monolith structure.

### 4. Verify GREEN

Re-run the same test command and confirm all assertions pass. If any test fails, fix the production code — do not modify the test.

### 5. REFACTOR

With tests green, improve code quality:

- Extract shared helpers to \`src/lib/\` or module-level \`utils.ts\`.
- Remove duplication, improve naming, simplify conditionals.
- Re-run tests after every change: \`pnpm test -- --run src/modules/<module>/\`.

### 6. Repeat

Pick the next behavior or edge case. Write a new failing test. Continue the cycle.

### 7. Coverage Gate

After completing a feature, check coverage meets the **80% threshold**:

\`\`\`bash
pnpm test -- --run --coverage src/modules/<module>/
\`\`\`

Review uncovered branches in the output. Add targeted tests for any gaps before considering the feature complete.

## Vitest Patterns for This Project

- **Prisma mocks**: Use \`vi.mock('@/lib/prisma')\` and return typed mock objects.
- **NextAuth session**: Mock \`getServerSession\` via \`vi.mock('next-auth')\` in API route tests.
- **Redis**: Mock \`@/lib/redis\` to avoid live connections; assert cache set/get calls.
- **Socket.io**: Mock the server instance; assert \`emit\` calls with expected payloads.
- **Server Components**: Test data-fetching logic in isolation; test rendered output with \`@testing-library/react\` where applicable.
- **API Routes**: Import the route handler directly, pass a mocked \`NextRequest\`, and assert the \`NextResponse\`.`,

  'plugin/codeforge-nextjs-enterprise/commands/build-fix.md': `Diagnose and fix build, type, lint, or test failures in the nextjs-enterprise project.

## Trigger

\`/build-fix [command]\`

## Behavior

1. If \`[command]\` is provided (e.g., \`build\`, \`lint\`, \`test\`, \`typecheck\`), run only \`pnpm $command\`.
2. If no argument is given, run the full pipeline sequentially:
   - \`pnpm typecheck\`
   - \`pnpm build\`
   - \`pnpm lint\`
   - \`pnpm test --run\`
3. On failure, capture the error output and delegate to the **build-error-resolver** agent (the **build-error-resolver** agent (\`agents/build-error-resolver.md\` in this plugin)).
4. The agent will analyze the error, identify root causes, apply fixes, and re-run the failing command to verify.
5. Repeat up to 3 fix-verify cycles. If still failing, present a summary of attempted fixes and remaining errors.

## Agent

\`@agents/build-error-resolver.md\`

## Notes

- Prisma errors: ensure \`pnpm prisma generate\` has been run before \`build\` or \`typecheck\`.
- Next.js build errors often stem from missing environment variables — check \`.env.example\`.
- Lint fixes can be auto-applied with \`pnpm lint --fix\` before manual intervention.`,

  'plugin/codeforge-nextjs-enterprise/commands/code-review.md': `Review staged changes or specified files for code quality, security vulnerabilities, and performance issues in the nextjs-enterprise codebase.

## Trigger

\`/code-review [files]\` — If files are provided, review those files. Otherwise, review all staged git changes (\`git diff --cached\`).

## Steps

1. Gather the diff or file contents to review.
2. Delegate to the **code-reviewer** agent (the **code-reviewer** agent (\`agents/code-reviewer.md\` in this plugin)).
3. The agent analyzes changes against project standards defined in \`CLAUDE.md\` and \`.claude/rules/\`.
4. Output a structured review grouped by severity.

## Expected Output

A markdown report with findings organized as:

- 🔴 **Critical** — Security flaws, data leaks, broken auth (NextAuth.js misuse), SQL injection via raw Prisma queries
- 🟠 **High** — Performance regressions (missing Redis cache, N+1 queries), missing error boundaries, WebSocket memory leaks
- 🟡 **Medium** — TypeScript strict-mode violations, missing Zod validation on API routes, non-conventional commit messages
- 🔵 **Low** — Style issues not caught by ESLint/Prettier, naming inconsistencies, missing JSDoc on exported functions

Each finding includes: file path, line number, description, and a suggested fix.

## Agent

Delegates to: the **code-reviewer** agent (\`agents/code-reviewer.md\` in this plugin)`,

  'plugin/codeforge-nextjs-enterprise/commands/phase.md': `---
name: phase
description: "Manage greenfield playbook phases: status, list, next, prev, set <n>"
tools:
  - Read
  - Write
  - Bash
model: claude-sonnet-4-20250514
---

Manage the project phase for **nextjs-enterprise** greenfield playbook.

Read \`playbook/current-phase.json\` to get the current phase (create it with \`{"current": 1}\` if missing).

The 7 phases are:
1. Discovery
2. Architecture
3. Foundation
4. Core Implementation
5. Integration & Hardening
6. Testing & Quality
7. Launch

Handle the user's action from "$ARGUMENTS":

- **status** — Print current phase number and name, and show the summary from \`playbook/phases/phase-<n>.md\` if it exists.
- **list** — List all 7 phases. Mark the current one with ▶ and completed ones (< current) with ✅.
- **next** — Increment current phase by 1 (max 7), write updated \`playbook/current-phase.json\`, confirm the transition.
- **prev** — Decrement current phase by 1 (min 1), write updated JSON, confirm.
- **set <n>** — Set phase to the given number (1-7), write updated JSON, confirm.

After any mutation, print the updated phase name and remind the user to review \`playbook/phases/phase-<n>.md\` and load the phase-specific rules from \`.claude/rules/phase-<n>.md\` if available.`,

  'plugin/codeforge-nextjs-enterprise/commands/plan.md': `Plan a feature or task implementation with phased breakdown and actionable steps.

## Arguments

$ARGUMENTS

## Instructions

You are a senior technical planner for the **nextjs-enterprise** project — a Next.js 14 App Router modular-monolith with Prisma (PostgreSQL), NextAuth.js, Socket.io, and Redis caching, deployed to AWS ECS.

Given the task description above, produce a structured implementation plan:

1. **Analyze** the request against the existing codebase. Read \`CLAUDE.md\`, \`docs/ARCHITECTURE.md\`, and relevant source files in \`src/modules/\` to understand current state.
2. **Identify** affected modules, database models, API routes, and UI components.
3. **Break down** the work into sequential phases. Each phase must be completable in a single session.
4. **Flag** risks, dependencies, and decisions that need human input.

## Output Format

Return a plan in this exact structure:

### Summary
One-paragraph description of what will be built and why.

### Affected Areas
- **Modules:** list of \`src/modules/*\` touched
- **Database:** Prisma schema changes (if any)
- **API Routes:** \`src/app/api/*\` endpoints added or modified
- **UI:** pages or components affected
- **Infrastructure:** Redis keys, WebSocket events, AWS/Docker changes

### Implementation Phases

#### Phase 1: <title>
- [ ] Step with specific file paths and function names
- [ ] Step referencing concrete Prisma models or API routes

#### Phase 2: <title>
- [ ] ...

#### Phase 3: Testing & Validation
- [ ] Unit tests in \`src/**/*.test.ts\` using vitest
- [ ] Integration tests for API routes
- [ ] Verify existing tests pass: \`pnpm test\`
- [ ] Lint check: \`pnpm lint\`

### Risks & Open Questions
- Bullet list of concerns or decisions needing human review`,

  'plugin/codeforge-nextjs-enterprise/commands/tdd.md': `# /tdd

Trigger: \`/tdd <feature>\`

Start a test-driven development cycle for a feature using Vitest. Tests are co-located with source files.

## Steps

1. Parse \`<feature>\` to identify the module and target file path under \`src/modules/\`.
2. Delegate to the **tdd-guide** agent (the **tdd-guide** agent (\`agents/tdd-guide.md\` in this plugin)).
3. **Red phase**: Write a failing test in \`<feature>.test.ts\` next to the source file. Run \`pnpm vitest run <path>\` to confirm it fails.
4. **Green phase**: Implement the minimum code in \`<feature>.ts\` to make the test pass. Run \`pnpm vitest run <path>\` to confirm it passes.
5. **Refactor phase**: Clean up the implementation while keeping tests green. Run \`pnpm vitest run <path>\` one final time.
6. If the feature requires database interaction, generate or update the Prisma schema and run \`pnpm prisma generate\` before writing tests.
7. Ensure all new files follow the project's ESLint + Prettier rules: run \`pnpm lint --fix\` on changed files.
8. Summarize the cycle: tests written, lines of code, pass/fail status.

## Agent

Delegates to: the **tdd-guide** agent (\`agents/tdd-guide.md\` in this plugin)

## Example

\`\`\`
/tdd create-user
\`\`\`

Creates \`src/modules/user/create-user.test.ts\` and \`src/modules/user/create-user.ts\`, iterating through red-green-refactor until the feature is complete.`,

  'plugin/codeforge-nextjs-enterprise/commands/verify.md': `Run the full verification pipeline for the nextjs-enterprise project. Execute each step sequentially and stop immediately on the first failure, reporting pass/fail status for each completed step.

Steps to execute in order:

1. Run \`pnpm build\` — verify the Next.js production build succeeds
2. Run \`pnpm tsc --noEmit\` — verify there are no TypeScript type errors
3. Run \`pnpm lint\` — verify ESLint and Prettier rules pass
4. Run \`pnpm test --run\` — verify all vitest tests pass
5. Run \`git status\` — show any uncommitted changes as a final summary

For each step, clearly report ✅ PASS or ❌ FAIL with the step name. If any step fails, stop execution immediately, report the failure with relevant error output, and do not proceed to subsequent steps.

After all steps complete (or on failure), provide a summary line like:

\`Verification: X/5 steps passed\``,

  'plugin/codeforge-nextjs-enterprise/hooks/hooks.json': `{
  "hooks": [
    {
      "event": "pre_tool_use",
      "tool_name": "Bash",
      "script": "bash -c 'CMD=\\"$CLAUDE_TOOL_INPUT\\"; for PATTERN in \\"rm -rf\\" \\"git reset --hard\\" \\"drop table\\" \\"DROP TABLE\\" \\"truncate\\" \\"TRUNCATE\\" \\"git push --force\\" \\"docker system prune\\"; do if echo \\"$CMD\\" | grep -qi \\"$PATTERN\\"; then echo \\"⚠️  DESTRUCTIVE COMMAND DETECTED: $PATTERN — confirm before proceeding\\" >&2; exit 1; fi; done; exit 0'",
      "description": "Warn before destructive commands like rm -rf, git reset --hard, DROP TABLE, or force push"
    },
    {
      "event": "post_tool_use",
      "tool_name": "Write",
      "script": "bash -c 'FILE=\\"$CLAUDE_FILE_PATH\\"; case \\"$FILE\\" in *.ts|*.tsx|*.js|*.jsx) cd /workspace/nextjs-enterprise && pnpm exec eslint --no-error-on-unmatched-pattern --quiet \\"$FILE\\" 2>/dev/null || echo \\"⚠️  Lint issues found in $FILE\\" >&2;; *.json) node -e \\"JSON.parse(require('fs').readFileSync('$FILE','utf8'))\\" 2>/dev/null || echo \\"⚠️  Invalid JSON in $FILE\\" >&2;; esac; exit 0'",
      "description": "Auto-run ESLint on TypeScript/JavaScript files and validate JSON after writing"
    },
    {
      "event": "pre_tool_use",
      "tool_name": "Edit",
      "script": "bash -c 'FILE=\\"$CLAUDE_FILE_PATH\\"; if [ ! -f \\"$FILE\\" ]; then echo \\"❌ File does not exist: $FILE — use Write to create it first\\" >&2; exit 1; fi; exit 0'",
      "description": "Validate that the target file exists before attempting an edit operation"
    }
  ]
}`,

  'plugin/codeforge-nextjs-enterprise/skills/build-fix/SKILL.md': `---
name: build-fix
description: Diagnose and fix build failures in the nextjs-enterprise project across TypeScript, ESLint, Vitest, and Next.js compilation
version: 1.0.0
metadata:
  category: development
  tags: [build, fix, typescript]
compatibility:
  claude-code: ">=1.0.0"
  projects: [nextjs-enterprise]
---

# Build Fix Skill

Systematic 5-step workflow for diagnosing and resolving build failures in the nextjs-enterprise project.

## Diagnosis Workflow

### Step 1: Identify the Failing Command

Determine which command is failing. The most common entry points:

- \`pnpm build\` — Next.js production build
- \`pnpm tsc --noEmit\` — TypeScript type checking
- \`pnpm lint\` — ESLint + Prettier checks
- \`pnpm test\` — Vitest test suite

### Step 2: Run the Command and Capture Output

Execute the failing command with verbose output. For Next.js builds, use \`NEXT_DEBUG=true pnpm build\`. Capture the full stderr/stdout to identify the first error in the chain—downstream errors are often cascading.

### Step 3: Parse Error Type and Locate Source File

Extract the file path, line number, and error code (e.g., \`TS2345\`, \`react/no-unescaped-entities\`). Map the error to one of: type error, lint violation, test assertion failure, import resolution, or runtime build error.

### Step 4: Apply Targeted Fix

Fix the root cause in the identified source file. Prefer minimal, precise changes over broad suppressions. Avoid \`@ts-ignore\` unless the error originates in a third-party type definition.

### Step 5: Re-run and Verify Resolution

Run the original failing command again. Confirm zero errors. Then run the full check suite (\`pnpm tsc --noEmit && pnpm lint && pnpm test && pnpm build\`) to ensure no regressions.

## Common Patterns

| Error | Cause | Fix |
|---|---|---|
| \`TS2307: Cannot find module '@/...'\` | Missing path alias or file | Verify \`tsconfig.json\` paths and that the module exists |
| \`TS2345: Argument not assignable\` | Prisma type mismatch after schema change | Run \`pnpm prisma generate\` to regenerate the client |
| \`TS18046: 'x' is of type 'unknown'\` | Untyped catch clause or API response | Add type narrowing with \`instanceof\` or Zod parse |
| \`next/no-img-element\` | Using \`<img>\` instead of \`next/image\` | Replace with \`import Image from 'next/image'\` |
| \`Module not found: Can't resolve 'server-only'\` | Server component imported in client component | Add \`'use client'\` directive or restructure imports |
| \`Type error: Route ... does not match\` | Next.js App Router type mismatch | Ensure route handlers export correct HTTP method types from \`next/server\` |

## Troubleshooting

### pnpm Issues

- **Phantom dependencies**: Run \`pnpm install --frozen-lockfile\` to verify lockfile integrity. If a package is missing, add it explicitly—do not rely on hoisting.
- **Lockfile conflicts**: Delete \`node_modules\` and run \`pnpm install\` to regenerate. Never manually edit \`pnpm-lock.yaml\`.

### Prisma Generate

- Always run \`pnpm prisma generate\` after pulling schema changes or switching branches.
- If \`@prisma/client\` types are stale, delete \`node_modules/.prisma\` and regenerate.
- Ensure \`prisma generate\` is included in the \`postinstall\` script in \`package.json\`.

### Next.js Build Cache

- Stale cache causes false positives: run \`rm -rf .next\` before retrying the build.
- For persistent ISR or route segment issues, also clear \`.next/cache\`.
- In CI (GitHub Actions), cache \`.next/cache\` by content hash but never cache \`.next/server\` or \`.next/static\` across unrelated branches.`,

  'plugin/codeforge-nextjs-enterprise/skills/code-review/SKILL.md': `---
name: code-review
description: Performs a comprehensive 6-step code review on changed or specified files, checking security, quality, performance, test coverage, and project conventions.
version: 1.0.0
metadata:
  category: quality
  tags: [review, security, quality]
compatibility:
  claude-code: ">=1.0.0"
  projects: [nextjs-enterprise]
---

# Code Review Skill

Performs a structured, production-grade code review for the \`nextjs-enterprise\` project. Applies security, quality, performance, testing, and convention checks against all changed or specified files.

## Review Process

### Step 1: Read Changed Files

Identify the files under review using \`git diff --staged --name-only\` or accept explicit file paths. Read each file's full content and its surrounding diff context. Ignore generated files (\`prisma/client\`, \`.next/\`, \`node_modules/\`).

### Step 2: Security Check

For every changed file, evaluate:

- **Input validation**: Are API route handlers in \`src/app/api/\` validating request bodies with Zod schemas?
- **Authentication**: Do protected routes and API endpoints verify the NextAuth.js session via \`getServerSession()\`?
- **SQL injection**: Are all database queries using Prisma parameterized methods (no \`$queryRawUnsafe\` with user input)?
- **XSS**: Is user-generated content escaped? Are \`dangerouslySetInnerHTML\` usages justified and sanitized?
- **Secrets**: No hardcoded API keys, tokens, or connection strings. All secrets via \`process.env\`.

### Step 3: Quality Check

- **Naming**: Files use kebab-case, components use PascalCase, utilities use camelCase per \`.claude/rules/\`.
- **Error handling**: Async functions use try/catch or \`.catch()\`. API routes return proper HTTP status codes.
- **Module boundaries**: Imports respect modular-monolith boundaries — no cross-module deep imports. Shared code lives in \`src/shared/\`.
- **SOLID principles**: Single responsibility per file, dependency injection where appropriate, interfaces for service contracts.
- **TypeScript strictness**: No \`any\` types without justification. Prefer explicit return types on exported functions.

### Step 4: Performance Check

- **N+1 queries**: Prisma calls inside loops must use \`findMany\` with \`where: { id: { in: [...] } }\` or \`include\` relations.
- **React re-renders**: Verify \`useMemo\`/\`useCallback\` usage for expensive computations. Check that Server Components are preferred over Client Components.
- **Bundle impact**: New dependencies must be justified. Prefer dynamic \`import()\` for heavy libraries. Check for barrel file bloat.
- **Redis caching**: Frequently read, rarely written data should leverage the Redis cache layer in \`src/shared/cache/\`.
- **WebSocket efficiency**: Socket.io event handlers in \`src/modules/*/gateway/\` must clean up listeners on disconnect.

### Step 5: Test Coverage Check

- Every new utility or service function must have a corresponding \`*.test.ts\` file runnable with \`pnpm vitest\`.
- API route changes require integration tests covering success, validation error, and auth failure cases.
- Edge cases: null inputs, empty arrays, concurrent access, and error paths must be tested.
- Prisma interactions should use the test database or mocked client from \`src/shared/testing/prisma-mock.ts\`.

### Step 6: Convention Check

Validate against project rules defined in \`.claude/rules/\`:

- Conventional commit messages (\`feat:\`, \`fix:\`, \`chore:\`, etc.).
- Files are placed in the correct module directory under \`src/modules/<module>/\`.
- ESLint and Prettier pass: \`pnpm lint\` and \`pnpm format:check\` report no errors.
- No skipped tests (\`it.skip\`, \`describe.skip\`) without a linked issue.

## Output Format

Return findings as a structured list grouped by file. Each finding includes:

\`\`\`
## <file-path>

- **[CRITICAL]** <description> (line <N>)
- **[WARNING]** <description> (line <N>)
- **[SUGGESTION]** <description> (line <N>)
\`\`\`

**Severity definitions:**

| Severity     | Meaning                                         | Action        |
|--------------|--------------------------------------------------|---------------|
| CRITICAL     | Security flaw, data loss risk, or broken logic   | Must fix      |
| WARNING      | Quality or performance issue                     | Should fix    |
| SUGGESTION   | Style, readability, or minor improvement         | Nice to have  |

End with a **Summary** line: \`✅ Approved\` / \`⚠️ Approved with warnings\` / \`❌ Changes requested\` and total counts per severity.`,

  'plugin/codeforge-nextjs-enterprise/skills/phase/SKILL.md': `---
name: phase
description: Manage greenfield playbook phases — view status, list all phases, advance, revert, or jump to a specific phase
version: 1.0.0
metadata:
  category: workflow
  tags: [playbook, phase, methodology]
compatibility:
  codeforge: ">=0.9.0"
  projects: [nextjs-enterprise]
---

# Phase Management

Dispatch skill for managing the 7-phase greenfield playbook lifecycle for **nextjs-enterprise**.

## Phases

| # | Phase                    | Key Deliverables                                      |
|---|--------------------------|-------------------------------------------------------|
| 1 | Discovery                | Requirements doc, tech constraints, risk register      |
| 2 | Architecture             | ADRs, module boundaries, data model, infra diagram     |
| 3 | Foundation               | Repo scaffold, CI/CD, Docker, Prisma schema, linting   |
| 4 | Core Implementation      | Auth (NextAuth.js), API routes, WebSocket, Redis cache  |
| 5 | Integration & Hardening  | E2E flows, error handling, rate limiting, security scan |
| 6 | Testing & Quality        | Vitest coverage ≥80%, load tests, accessibility audit   |
| 7 | Launch                   | ECS deploy, Prometheus/Grafana, runbook, go-live checklist |

## Commands

- **\`phase status\`** — Read \`playbook/current-phase.json\` and display the current phase number, name, objectives, and quality gates.
- **\`phase list\`** — Print all 7 phases with a ✅ or ⬜ completion indicator based on \`completedPhases\` in \`current-phase.json\`.
- **\`phase next\`** — Validate current phase quality gates are met, increment the phase, update \`current-phase.json\`, and display new phase objectives.
- **\`phase prev\`** — Decrement the phase by one (minimum phase 1), update \`current-phase.json\`.
- **\`phase set N\`** — Jump directly to phase N (1–7), update \`current-phase.json\`, and display that phase's objectives.

## State File

Location: \`playbook/current-phase.json\`

\`\`\`json
{
  "currentPhase": 3,
  "phaseName": "Foundation",
  "startedAt": "2024-11-15T10:00:00Z",
  "completedPhases": [1, 2],
  "project": "nextjs-enterprise"
}
\`\`\`

## Behavior Notes

- On **\`phase next\`**, append the current phase number to \`completedPhases\` before advancing.
- On **\`phase set N\`**, do NOT modify \`completedPhases\` — this is a manual override.
- Always load phase-specific overrides from \`playbook/phases/{N}-*.md\` when switching phases.
- After any phase change, remind the user to review \`.claude/playbook/claude-overrides.yml\` for phase-specific rules.`,

  'plugin/codeforge-nextjs-enterprise/skills/plan/SKILL.md': `---
name: plan
description: "Create a structured implementation plan with subtasks and dependencies for nextjs-enterprise"
version: "1.0.0"
metadata:
  category: planning
  tags: [planning, architecture, task-breakdown]
compatibility:
  claudeCode: ">=1.0.0"
---

# Plan Skill

Generate a structured, dependency-aware implementation plan for features in the **nextjs-enterprise** modular monolith.

## Instructions

1. **Analyze the feature request** — Parse the user's description and identify the core objective, acceptance criteria, and any constraints. Cross-reference with existing modules under \`src/modules/\` and the Prisma schema at \`prisma/schema.prisma\`.

2. **Identify affected modules, files, and APIs** — Map the feature to concrete touch-points:
   - Next.js App Router routes (\`src/app/\`)
   - API route handlers (\`src/app/api/\`)
   - Prisma models and migrations (\`prisma/\`)
   - Shared libraries (\`src/lib/\`) — e.g., Redis cache utilities, Socket.io event handlers
   - NextAuth.js configuration (\`src/lib/auth/\`)
   - Vitest test files (\`__tests__/\` or co-located \`.test.ts\`)

3. **Break down into subtasks with dependencies** — Each subtask must specify:
   - A short title and one-line description
   - Files to create or modify
   - Dependencies on other subtasks (by ID)
   - Whether it requires a Prisma migration, new API route, or WebSocket event

4. **Estimate complexity per subtask** — Use a three-tier scale:
   - **S** — isolated change, single file, no migration (~1 hr)
   - **M** — multi-file, may touch Prisma schema or Redis logic (~2-4 hrs)
   - **L** — cross-module, new API surface, requires integration tests (~4-8 hrs)

5. **Generate plan as a markdown checklist** — Output a numbered list with checkboxes, complexity badges, and dependency annotations ready for copy-paste into a GitHub issue.

## Example

**Invocation:**
> Plan the implementation of a "team invitations" feature where users can invite others by email, with an expiring token stored in Redis.

**Output:**

\`\`\`markdown
## Implementation Plan: Team Invitations

### Subtasks

- [ ] **1. Prisma model for Invitation** [M]
  - Add \`Invitation\` model to \`prisma/schema.prisma\` with fields: id, email, teamId, token, expiresAt
  - Run \`pnpm prisma migrate dev --name add-invitation-model\`
  - Dependencies: none

- [ ] **2. Invitation service module** [M]
  - Create \`src/modules/invitations/invitation.service.ts\`
  - Generate token, store in Redis with TTL via \`src/lib/redis.ts\`
  - Dependencies: #1

- [ ] **3. POST /api/invitations route** [M]
  - Create \`src/app/api/invitations/route.ts\`
  - Validate auth session via NextAuth.js, call invitation service
  - Dependencies: #2

- [ ] **4. Accept invitation page** [S]
  - Create \`src/app/invitations/[token]/page.tsx\`
  - Verify token against Redis, add user to team
  - Dependencies: #2

- [ ] **5. WebSocket notification** [S]
  - Emit \`invitation:received\` event via Socket.io to online invitees
  - Dependencies: #3

- [ ] **6. Vitest integration tests** [L]
  - Test token generation, expiry, acceptance flow, and WebSocket event
  - Dependencies: #3, #4, #5
\`\`\`

## Troubleshooting

| Problem | Resolution |
|---|---|
| Feature spans unknown modules | Run \`find src/modules -name '*.ts'\` to list existing modules before planning |
| Unclear dependency ordering | Ensure Prisma migrations always come first; API routes depend on services, not vice versa |
| Complexity estimate feels wrong | Re-check if the subtask touches Redis, WebSocket, or auth — each adds roughly one tier |
| Plan is too granular | Merge subtasks that modify the same file into a single item to keep plans actionable |`,

  'plugin/codeforge-nextjs-enterprise/skills/tdd/SKILL.md': `---
name: tdd
description: Test-Driven Development workflow using Vitest and Testing Library for the nextjs-enterprise project
version: 1.0.0
metadata:
  category: development
  tags:
    - testing
    - tdd
    - vitest
compatibility:
  frameworks:
    - next.js
  languages:
    - typescript
  tools:
    - vitest
    - testing-library
---

# TDD Skill — RED / GREEN / REFACTOR

Apply this 7-step workflow every time you write new functionality in \`nextjs-enterprise\`.

## Workflow

### 1. Understand the Requirement
Read the acceptance criteria. Identify inputs, outputs, edge cases, and error states before writing any code. Break complex features into the smallest testable unit.

### 2. Write a Failing Test
Create a test file colocated with the source: \`src/modules/<module>/__tests__/<name>.test.ts\` (or \`.test.tsx\` for components).

**Unit / service example:**
\`\`\`typescript
import { describe, it, expect } from 'vitest';
import { calculateDiscount } from '../services/pricing.service';

describe('calculateDiscount', () => {
  it('applies 10% discount for orders over $100', () => {
    expect(calculateDiscount(150)).toBe(135);
  });

  it('returns original price for orders under $100', () => {
    expect(calculateDiscount(50)).toBe(50);
  });
});
\`\`\`

**Component example:**
\`\`\`typescript
import { describe, it, expect } from 'vitest';
import { render, screen } from '@testing-library/react';
import { UserBadge } from '../components/UserBadge';

describe('UserBadge', () => {
  it('renders the user display name', () => {
    render(<UserBadge name="Alice" role="admin" />);
    expect(screen.getByText('Alice')).toBeInTheDocument();
  });
});
\`\`\`

### 3. Run Test — Confirm RED
\`\`\`bash
pnpm test -- --run src/modules/<module>/__tests__/<name>.test.ts
\`\`\`
The test **must fail**. If it passes, the test is not covering new behavior — revisit step 2.

### 4. Write Minimal Passing Code
Implement only enough production code in \`src/modules/<module>/\` to make the test pass. Do not add logic that is not demanded by a test.

### 5. Run Test — Confirm GREEN
\`\`\`bash
pnpm test -- --run src/modules/<module>/__tests__/<name>.test.ts
\`\`\`
All tests in the file must pass. If any fail, fix the implementation — not the test.

### 6. Refactor While Tests Stay Green
Improve naming, extract helpers, reduce duplication, apply project patterns from \`.claude/rules/\`. After each change run:
\`\`\`bash
pnpm test -- --run src/modules/<module>/__tests__/<name>.test.ts
\`\`\`
Never refactor on red.

### 7. Check Coverage
\`\`\`bash
pnpm test:coverage
\`\`\`
Ensure the module meets the project threshold (≥ 80% line coverage). Add edge-case tests if coverage gaps exist.

## Quick Reference

| Command | Purpose |
|---|---|
| \`pnpm test\` | Run full test suite in watch mode |
| \`pnpm test -- --run <path>\` | Single run for a specific file |
| \`pnpm test:coverage\` | Generate coverage report (v8) |
| \`pnpm test -- --reporter=verbose\` | Detailed per-test output |

## Rules
- **One assertion concept per test.** Multiple \`expect\` calls are fine if they verify the same behavior.
- **No mocking Prisma in integration tests.** Use the test database defined in \`.env.test\`.
- **Async tests** must use \`async/await\` — never leave dangling promises.
- **Name tests as behavior:** \`it('returns 401 when session is expired')\` not \`it('test auth')\`.`,

  'plugin/codeforge-nextjs-enterprise/skills/verify/SKILL.md': `---
name: verify
description: Run the full verification pipeline for nextjs-enterprise — build, typecheck, lint, test, and git status — stopping on first failure with a clear pass/fail summary.
version: 1.0.0
metadata:
  category: quality
  tags: [verify, pipeline, ci]
compatibility:
  claude-code: ">=1.0.0"
  project: nextjs-enterprise
---

# Verify Skill

Run the complete verification pipeline for the **nextjs-enterprise** project. This skill mirrors what CI (GitHub Actions) executes, so you can catch failures locally before pushing.

## Pipeline Steps

Execute each step **sequentially**. Stop immediately on the first failure.

### Step 1 — Build

\`\`\`bash
pnpm build
\`\`\`

Runs \`next build\` to compile the App Router application, generate Prisma client artifacts, and verify all server/client components resolve correctly.

### Step 2 — Type Check

\`\`\`bash
pnpm tsc --noEmit
\`\`\`

Runs the TypeScript compiler in strict mode across the entire codebase without emitting files. Catches type errors that \`next build\` may not surface.

### Step 3 — Lint

\`\`\`bash
pnpm lint
\`\`\`

Runs ESLint + Prettier checks against \`src/\`, \`prisma/\`, and config files using the project's \`.eslintrc.cjs\` and \`.prettierrc\` configuration.

### Step 4 — Test

\`\`\`bash
pnpm test --run
\`\`\`

Executes the full Vitest suite in single-run mode (no watch). Includes unit tests, integration tests, and Prisma-based repository tests.

### Step 5 — Git Status

\`\`\`bash
git status --short
\`\`\`

Check for uncommitted changes. If the build or codegen produced unexpected file changes (e.g., Prisma migrations, generated types), flag them. A clean working tree is expected.

## Reporting

After all steps complete (or one fails), produce a summary table:

| Step        | Status | Duration |
|-------------|--------|----------|
| Build       | ✅ / ❌ | Xs       |
| Type Check  | ✅ / ❌ | Xs       |
| Lint        | ✅ / ❌ | Xs       |
| Test        | ✅ / ❌ | Xs       |
| Git Status  | ✅ / ❌ | —        |

If a step fails, include the **step name**, **exit code**, and the **last 20 lines of output** to help diagnose the issue. Do not proceed to subsequent steps after a failure.`,

  'plugin/marketplace.json.example': `{
  "name": "nextjs-enterprise",
  "version": "1.0.0",
  "description": "Production-grade Next.js 14 enterprise starter with App Router, Prisma ORM, NextAuth.js, WebSocket support, Redis caching, and full CI/CD pipeline for AWS ECS deployment.",
  "author": {
    "name": "Your Organization",
    "email": "team@your-org.dev",
    "url": "https://github.com/your-org"
  },
  "license": "MIT",
  "repository": "https://github.com/your-org/nextjs-enterprise",
  "keywords": ["nextjs", "enterprise", "typescript", "claude-code", "codeforge"],
  "category": "project-setup",
  "compatibility": {
    "codeforge": ">=1.0.0",
    "claude-code": ">=1.0.0",
    "node": ">=20.0.0",
    "pnpm": ">=9.0.0"
  },
  "screenshots": [
    "https://raw.githubusercontent.com/your-org/nextjs-enterprise/main/.github/screenshots/architecture.png",
    "https://raw.githubusercontent.com/your-org/nextjs-enterprise/main/.github/screenshots/dashboard.png",
    "https://raw.githubusercontent.com/your-org/nextjs-enterprise/main/.github/screenshots/ci-pipeline.png"
  ],
  "pricing": "free",
  "downloads": 0,
  "rating": 0,
  "verified": false
}`,

};

// Demo templates per template ID
const demoTemplates: Record<string, { template: Template; fileTree: FileTreeNode[]; fileContents: Record<string, string> }> = {
  '1': {
    template: {
      id: '1',
      name: 'Next.js Enterprise',
      description: 'Production-ready Next.js 14 with TypeScript, Tailwind CSS, Prisma ORM, and comprehensive testing setup including unit, integration, and E2E tests. Includes CI/CD pipelines, code ownership, and AI-assisted development configurations.',
      category: 'Frontend',
      tags: ['next.js', 'typescript', 'prisma', 'tailwind', 'enterprise'],
      stars: 342,
      forks: 89,
      author: 'codeforge-team',
      createdAt: '2024-11-01T00:00:00Z',
      updatedAt: '2024-11-20T00:00:00Z',
      techStack: ['Next.js', 'TypeScript', 'Prisma', 'Tailwind CSS', 'PostgreSQL'],
      wizardData: {} as Template['wizardData'],
    },
    fileTree: demoFileTree,
    fileContents: demoFileContents,
  },
  '2': {
    template: {
      id: '2',
      name: 'Spring Boot Microservices',
      description: 'Enterprise microservice architecture with Spring Boot 3.2, Java 21, Kafka event streaming, and Kubernetes deployment. Includes hexagonal architecture, Testcontainers integration tests, and Claude Code plugin for Java development.',
      category: 'Backend',
      tags: ['java', 'spring-boot', 'kafka', 'kubernetes', 'microservices'],
      stars: 256,
      forks: 67,
      author: 'codeforge-team',
      createdAt: '2024-10-15T00:00:00Z',
      updatedAt: '2024-11-18T00:00:00Z',
      techStack: ['Java', 'Spring Boot', 'Kafka', 'Kubernetes', 'PostgreSQL'],
      wizardData: {} as Template['wizardData'],
    },
    fileTree: springBootDemoFileTree,
    fileContents: springBootDemoFileContents,
  },
  '3': {
    template: {
      id: '3',
      name: 'Python ML Pipeline',
      description: 'End-to-end machine learning pipeline with PyTorch, MLflow experiment tracking, DVC data versioning, and FastAPI model serving. Includes GPU training configs, reproducible experiments, and Claude Code plugin for ML development.',
      category: 'Data & ML',
      tags: ['python', 'pytorch', 'mlflow', 'dvc', 'fastapi'],
      stars: 289,
      forks: 73,
      author: 'codeforge-team',
      createdAt: '2024-10-20T00:00:00Z',
      updatedAt: '2024-11-19T00:00:00Z',
      techStack: ['Python', 'PyTorch', 'MLflow', 'DVC', 'FastAPI'],
      wizardData: {} as Template['wizardData'],
    },
    fileTree: pythonMlDemoFileTree,
    fileContents: pythonMlDemoFileContents,
  },
  '4': {
    template: {
      id: '4',
      name: 'Django SaaS Platform',
      description: 'Multi-tenant SaaS platform with Django 5.0, Celery task processing, Stripe billing integration, and REST/GraphQL API. Includes tenant isolation, role-based access control, and Claude Code plugin for Django development.',
      category: 'Full Stack',
      tags: ['python', 'django', 'celery', 'stripe', 'saas'],
      stars: 187,
      forks: 52,
      author: 'saas-builders',
      createdAt: '2024-10-01T00:00:00Z',
      updatedAt: '2024-11-12T00:00:00Z',
      techStack: ['Python', 'Django', 'Celery', 'Redis', 'PostgreSQL'],
      wizardData: {} as Template['wizardData'],
    },
    fileTree: djangoSaasDemoFileTree,
    fileContents: djangoSaasDemoFileContents,
  },
  '5': {
    template: {
      id: '5',
      name: 'Go API Gateway',
      description: 'High-performance API gateway with Go 1.22 and Chi router, featuring JWT auth, rate limiting, circuit breakers, and OpenTelemetry observability. Includes Claude Code plugin for Go development.',
      category: 'API',
      tags: ['go', 'chi', 'jwt', 'api-gateway', 'observability'],
      stars: 145,
      forks: 38,
      author: 'go-enthusiasts',
      createdAt: '2024-10-20T00:00:00Z',
      updatedAt: '2024-11-19T00:00:00Z',
      techStack: ['Go', 'Chi', 'Docker', 'Prometheus', 'Redis'],
      wizardData: {} as Template['wizardData'],
    },
    fileTree: goApiDemoFileTree,
    fileContents: goApiDemoFileContents,
  },
  '6': {
    template: {
      id: '6',
      name: 'React Native Mobile App',
      description: 'Cross-platform mobile app with React Native, Expo SDK 51, Firebase backend, and offline-first architecture. Includes Detox E2E testing, EAS Build CI/CD, and Claude Code plugin for mobile development.',
      category: 'Mobile',
      tags: ['react-native', 'expo', 'typescript', 'firebase', 'mobile'],
      stars: 134,
      forks: 41,
      author: 'mobile-devs',
      createdAt: '2024-09-15T00:00:00Z',
      updatedAt: '2024-11-10T00:00:00Z',
      techStack: ['React Native', 'Expo', 'TypeScript', 'Firebase'],
      wizardData: {} as Template['wizardData'],
    },
    fileTree: reactNativeDemoFileTree,
    fileContents: reactNativeDemoFileContents,
  },
  '7': {
    template: {
      id: '7',
      name: 'Terraform AWS Platform',
      description: 'Complete AWS infrastructure as code with Terraform modules for VPC, ECS, RDS, and monitoring. Includes CI/CD, security scanning, compliance automation, and Claude Code plugin for infrastructure development.',
      category: 'DevOps',
      tags: ['terraform', 'aws', 'ecs', 'infrastructure', 'devops'],
      stars: 167,
      forks: 54,
      author: 'platform-team',
      createdAt: '2024-10-05T00:00:00Z',
      updatedAt: '2024-11-17T00:00:00Z',
      techStack: ['Terraform', 'Go', 'AWS', 'Docker', 'CloudWatch'],
      wizardData: {} as Template['wizardData'],
    },
    fileTree: terraformDemoFileTree,
    fileContents: terraformDemoFileContents,
  },
  '8': {
    template: {
      id: '8',
      name: 'Rust Actix Web API',
      description: 'High-performance REST API with Actix Web 4, SQLx compile-time checked queries, structured error handling, and OpenTelemetry observability. Includes Claude Code plugin for Rust development.',
      category: 'Backend',
      tags: ['rust', 'actix-web', 'sqlx', 'api', 'performance'],
      stars: 112,
      forks: 28,
      author: 'rust-community',
      createdAt: '2024-11-05T00:00:00Z',
      updatedAt: '2024-11-19T00:00:00Z',
      techStack: ['Rust', 'Actix Web', 'SQLx', 'PostgreSQL', 'Docker'],
      wizardData: {} as Template['wizardData'],
    },
    fileTree: rustActixDemoFileTree,
    fileContents: rustActixDemoFileContents,
  },
};

const defaultDemo = demoTemplates['1'];

interface VersionItem {
  id: string;
  version: string;
  changelog: string;
  createdAt: string;
  createdBy: string;
}

export default function TemplateDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const demo = (id && demoTemplates[id]) || defaultDemo;
  const [template, setTemplate] = useState<Template>(demo.template);
  const [activeTab, setActiveTab] = useState<(typeof tabs)[number]>('Overview');
  const [selectedFile, setSelectedFile] = useState<string>('CLAUDE.md');
  const [loading, setLoading] = useState(false);
  const [versions, setVersions] = useState<VersionItem[]>([]);
  const [versionsLoading, setVersionsLoading] = useState(false);
  const [starred, setStarred] = useState(false);
  const [forking, setForking] = useState(false);
  const [fileTree, setFileTree] = useState<FileTreeNode[]>(demo.fileTree);
  const [fileContents, setFileContents] = useState<Record<string, string>>(demo.fileContents);

  useEffect(() => {
    const loadTemplate = async () => {
      if (!id) return;
      try {
        setLoading(true);
        const data = await fetchTemplate(id);
        setTemplate(data);
        // If template has real file data from latest version, use it
        if (data.latestVersion?.files && typeof data.latestVersion.files === 'object') {
          const files = data.latestVersion.files as Record<string, string>;
          setFileContents(files);
          setFileTree(buildFileTree(files));
          const firstFile = Object.keys(files)[0];
          if (firstFile) setSelectedFile(firstFile);
        }
      } catch {
        // Use demo data
      } finally {
        setLoading(false);
      }
    };
    loadTemplate();
  }, [id]);

  // Load versions when tab is selected
  useEffect(() => {
    if (activeTab !== 'Versions' || !id) return;
    const loadVersions = async () => {
      try {
        setVersionsLoading(true);
        const data = await fetchVersions(id);
        if (data && data.length > 0) {
          setVersions(data);
        } else {
          // Fallback demo versions
          setVersions([
            { id: '1', version: '1.2.0', changelog: 'Added AI code review workflow', createdAt: '2024-11-20T00:00:00Z', createdBy: 'system' },
            { id: '2', version: '1.1.0', changelog: 'Updated ESLint to strict mode', createdAt: '2024-11-10T00:00:00Z', createdBy: 'system' },
            { id: '3', version: '1.0.0', changelog: 'Initial release', createdAt: '2024-11-01T00:00:00Z', createdBy: 'system' },
          ]);
        }
      } catch {
        setVersions([
          { id: '1', version: '1.2.0', changelog: 'Added AI code review workflow', createdAt: '2024-11-20T00:00:00Z', createdBy: 'system' },
          { id: '2', version: '1.1.0', changelog: 'Updated ESLint to strict mode', createdAt: '2024-11-10T00:00:00Z', createdBy: 'system' },
          { id: '3', version: '1.0.0', changelog: 'Initial release', createdAt: '2024-11-01T00:00:00Z', createdBy: 'system' },
        ]);
      } finally {
        setVersionsLoading(false);
      }
    };
    loadVersions();
  }, [activeTab, id]);

  const handleFork = async () => {
    if (!id || forking) return;
    try {
      setForking(true);
      const forked = await forkTemplate(id);
      navigate(`/templates/${forked.id}`);
    } catch {
      // Fork failed — stay on page
    } finally {
      setForking(false);
    }
  };

  const handleStar = async () => {
    if (!id) return;
    try {
      const action = starred ? 'unstar' : 'star';
      const result = await starTemplate(id, action);
      setTemplate((prev) => ({ ...prev, stars: result.stars }));
      setStarred(!starred);
    } catch {
      // Star failed
    }
  };

  const fileContent = fileContents[selectedFile] || '// File content not available in preview';

  if (loading) {
    return (
      <div className="min-h-screen pt-28 pb-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="animate-pulse space-y-6">
            <div className="h-8 bg-surface-200 rounded w-1/3" />
            <div className="h-4 bg-surface-200 rounded w-2/3" />
            <div className="h-96 bg-surface-200 rounded" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-28 pb-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Back */}
        <Link
          to="/templates"
          className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-300 mb-6 transition-colors"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Templates
        </Link>

        {/* Header */}
        <div className="card mb-8">
          <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-6">
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-3">
                <h1 className="text-2xl font-bold text-white">
                  {template.name}
                </h1>
                <span className="badge border bg-blue-500/10 text-blue-400 border-blue-500/20">
                  {template.category}
                </span>
              </div>
              <p className="text-gray-400 mb-4 max-w-2xl">
                {template.description}
              </p>
              <div className="flex flex-wrap items-center gap-4 text-sm text-gray-500">
                <span className="flex items-center gap-1.5">
                  <User className="h-3.5 w-3.5" />
                  {template.author}
                </span>
                <button
                  onClick={handleStar}
                  className={clsx(
                    'flex items-center gap-1.5 transition-colors',
                    starred ? 'text-amber-400 hover:text-amber-300' : 'hover:text-amber-400'
                  )}
                >
                  <Star className={clsx('h-3.5 w-3.5', starred && 'fill-current')} />
                  {template.stars} stars
                </button>
                <span className="flex items-center gap-1.5">
                  <GitFork className="h-3.5 w-3.5" />
                  {template.forks} forks
                </span>
                <span className="flex items-center gap-1.5">
                  <Calendar className="h-3.5 w-3.5" />
                  Updated{' '}
                  {new Date(template.updatedAt).toLocaleDateString()}
                </span>
              </div>
              <div className="flex flex-wrap gap-1.5 mt-4">
                {template.tags.map((tag) => (
                  <span
                    key={tag}
                    className="px-2.5 py-1 text-xs rounded-md bg-surface-100 text-gray-400 border border-dark-border flex items-center gap-1"
                  >
                    <Tag className="h-3 w-3" />
                    {tag}
                  </span>
                ))}
              </div>
              {template.techStack.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mt-3">
                  {template.techStack.map((tech) => (
                    <span
                      key={tech}
                      className="px-2.5 py-1 text-xs rounded-md bg-accent/10 text-accent border border-accent/20"
                    >
                      {tech}
                    </span>
                  ))}
                </div>
              )}
            </div>
            <div className="flex gap-3 shrink-0">
              <Link
                to={`/wizard?template=${template.id}`}
                className="btn-primary"
              >
                Use Template
              </Link>
              <button
                onClick={handleFork}
                disabled={forking}
                className={clsx('btn-ghost', forking && 'opacity-50 cursor-wait')}
              >
                <GitFork className="h-4 w-4" />
                {forking ? 'Forking...' : 'Fork'}
              </button>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex items-center gap-1 border-b border-dark-border mb-6">
          {tabs.map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={clsx(
                'px-4 py-3 text-sm font-medium border-b-2 transition-colors -mb-px',
                activeTab === tab
                  ? 'text-white border-accent'
                  : 'text-gray-500 border-transparent hover:text-gray-300'
              )}
            >
              {tab}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        {activeTab === 'Overview' && (
          <div className="prose prose-invert max-w-none">
            <div className="card">
              <h2 className="text-lg font-semibold text-white mb-4">
                About this template
              </h2>
              <p className="text-gray-400 mb-4">{template.description}</p>
              <h3 className="text-base font-semibold text-white mb-2 mt-6">
                What&apos;s included
              </h3>
              <ul className="space-y-2 text-sm text-gray-400">
                <li className="flex items-start gap-2">
                  <span className="text-accent mt-1">--</span>
                  Project-aware CLAUDE.md with coding standards and guardrails
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-accent mt-1">--</span>
                  MCP server configuration for your tools
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-accent mt-1">--</span>
                  CI/CD pipeline with lint, test, and build stages
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-accent mt-1">--</span>
                  CODEOWNERS file with team-based ownership rules
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-accent mt-1">--</span>
                  Pre-commit hooks for linting and formatting
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-accent mt-1">--</span>
                  Developer profiles (junior/mid/senior guardrails)
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-accent mt-1">--</span>
                  Architecture docs, onboarding guide, and AI usage policy
                </li>
              </ul>
            </div>
          </div>
        )}

        {activeTab === 'Files' && (
          <div className="grid grid-cols-[280px_1fr] gap-4 h-[600px]">
            <div className="card overflow-auto p-3">
              <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider px-2 mb-2">
                Files
              </div>
              <FileTree
                nodes={fileTree}
                selectedPath={selectedFile}
                onSelect={(path) => setSelectedFile(path)}
              />
            </div>
            <FilePreview path={selectedFile} content={fileContent} />
          </div>
        )}

        {activeTab === 'Versions' && (
          <div className="card">
            <h2 className="text-lg font-semibold text-white mb-4">
              Version History
            </h2>
            {versionsLoading ? (
              <div className="space-y-4">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="animate-pulse flex justify-between py-3">
                    <div>
                      <div className="h-4 bg-surface-200 rounded w-20 mb-2" />
                      <div className="h-3 bg-surface-200 rounded w-48" />
                    </div>
                    <div className="h-3 bg-surface-200 rounded w-24" />
                  </div>
                ))}
              </div>
            ) : (
              <div className="space-y-4">
                {versions.map((v) => (
                  <div
                    key={v.id}
                    className="flex items-center justify-between py-3 border-b border-dark-border last:border-0"
                  >
                    <div>
                      <span className="text-white font-mono text-sm font-medium">
                        v{v.version}
                      </span>
                      <p className="text-sm text-gray-500 mt-0.5">{v.changelog}</p>
                    </div>
                    <span className="text-xs text-gray-600">
                      {new Date(v.createdAt).toLocaleDateString()}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// Build a FileTreeNode[] from a flat Record<string, string> of file paths
function buildFileTree(files: Record<string, string>): FileTreeNode[] {
  const root: FileTreeNode[] = [];

  for (const filePath of Object.keys(files).sort()) {
    const parts = filePath.split('/');
    let current = root;

    for (let i = 0; i < parts.length; i++) {
      const name = parts[i];
      const path = parts.slice(0, i + 1).join('/');
      const isFile = i === parts.length - 1;

      let existing = current.find((n) => n.name === name);
      if (!existing) {
        existing = {
          name,
          path,
          type: isFile ? 'file' : 'directory',
          children: isFile ? undefined : [],
        };
        current.push(existing);
      }
      if (!isFile && existing.children) {
        current = existing.children;
      }
    }
  }

  return root;
}
